import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./App.css";
import {
  ProductPreviewModal,
  PersonnelScreen,
  AvailabilityScreen,
  DayReservationScreen,
  AppointmentDurationBanner,
} from "./ReservationFlow";
import { formatDurationMinutes, formatDurationValue, getUiLanguageMeta, getUiText } from "./kioskI18n";

const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_KIOSK_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/kiosk"
).replace(/\/+$/, "");

const CASH_IMG =
  "https://images.kiosk-merchant.net/asdasd/Ekran%20g%C3%B6r%C3%BCnt%C3%BCs%C3%BC%202026-02-28%20060920.png";
const CARD_IMG =
  "https://images.kiosk-merchant.net/asdasd/kredikart%C4%B1.png";

const MULTINET_IMG =
  "https://images.kiosk-merchant.net/asdasd/multinet.png";
const SODEXO_IMG =
  "https://images.kiosk-merchant.net/asdasd/sodexo.jpg";
const SETCARD_IMG =
  "https://images.kiosk-merchant.net/asdasd/setcard.jpg";
const METROPOL_IMG =
  "https://images.kiosk-merchant.net/asdasd/metropolcard.jpg";
const EDENRED_IMG =
  "https://images.kiosk-merchant.net/asdasd/edenred.jpg";

const DEFAULT_DESIGN_DATA = {
  applogo: "",
  appvideo: "",
  appyazi: "",
  appaltyazi: "",
  drinkin: "",
  drinkinwrite: "İçeride",
  takeaway: "",
  takeawaywrite: "Paket",
  butonrenk: "",
  menupagephotorenk: "",
  menukategorirenk: "",
  menupageyazi: "",
  openhour: "08:00 - 22:00",
  addinfo: "1995'ten beri",
  firstemo: "coffee",
  firstemowrite: "Kahve",
  secondemo: "soda",
  secondemowrite: "İçecekler",
  thirdemo: "cake",
  thirdemowrite: "Tatlılar",
};

const DEFAULT_BOOTSTRAP = {
  designData: DEFAULT_DESIGN_DATA,
  homepageData: {
    categories: [],
    products: [],
    reklamphoto: "",
    baslikyazisi: "ÖNE ÇIKAN HİZMETLER",
  },
  menuItems: [],
  categoryContent: {},
  productsByUniqcode: {},
  branches: [],
  selectedBranch: null,
  subeId: "",
  personnel: [],
  languages: ["tr", "en", "ru", "de", "ar"],
  timezone: "Europe/Istanbul",
  preferance: {
    preapprove: false,
    // Eski preferans kayıtlarında cash alanı yoksa Nakit açık kabul edilir.
    cash: true,
    yemekkart: false,
    multinet: false,
    sodexo: false,
    setcard: false,
    metropol: false,
    edenred: false,
    recommendedHeadingLocalizedData: [
      { languageCode: "tr", language: "Türkçe", text: "Bu hizmeti alanlar bunları da ekledi" },
    ],
    recommendedProducts: [],
    reservationCompleteExtraHeadingLocalizedData: [
      { languageCode: "tr", language: "Türkçe", text: "Rezervasyonunuza bunları da eklemek ister misiniz?" },
    ],
    reservationCompleteExtraProducts: [],
  },
};

const tryFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
});

const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");
const getAuthKiosk = () => localStorage.getItem("kiosk");
const getAuthSubeId = () => localStorage.getItem("subeId") || "";
const getAuthBranchName = () => localStorage.getItem("branchName") || "";

const ACTIVE_ORDER_MODIFY_KEY = "activeOrderModify";
const ACTIVE_ORDER_MODIFY_MODE_KEY = "activeOrderModifyMode";
const ORDER_SESSION_ID_KEY = "kioskOrderSessionId";
const CUSTOMER_NAME_STORAGE_KEY = "reservationCustomerName";
const PAYMENT_IN_FLIGHT_MAX_AGE_MS = 90 * 1000;

// V44_FRONTEND_PAYMENT_DEBUG
const FRONTEND_PAYMENT_DEBUG_VERSION = "V47_PAYMENT_RETRY_FIX";
const getPerfMs = () => {
  try {
    return typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  } catch {
    return Date.now();
  }
};
const paymentDebugNow = () => new Date().toISOString();
const makePaymentDebugId = (prefix = "pay") => {
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return `${prefix}_${window.crypto.randomUUID()}`;
    }
  } catch {}
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};
const safeParseDebugJSON = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};
const readPaymentLocalStorageDebug = () => {
  try {
    return {
      paymentAttemptId: localStorage.getItem("paymentAttemptId"),
      paymentOrderNumber: localStorage.getItem("paymentOrderNumber"),
      paymentAttemptInFlight: localStorage.getItem("paymentAttemptInFlight"),
      paymentAttemptInFlightAt: localStorage.getItem("paymentAttemptInFlightAt"),
      activeOrderModify: (() => {
        try {
          const raw = localStorage.getItem(ACTIVE_ORDER_MODIFY_KEY);
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })(),
      activeOrderModifyMode: localStorage.getItem(ACTIVE_ORDER_MODIFY_MODE_KEY),
      orderSessionId: localStorage.getItem(ORDER_SESSION_ID_KEY),
      selectedOrderType: localStorage.getItem("selectedOrderType"),
    };
  } catch (error) {
    return { readError: error?.message || String(error) };
  }
};
const paymentDebug = (label, data = {}) => {
  try {
    console.log(
      `%c[KIOSK-PAY][${FRONTEND_PAYMENT_DEBUG_VERSION}] ${label}`,
      "color:#0f766e;font-weight:bold",
      { at: paymentDebugNow(), ...data }
    );
  } catch {}
};
const paymentWarn = (label, data = {}) => {
  try {
    console.warn(
      `%c[KIOSK-PAY][${FRONTEND_PAYMENT_DEBUG_VERSION}] ${label}`,
      "color:#b45309;font-weight:bold",
      { at: paymentDebugNow(), ...data }
    );
  } catch {}
};
const paymentErrorLog = (label, data = {}) => {
  try {
    console.error(
      `%c[KIOSK-PAY][${FRONTEND_PAYMENT_DEBUG_VERSION}] ${label}`,
      "color:#dc2626;font-weight:bold",
      { at: paymentDebugNow(), ...data }
    );
  } catch {}
};


const safeParseJSON = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const setStorageJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`localStorage write skipped for ${key}:`, error?.message || error);
  }
};

const getStorageJSON = (key, fallback = null) => {
  return safeParseJSON(localStorage.getItem(key), fallback);
};

const makeClientOrderSessionId = () => {
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch {}

  return `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random()
    .toString(36)
    .slice(2)}`;
};

const ensureOrderSessionIdStorage = () => {
  let sessionId = String(localStorage.getItem(ORDER_SESSION_ID_KEY) || "").trim();

  if (!sessionId) {
    sessionId = makeClientOrderSessionId();
    localStorage.setItem(ORDER_SESSION_ID_KEY, sessionId);
  }

  return sessionId;
};


const clearPaymentInFlightState = () => {
  localStorage.removeItem("paymentAttemptInFlight");
  localStorage.removeItem("paymentAttemptInFlightAt");
};

const clearOrderAttemptState = () => {
  localStorage.removeItem("paymentAttemptId");
  localStorage.removeItem("paymentOrderNumber");
  localStorage.removeItem("paymentAttemptCompleted");
  localStorage.removeItem(ORDER_SESSION_ID_KEY);
  clearPaymentInFlightState();
  localStorage.removeItem(ACTIVE_ORDER_MODIFY_KEY);
  localStorage.removeItem(ACTIVE_ORDER_MODIFY_MODE_KEY);
};

const isPaymentInFlightStillFresh = () => {
  const inFlight = localStorage.getItem("paymentAttemptInFlight") === "1";
  if (!inFlight) return false;

  const rawAt = localStorage.getItem("paymentAttemptInFlightAt");
  const startedAt = rawAt ? Date.parse(rawAt) : NaN;

  // Eski submit lock localStorage'da kalmışsa ödeme butonunu sonsuza kadar kilitlemesin.
  if (!Number.isFinite(startedAt) || Date.now() - startedAt > PAYMENT_IN_FLIGHT_MAX_AGE_MS) {
    console.warn("🧹 Eski paymentAttemptInFlight temizlendi", { rawAt });
    clearPaymentInFlightState();
    return false;
  }

  return true;
};

const clearAllExceptAuth = () => {
  const kept = {};
  [
    "userToken",
    "card",
    "kiosk",
    "subeId",
    "branchName",
    "selectedLanguageCode",
  ].forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) kept[key] = value;
  });

  localStorage.clear();
  Object.entries(kept).forEach(([key, value]) => localStorage.setItem(key, value));
};

const redirectToLogin = () => {
  clearAllExceptAuth();
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  localStorage.removeItem("kiosk");
  localStorage.removeItem("subeId");
  localStorage.removeItem("branchName");
  window.location.href = "/login";
};

const isUsableAuthValue = (value) => {
  const normalized = String(value ?? "").trim();
  return Boolean(normalized) && normalized !== "undefined" && normalized !== "null";
};

const decodeJwtPayloadSafely = (token) => {
  try {
    const tokenParts = String(token || "").split(".");
    if (tokenParts.length !== 3) return null;

    // JWT segmentleri normal Base64 değil Base64URL'dir.
    // '-' / '_' karakterleri ve eksik padding doğrudan atob() ile hata verebilir.
    let base64 = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) base64 += "=";

    return JSON.parse(atob(base64));
  } catch (error) {
    console.warn("⚠️ JWT payload client tarafında çözülemedi; doğrulamayı sunucuya bırakıyoruz.", {
      message: error?.message || String(error),
    });
    return null;
  }
};

const checkAuthentication = () => {
  const token = getAuthToken();
  const card = getAuthCard();
  const kiosk = getAuthKiosk();

  // Frontend yalnızca oturum bilgilerinin mevcut olduğunu kontrol eder.
  // Tokenın gerçek imza/yetki doğrulaması API sunucusunun görevidir.
  if (!isUsableAuthValue(token) || !isUsableAuthValue(card) || !isUsableAuthValue(kiosk)) {
    return false;
  }

  const payload = decodeJwtPayloadSafely(token);
  if (!payload) return true;

  const now = Date.now() / 1000;
  const CLOCK_SKEW_SECONDS = 90;

  // Gerçekten süresi geçmiş tokenı erkenden reddet; küçük cihaz/sunucu saat
  // farkları yüzünden yeni login olmuş kullanıcıyı tekrar login ekranına atma.
  if (payload.exp && Number(payload.exp) < now - CLOCK_SKEW_SECONDS) return false;

  // iat gelecekte görünüyor diye oturumu bozma. İmza ve claim doğrulamasını server yapar.
  return true;
};

const makeApiRequest = async (url, options = {}) => {
  const __apiDebugId = makePaymentDebugId("api");
  const __apiStartedAt = getPerfMs();
  const __apiTimeoutMs = Number(options?.timeoutMs || 0);
  paymentDebug("API ENTER", {
    requestId: __apiDebugId,
    url,
    method: options?.method || "GET",
    timeoutMs: __apiTimeoutMs || null,
    storage: readPaymentLocalStorageDebug(),
  });
  if (!checkAuthentication()) {
    console.error("❌ AUTH FAILED BEFORE REQUEST", {
      url,
      token: getAuthToken(),
      card: getAuthCard(),
      kiosk: getAuthKiosk(),
    });

    redirectToLogin();
    throw new Error("Kimlik doğrulama başarısız");
  }

  const token = getAuthToken();
  const card = getAuthCard();
  const kiosk = getAuthKiosk();

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (token) headers.Authorization = `Bearer ${token}`;

  let body = options.body;
  let loggedBody = null;

  if (body) {
    try {
      const parsed = JSON.parse(body);
      if (!parsed.card) parsed.card = card;
      if (!parsed.kiosk) parsed.kiosk = kiosk;
      loggedBody = parsed;
      body = JSON.stringify(parsed);
    } catch (err) {
      console.warn("⚠️ Request body JSON parse edilemedi:", {
        url,
        body,
        error: err?.message || err,
      });
    }
  }

  console.log("📤 API REQUEST START", {
    url,
    method: options.method || "GET",
    headers: {
      ...headers,
      Authorization: headers.Authorization ? "Bearer ***TOKEN***" : "",
    },
    body: loggedBody,
    localStorage: {
      card,
      kiosk,
      hasToken: !!token,
    },
  });

  /** @type {any} */
  const __fetchOptions = { ...options, headers, body };
  delete __fetchOptions.timeoutMs;

  let __apiAbortController = null;
  let __apiAbortTimer = null;
  if (__apiTimeoutMs > 0 && typeof AbortController !== "undefined") {
    __apiAbortController = new AbortController();
    __fetchOptions.signal = __apiAbortController.signal;
    __apiAbortTimer = window.setTimeout(() => {
      paymentWarn("API TIMEOUT ABORT", {
        requestId: __apiDebugId,
        url,
        method: options?.method || "GET",
        timeoutMs: __apiTimeoutMs,
        elapsedMs: Math.round(getPerfMs() - __apiStartedAt),
      });
      try { __apiAbortController.abort(); } catch {}
    }, __apiTimeoutMs);
  }

  let response;
  try {
    paymentDebug("API FETCH START", {
      requestId: __apiDebugId,
      url,
      method: options?.method || "GET",
      body: loggedBody,
    });
    response = await fetch(url, __fetchOptions);
  } catch (fetchError) {
    paymentErrorLog("API FETCH THROW", {
      requestId: __apiDebugId,
      url,
      method: options?.method || "GET",
      elapsedMs: Math.round(getPerfMs() - __apiStartedAt),
      name: fetchError?.name,
      message: fetchError?.message || String(fetchError),
      storage: readPaymentLocalStorageDebug(),
    });
    throw fetchError;
  } finally {
    if (__apiAbortTimer) window.clearTimeout(__apiAbortTimer);
  }

  const rawText = await response.text();

  let json = null;
  try {
    json = rawText ? JSON.parse(rawText) : null;
  } catch (err) {
    console.error("❌ API RESPONSE JSON PARSE FAILED", {
      url,
      status: response.status,
      rawText,
      error: err?.message || err,
    });
  }

  console.log("📥 API RESPONSE", {
    url,
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    rawText,
    json,
  });

  paymentDebug(response.ok ? "API RESPONSE OK" : "API RESPONSE NOT_OK", {
    requestId: __apiDebugId,
    url,
    status: response.status,
    statusText: response.statusText,
    elapsedMs: Math.round(getPerfMs() - __apiStartedAt),
    json,
    rawTextLength: rawText?.length || 0,
  });

  if (!response.ok) {
    if (json?.redirectToLogin || response.status === 401) {
      console.error("🔐 API AUTH ERROR - REDIRECT LOGIN", {
        url,
        status: response.status,
        json,
      });
      redirectToLogin();
    }

    const error = Object.assign(
      new Error(json?.message || json?.error || `HTTP ${response.status}`),
      { status: response.status, payload: json, rawText }
    );
    throw error;
  }

  paymentDebug("API RETURN SUCCESS", {
    requestId: __apiDebugId,
    url,
    elapsedMs: Math.round(getPerfMs() - __apiStartedAt),
    json,
  });

  // Bootstrap dışındaki daha sonraki personel/ürün GET cevaplarında yeni
  // görsel veya video gelirse onları da arka planda hazırla. API cevabını bekletmez.
  if (!String(url || "").includes("/kioskbootstrap")) {
    Promise.resolve()
      .then(() => Promise.all([
        scheduleImagePreload(json),
        scheduleElectronVideoCache(json),
      ]))
      .catch(() => {});
  }

  return json;
};

const parseMoneyToNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^\d.,-]/g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatTRY = (value) => {
  const n = Number(value || 0);
  return tryFormatter.format(Number.isFinite(n) ? n : 0);
};
const getOrderDisplayNumberFromResponse = (res, fallback = "") => {
  const candidates = [
    res?.orderDisplayNumber,
    res?.orderSayi,
    res?.ordersayi,
    res?.data?.orderDisplayNumber,
    res?.data?.orderSayi,
    res?.data?.ordersayi,
    fallback,
  ];

  for (const value of candidates) {
    const clean = String(value ?? "").trim();
    if (clean && clean !== "null" && clean !== "undefined") return clean;
  }

  return "";
};


const normalizeDepartmentNoForPayment = (value) => {
  const clean = String(value ?? "")
    .trim()
    .replace(/,/g, "")
    .replace(/\s+/g, "");
  return clean || "1";
};


const generateOrderNumber30 = () => {
  try {
    if (window.crypto?.getRandomValues) {
      const bytes = new Uint8Array(30);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => String(b % 10)).join("");
    }
  } catch {}
  const base = `${Date.now()}${Math.floor(Math.random() * 1e12)}`.replace(/\D/g, "");
  return (base + "0".repeat(30)).slice(0, 30);
};

const createOrderKey = (uniqcode = "item") => {
  const rand = Math.random().toString(36).slice(2, 8);
  return `${uniqcode}_${Date.now()}_${rand}`;
};

const getNextCountingNumber = (cart) => {
  const nums = Object.values(cart || {})
    .map((item) => Number(item?.countingnumber || 0))
    .filter((n) => n > 0);

  return nums.length ? Math.max(...nums) + 1 : 1;
};

const sortCartEntries = (cart) =>
  Object.entries(cart || {}).sort(([, a], [, b]) => {
    return Number(a?.countingnumber || 0) - Number(b?.countingnumber || 0);
  });

const IMAGE_PRELOAD_TIMEOUT_MS = 18000;
const IMAGE_PRELOAD_HIGH_PRIORITY_COUNT = 1200;

// Browser bazen görünmeyen/visibility:hidden img elementlerini düşük önceliğe atıyor.
// Bu yüzden ayrıca DOM içinde gerçek <img> preload layer kullanıyoruz.
const DOM_IMAGE_PRELOAD_TIMEOUT_MS = 32000;
const DOM_IMAGE_PRELOAD_LIMIT = 2000;

const imagePreloadCache = new Map();
const imagePreloadLinkSet = new Set();

const isPreloadableImageUrl = (src) => {
  if (typeof src !== "string") return false;
  const value = src.trim();
  if (!value) return false;
  return (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/") ||
    value.startsWith("data:image/")
  );
};

const addImagePreloadLink = (src, priority = "high") => {
  try {
    if (typeof document === "undefined") return;
    if (!isPreloadableImageUrl(src)) return;
    if (imagePreloadLinkSet.has(src)) return;

    imagePreloadLinkSet.add(src);

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    link.fetchPriority = /** @type {"high"|"low"|"auto"} */ (priority === "low" ? "low" : "high");
    document.head.appendChild(link);
  } catch {}
};

const preloadOneImage = (src, options = {}) => {
  const cleanSrc = typeof src === "string" ? src.trim() : "";
  const timeoutMs = Number(options.timeoutMs || IMAGE_PRELOAD_TIMEOUT_MS);
  const priority = options.priority || "high";

  if (!isPreloadableImageUrl(cleanSrc)) {
    return Promise.resolve({ src: cleanSrc, ok: false, status: "skipped" });
  }

  if (imagePreloadCache.has(cleanSrc)) {
    return imagePreloadCache.get(cleanSrc);
  }

  addImagePreloadLink(cleanSrc, priority);

  const promise = new Promise((resolve) => {
    if (typeof Image === "undefined") {
      resolve({ src: cleanSrc, ok: false, status: "no-image-api" });
      return;
    }

    let settled = false;
    let timer = null;

    const finish = (ok, status) => {
      if (settled) return;
      settled = true;
      if (timer) window.clearTimeout(timer);
      resolve({ src: cleanSrc, ok, status });
    };

    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.fetchPriority = /** @type {"high"|"low"|"auto"} */ (priority === "low" ? "low" : "high");

    img.onload = async () => {
      try {
        if (typeof img.decode === "function") {
          await img.decode();
        }
      } catch {}
      finish(true, "loaded");
    };

    img.onerror = () => finish(false, "error");

    timer = window.setTimeout(() => {
      // Tek bir yavaş/bozuk görsel kiosk akışını sonsuza kadar kilitlemesin.
      finish(false, "timeout");
    }, timeoutMs);

    img.src = cleanSrc;

    if (img.complete && img.naturalWidth > 0) {
      Promise.resolve()
        .then(async () => {
          try {
            if (typeof img.decode === "function") await img.decode();
          } catch {}
          finish(true, "cached");
        })
        .catch(() => finish(true, "cached"));
    }
  });

  imagePreloadCache.set(cleanSrc, promise);
  return promise;
};

const preloadImages = async (urls, options = {}) => {
  const list = Array.from(new Set(urls || []))
    .map((src) => (typeof src === "string" ? src.trim() : ""))
    .filter(isPreloadableImageUrl);

  const priorityCount = Number(
    options.priorityCount ?? Math.min(IMAGE_PRELOAD_HIGH_PRIORITY_COUNT, list.length)
  );
  const timeoutMs = Number(options.timeoutMs || IMAGE_PRELOAD_TIMEOUT_MS);
  const onProgress = typeof options.onProgress === "function" ? options.onProgress : null;

  if (!list.length) {
    const empty = { total: 0, completed: 0, loaded: 0, failed: 0 };
    onProgress?.(empty);
    return empty;
  }

  list.slice(0, priorityCount).forEach((src) => addImagePreloadLink(src, "high"));

  let completed = 0;
  let loaded = 0;
  let failed = 0;

  const results = await Promise.all(
    list.map((src, index) =>
      preloadOneImage(src, {
        timeoutMs,
        priority: index < priorityCount ? "high" : "low",
      }).then((result) => {
        completed += 1;
        if (result.ok) loaded += 1;
        else failed += 1;
        onProgress?.({ total: list.length, completed, loaded, failed });
        return result;
      })
    )
  );

  return {
    total: list.length,
    completed,
    loaded,
    failed,
    results,
  };
};


const AggressiveImagePreloader = React.memo(function AggressiveImagePreloader({
  urls,
  onProgress,
  onReady,
}) {
  const cleanUrls = useMemo(() => {
    const seen = new Set();
    const unique = (urls || [])
      .map((src) => (typeof src === "string" ? src.trim() : ""))
      .filter(isPreloadableImageUrl)
      .filter((src) => {
        if (seen.has(src)) return false;
        seen.add(src);
        return true;
      });

    // Kiosk verisindeki bütün dil görsellerini gerçek DOM <img> öğeleriyle
    // decode ediyoruz. Çok büyük/bozuk bir veri cevabı tarayıcıyı kilitlemesin
    // diye yalnızca olağanüstü yüksek bir güvenlik sınırı tutulur.
    return unique.slice(0, DOM_IMAGE_PRELOAD_LIMIT);
  }, [urls]);

  const imgRefs = useRef(new Map());
  const readyKeyRef = useRef("");

  useEffect(() => {
    const key = cleanUrls.join("|");

    if (!cleanUrls.length) {
      onProgress?.({ total: 0, completed: 0, loaded: 0, failed: 0 });
      onReady?.({ total: 0, completed: 0, loaded: 0, failed: 0 });
      return;
    }

    if (readyKeyRef.current === key) return;

    let cancelled = false;
    let completed = 0;
    let loaded = 0;
    let failed = 0;
    let fallbackTimer = null;

    const emitProgress = () => {
      onProgress?.({ total: cleanUrls.length, completed, loaded, failed });
    };

    const waitForDomImage = (src) => {
      return new Promise((resolve) => {
        const img = imgRefs.current.get(src);
        if (!img) {
          failed += 1;
          completed += 1;
          emitProgress();
          resolve({ src, ok: false, status: "missing-dom-img" });
          return;
        }

        let done = false;
        let timer = null;

        const finish = async (ok, status) => {
          if (done || cancelled) return;
          done = true;
          if (timer) window.clearTimeout(timer);

          if (ok) {
            try {
              // Dosya yüklü olsa bile decode tamamlanmadan menüye geçerse
              // ilk ekranda beyaz/boş görünebilir. Bunu burada bitiriyoruz.
              if (typeof img.decode === "function") await img.decode();
            } catch {}
            loaded += 1;
          } else {
            failed += 1;
          }

          completed += 1;
          emitProgress();
          resolve({ src, ok, status });
        };

        timer = window.setTimeout(() => {
          finish(false, "timeout");
        }, DOM_IMAGE_PRELOAD_TIMEOUT_MS);

        if (img.complete && img.naturalWidth > 0) {
          finish(true, "cached");
          return;
        }

        img.onload = () => finish(true, "loaded");
        img.onerror = () => finish(false, "error");
      });
    };

    // Önce browser'a DOM'u paint kuyruğuna sokması için 2 frame veriyoruz.
    let raf1 = 0;
    let raf2 = 0;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        if (cancelled) return;
        emitProgress();

        Promise.all(cleanUrls.map(waitForDomImage)).then((results) => {
          if (cancelled) return;

          // Decode bittikten sonra 1 frame daha bekle; böylece sonraki ekranda
          // image paint cache sıcak kalır.
          window.requestAnimationFrame(() => {
            if (cancelled) return;
            readyKeyRef.current = key;
            onReady?.({
              total: cleanUrls.length,
              completed,
              loaded,
              failed,
              results,
            });
          });
        });
      });
    });

    // Çok yavaş/bozuk resim varsa kiosk sonsuza kadar kilitlenmesin.
    fallbackTimer = window.setTimeout(() => {
      if (cancelled) return;
      readyKeyRef.current = key;
      onReady?.({ total: cleanUrls.length, completed, loaded, failed, fallback: true });
    }, DOM_IMAGE_PRELOAD_TIMEOUT_MS + 2500);

    return () => {
      cancelled = true;
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
    };
  }, [cleanUrls, onProgress, onReady]);

  if (!cleanUrls.length) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        // Aktif ekranın üstünde ama neredeyse tamamen şeffaf.
        // Böylece browser gerçekten paint/decode yapıyor; pointerEvents kapalı.
        zIndex: 1000,
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        pointerEvents: "none",
        opacity: 0.001,
        transform: "translate3d(0,0,0)",
        contentVisibility: "visible",
        contain: "paint",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 96px)",
          gridAutoRows: "96px",
          gap: 4,
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {cleanUrls.map((src, index) => (
          <img
            key={`${src}_${index}`}
            ref={(el) => {
              if (el) imgRefs.current.set(src, el);
              else imgRefs.current.delete(src);
            }}
            src={src}
            alt=""
            loading="eager"
            decoding="async"
            fetchPriority={index < IMAGE_PRELOAD_HIGH_PRIORITY_COUNT ? "high" : "low"}
            style={{
              width: 96,
              height: 96,
              objectFit: "cover",
              display: "block",
              transform: "translateZ(0)",
            }}
          />
        ))}
      </div>
    </div>
  );
});

const IMAGE_FIELD_KEY_PATTERN = /(image|img|icon|photo|picture|cover|logo|banner|poster|thumbnail|thumb|background|wallpaper|reklam)/i;
const IMAGE_FILE_URL_PATTERN = /\.(?:avif|bmp|gif|ico|jpe?g|jfif|png|svg|webp)(?:[?#].*)?$/i;

const looksLikeImageAsset = (value, key = "") => {
  if (!isPreloadableImageUrl(value)) return false;
  const clean = String(value || "").trim();
  if (clean.startsWith("data:")) return clean.startsWith("data:image/");
  return IMAGE_FIELD_KEY_PATTERN.test(String(key || "")) || IMAGE_FILE_URL_PATTERN.test(clean);
};

const collectNestedImageUrls = (root, add) => {
  const visited = new WeakSet();

  const walk = (value, parentKey = "") => {
    if (!value) return;

    if (typeof value === "string") {
      if (looksLikeImageAsset(value, parentKey)) add(value);
      return;
    }

    if (typeof value !== "object") return;
    if (visited.has(value)) return;
    visited.add(value);

    if (Array.isArray(value)) {
      value.forEach((child) => walk(child, parentKey));
      return;
    }

    Object.entries(value).forEach(([key, child]) => {
      if (typeof child === "string" && looksLikeImageAsset(child, key)) {
        add(child);
      } else {
        walk(child, key);
      }
    });
  };

  walk(root);
};


const collectRecommendedMediaAssets = (data) => {
  const images = [];
  const videos = [];
  const imageSeen = new Set();
  const videoSeen = new Set();
  const visited = new WeakSet();

  const addImage = (value) => {
    const clean = typeof value === "string" ? value.trim() : "";
    if (!clean || imageSeen.has(clean) || !isPreloadableImageUrl(clean)) return;
    imageSeen.add(clean);
    images.push(clean);
  };

  const addVideo = (value) => {
    const clean = typeof value === "string" ? value.trim() : "";
    if (!clean || videoSeen.has(clean) || !isRemoteVideoUrl(clean)) return;
    videoSeen.add(clean);
    videos.push(clean);
  };

  const walk = (value) => {
    if (!value || typeof value !== "object") return;
    if (visited.has(value)) return;
    visited.add(value);

    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }

    const mediaType = String(value.mediaType || "").trim().toLowerCase();
    const mediaUrl = String(value.mediaUrl || "").trim();
    if (mediaType === "image") addImage(mediaUrl);
    if (mediaType === "video") addVideo(mediaUrl);

    // Yeni öneri medya yapısında kart fotoğrafı ve tanıtım videosu ayrı tutulur.
    // İkisi de kiosk açılırken hazırlanır; eski mediaType/mediaUrl kayıtları da desteklenir.
    addImage(value.imageUrl);
    addImage(value.posterUrl);
    addImage(value.image);
    addImage(value.photoUrl);
    addImage(value.coverPhotoUrl);
    addVideo(value.videoUrl);
    addVideo(value.video);

    Object.values(value).forEach(walk);
  };

  walk(data?.preferance?.recommendedProducts || []);
  walk(data?.preferance?.reservationCompleteExtraProducts || []);
  return { images, videos };
};

const recommendedVideoPreloadCache = new Map();
const RECOMMENDED_VIDEO_PRELOAD_TIMEOUT_MS = 45000;
const RECOMMENDED_VIDEO_PRELOAD_CONCURRENCY = 3;

const preloadOneRecommendedVideo = (src) => {
  const cleanSrc = typeof src === "string" ? src.trim() : "";
  if (!isRemoteVideoUrl(cleanSrc)) {
    return Promise.resolve({ src: cleanSrc, ok: false, status: "skipped" });
  }
  if (recommendedVideoPreloadCache.has(cleanSrc)) {
    return recommendedVideoPreloadCache.get(cleanSrc).promise;
  }

  let element = null;
  const promise = new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve({ src: cleanSrc, ok: false, status: "no-document" });
      return;
    }

    const video = document.createElement("video");
    element = video;
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("muted", "");
    video.style.position = "fixed";
    video.style.left = "-10000px";
    video.style.top = "-10000px";
    video.style.width = "2px";
    video.style.height = "2px";
    video.style.opacity = "0.001";
    video.style.pointerEvents = "none";

    let settled = false;
    let timer = null;
    const finish = (ok, status) => {
      if (settled) return;
      settled = true;
      if (timer) window.clearTimeout(timer);
      video.removeEventListener("canplaythrough", onReady);
      video.removeEventListener("loadeddata", onLoadedData);
      video.removeEventListener("error", onError);
      resolve({ src: cleanSrc, ok, status });
    };
    const onReady = () => finish(true, "canplaythrough");
    const onLoadedData = () => {
      // canplaythrough bazı CDN/Chromium kombinasyonlarında hiç gelmeyebiliyor.
      // loadeddata geldiğinde dosya artık media cache tarafından hazırlanmıştır;
      // kısa bir ek süre verip hazır kabul ediyoruz.
      window.setTimeout(() => finish(true, "loadeddata"), 350);
    };
    const onError = () => finish(false, "error");

    video.addEventListener("canplaythrough", onReady, { once: true });
    video.addEventListener("loadeddata", onLoadedData, { once: true });
    video.addEventListener("error", onError, { once: true });
    timer = window.setTimeout(() => finish(false, "timeout"), RECOMMENDED_VIDEO_PRELOAD_TIMEOUT_MS);

    document.body.appendChild(video);
    video.src = cleanSrc;
    try { video.load(); } catch {}
  });

  recommendedVideoPreloadCache.set(cleanSrc, { promise, element });
  return promise;
};

const preloadRecommendedVideos = async (urls, onProgress) => {
  const list = Array.from(new Set((urls || []).map((x) => String(x || "").trim()).filter(isRemoteVideoUrl)));
  if (!list.length) {
    const empty = { total: 0, completed: 0, loaded: 0, failed: 0 };
    onProgress?.(empty);
    return empty;
  }

  let cursor = 0;
  let completed = 0;
  let loaded = 0;
  let failed = 0;

  const worker = async () => {
    while (cursor < list.length) {
      const index = cursor++;
      const result = await preloadOneRecommendedVideo(list[index]);
      completed += 1;
      if (result?.ok) loaded += 1;
      else failed += 1;
      onProgress?.({ total: list.length, completed, loaded, failed });
    }
  };

  const workerCount = Math.min(RECOMMENDED_VIDEO_PRELOAD_CONCURRENCY, list.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return { total: list.length, completed, loaded, failed };
};

const collectImageUrls = (data) => {
  if (!data) return [];
  const urls = [];
  const seen = new Set();

  const add = (v) => {
    if (typeof v !== "string") return;
    const value = v.trim();
    if (!value || seen.has(value) || !isPreloadableImageUrl(value)) return;
    seen.add(value);
    urls.push(value);
  };

  // İlk görünen ekranlar ve ödeme ikonları en önce gelir.
  add(data?.designData?.applogo);
  add(data?.designData?.appvideo);
  add(CASH_IMG);
  add(CARD_IMG);
  add(MULTINET_IMG);
  add(SODEXO_IMG);
  add(SETCARD_IMG);
  add(METROPOL_IMG);
  add(EDENRED_IMG);

  // Menü açıldığında ilk görünen alanlar.
  (data?.menuItems || []).forEach((x) => add(x?.icon));
  (data?.homepageData?.categories || []).forEach((x) => add(x?.image));
  add(data?.homepageData?.reklamphoto);
  (data?.homepageData?.products || []).forEach((x) => add(x?.image));

  // Kategori ürünleri.
  Object.values(data?.categoryContent || {}).forEach((list) => {
    (list || []).forEach((x) => add(x?.image));
  });

  // Personel kartları ve personelin dile özel olabilecek medya alanları.
  (data?.personnel || []).forEach((person) => {
    add(person?.coverPhotoUrl);
    collectNestedImageUrls(person?.localizedData, add);
  });

  // Ürün detayları, bütün dillerdeki soru ve opsiyon görselleri.
  // Türkçe kayıt çoğunlukla ana questions alanında; diğer diller localizedData
  // içindedir. İki alan da başlangıçta decode edilmeden menü hazır sayılmaz.
  Object.values(data?.productsByUniqcode || {}).forEach((product) => {
    add(product?.image);
    (product?.questions || []).forEach((q) => {
      add(q?.image);
      (q?.options || []).forEach((opt) => add(opt?.image));
    });

    (product?.localizedData || []).forEach((locale) => {
      add(locale?.image);
      (locale?.questions || []).forEach((q) => {
        add(q?.image);
        (q?.options || []).forEach((opt) => add(opt?.image));
      });
    });
  });

  // Ortak önerilen/ekstra ürünlerin bütün dillerdeki medya görselleri de
  // kiosk açılırken hazırlanır. mediaUrl uzantısız CDN adresi olsa bile kaçırılmaz.
  collectRecommendedMediaAssets(data).images.forEach(add);

  // Gelecekte yeni bir görsel alanı eklense bile atlanmasın. image/icon/photo/
  // cover/logo/banner/poster/background benzeri bütün alanları derin tarar.
  collectNestedImageUrls(data, add);

  try {
    add(`${process.env.PUBLIC_URL || ""}/coffee.png`);
    add(`${process.env.PUBLIC_URL || ""}/cafe-de-colombia-logo-png-transparent.png`);
    add(`${process.env.PUBLIC_URL || ""}/vecteezy_hand-holds-warm-coffee-cup-offering-convenience-and-moment_68028710.png`);
  } catch {}

  return urls;
};

let imagePreloadQueue = Promise.resolve(null);

const scheduleImagePreload = (data) => {
  const urls = collectImageUrls(data);
  if (!urls.length) return Promise.resolve({ total: 0, completed: 0, loaded: 0, failed: 0 });

  imagePreloadQueue = imagePreloadQueue
    .catch(() => null)
    .then(() =>
      preloadImages(urls, {
        priorityCount: Math.min(IMAGE_PRELOAD_HIGH_PRIORITY_COUNT, urls.length),
        timeoutMs: IMAGE_PRELOAD_TIMEOUT_MS,
      })
    );

  return imagePreloadQueue;
};

const isRemoteVideoUrl = (value) => {
  if (typeof value !== "string") return false;
  return /^https?:\/\//i.test(value.trim());
};

// Bootstrap cevabının herhangi bir seviyesindeki videoUrl alanlarını toplar.
// Ürün, personel, opsiyon veya daha sonra eklenecek başka video tiplerinde de çalışır.
const collectVideoAssets = (data) => {
  const assets = [];
  const seen = new Set();
  const visited = new WeakSet();

  const walk = (value, pathParts = []) => {
    if (!value || typeof value !== "object") return;
    if (visited.has(value)) return;
    visited.add(value);

    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, [...pathParts, String(index)]));
      return;
    }

    Object.entries(value).forEach(([key, child]) => {
      const normalizedKey = String(key || "").replace(/[_-]/g, "").toLowerCase();
      const parentMediaType = String(value.mediaType || "").trim().toLowerCase();
      const isVideoField = normalizedKey === "videourl" ||
        (normalizedKey === "mediaurl" && parentMediaType === "video") ||
        (normalizedKey === "video" && isRemoteVideoUrl(child));

      if (isVideoField && isRemoteVideoUrl(child)) {
        const remoteUrl = child.trim();
        const videoId = String(value.videoId || value.videoID || "").trim();
        const videoVersion = Number(value.videoVersion ?? value.version ?? 1) || 1;
        const videoUpdatedAt = value.videoUpdatedAt || value.updatedAt || null;
        const identity = `${videoId || remoteUrl}|${videoVersion}|${videoUpdatedAt || ""}`;
        if (!seen.has(identity)) {
          seen.add(identity);
          assets.push({
            videoUrl: remoteUrl,
            videoId,
            videoVersion,
            videoUpdatedAt,
            languageCode: value.languageCode || value.language || "",
            label: value.name || value.label || value.title || pathParts.join(" / "),
          });
        }
      }

      walk(child, [...pathParts, key]);
    });
  };

  walk(data, ["bootstrap"]);
  return assets;
};

const replaceVideoUrlsWithLocalCache = (data, cacheItems = []) => {
  const urlMap = new Map(
    (Array.isArray(cacheItems) ? cacheItems : [])
      .filter((item) => item?.cached && item?.remoteUrl && item?.localUrl)
      .map((item) => [String(item.remoteUrl), String(item.localUrl)])
  );
  if (!urlMap.size) return data;

  const visited = new WeakMap();
  const walk = (value) => {
    if (!value || typeof value !== "object") return value;
    if (visited.has(value)) return visited.get(value);

    if (Array.isArray(value)) {
      const next = [];
      visited.set(value, next);
      value.forEach((item) => next.push(walk(item)));
      return next;
    }

    const next = {};
    visited.set(value, next);
    Object.entries(value).forEach(([key, child]) => {
      const normalizedKey = String(key || "").replace(/[_-]/g, "").toLowerCase();
      const parentMediaType = String(value.mediaType || "").trim().toLowerCase();
      const isVideoField = normalizedKey === "videourl" || normalizedKey === "video" ||
        (normalizedKey === "mediaurl" && parentMediaType === "video");
      if (isVideoField && typeof child === "string" && urlMap.has(child)) {
        next[key] = urlMap.get(child);
      } else {
        next[key] = walk(child);
      }
    });
    return next;
  };

  return walk(data);
};

let electronVideoCacheQueue = Promise.resolve(null);

const warmElectronVideoCache = async (bootstrapData) => {
  const bridge = window?.kioskVideoCache;
  if (!bridge?.sync) return null;

  const videos = collectVideoAssets(bootstrapData);
  if (!videos.length) {
    return { success: true, total: 0, completed: 0, downloaded: 0, failed: 0, items: [] };
  }

  return bridge.sync({
    namespace: getAuthCard() || "default",
    videos,
  });
};

const scheduleElectronVideoCache = (data) => {
  if (!data || !window?.kioskVideoCache?.sync) return Promise.resolve(null);
  if (!collectVideoAssets(data).length) return Promise.resolve(null);

  electronVideoCacheQueue = electronVideoCacheQueue
    .catch(() => null)
    .then(() => warmElectronVideoCache(data));
  return electronVideoCacheQueue;
};

const getEmojiFromCode = (emojiCode) => {
  const emojiMap = {
    coffee: "☕",
    tea: "🍵",
    bubble_tea: "🧋",
    juice: "🧃",
    soda: "🥤",
    espresso: "☕",
    cappuccino: "☕",
    latte: "☕",
    mocha: "☕",
    cold_brew: "🥤",
    water: "💧",
    wine: "🍷",
    beer: "🍺",
    cocktail: "🍹",
    croissant: "🥐",
    sandwich: "🥪",
    burger: "🍔",
    pizza: "🍕",
    salad: "🥗",
    sushi: "🍣",
    taco: "🌮",
    burrito: "🌯",
    ramen: "🍜",
    spaghetti: "🍝",
    soup: "🍲",
    stew: "🥘",
    cake: "🍰",
    cupcake: "🧁",
    ice_cream: "🍦",
    doughnut: "🍩",
    cookie: "🍪",
    pie: "🥧",
    chocolate: "🍫",
    candy: "🍬",
    pudding: "🍮",
    pancake: "🥞",
    waffle: "🧇",
    egg: "🍳",
    bacon: "🥓",
    bread: "🍞",
    bagel: "🥯",
    drinks: "🥤",
    desserts: "🍰",
    breakfast: "🥞",
    lunch: "🍽️",
    dinner: "🍲",
    snacks: "🍪",
    specials: "⭐",
    popular: "🔥",
    new: "🆕",
    vegan: "🌱",
    default: "📦",
  };

  if (!emojiCode) return emojiMap.default;
  return emojiMap[String(emojiCode).trim()] || emojiMap.default;
};

const getColorClass = (colorName) => {
  const colorMap = {
    red: "from-red-500 to-red-600",
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    yellow: "from-yellow-500 to-yellow-600",
    orange: "from-orange-500 to-orange-600",
    purple: "from-purple-500 to-purple-600",
    pink: "from-pink-500 to-pink-600",
    indigo: "from-indigo-500 to-indigo-600",
    teal: "from-teal-500 to-teal-600",
    cyan: "from-cyan-500 to-cyan-600",
    amber: "from-amber-500 to-amber-600",
    emerald: "from-emerald-500 to-emerald-600",
    lime: "from-lime-500 to-lime-600",
    rose: "from-rose-500 to-rose-600",
    fuchsia: "from-fuchsia-500 to-fuchsia-600",
    violet: "from-violet-500 to-violet-600",
    sunset: "from-orange-500 to-pink-500",
    ocean: "from-blue-500 to-teal-500",
    forest: "from-green-500 to-emerald-500",
    berry: "from-purple-500 to-pink-500",
    fire: "from-red-500 to-orange-500",
    sky: "from-cyan-500 to-blue-500",
    default: "from-amber-500 to-orange-500",
  };

  if (!colorName) return colorMap.default;
  return colorMap[String(colorName).trim().toLowerCase()] || colorMap.default;
};

const normalizeBooleanFlag = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const text = String(value).trim().toLowerCase();
  return text === "true" || text === "1" || text === "yes" || text === "on";
};

const normalizeDiscountRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round((n + Number.EPSILON) * 100) / 100));
};

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const getDiscountPrice = (grossValue, enabled, rateValue) => {
  const original = Math.max(0, Number(grossValue || 0));
  const rate = enabled ? normalizeDiscountRate(rateValue) : 0;
  const effective = enabled ? roundMoney(original * (1 - rate / 100)) : roundMoney(original);
  return {
    original: roundMoney(original),
    effective,
    rate,
    amount: roundMoney(original - effective),
  };
};

const getProductPriceInfo = (product) => getDiscountPrice(
  product?.basePrice ?? parseMoneyToNumber(product?.price),
  !!product?.discountEnabled,
  product?.discountRate
);

const getOptionPriceInfo = (option) => getDiscountPrice(
  option?.price,
  !!option?.discountEnabled,
  option?.discountRate
);

const normalizeProductForUI = (product) => {
  if (!product) return null;

  // "Ana Ürünü Fişe Ekle" ana anahtardır. Kapalıysa ana ürün ne sepette
  // ne fişte ne de POS'ta ana kalem olarak kabul edilir.
  const addMainProductToReceipt = normalizeBooleanFlag(product?.addMainProductToReceipt);
  const menuProductIsMain = addMainProductToReceipt && normalizeBooleanFlag(product?.menuProductIsMain);

  return {
    ...product,
    menuProductIsMain,
    addMainProductToReceipt,
    discountEnabled: !!product?.discountEnabled,
    discountRate: normalizeDiscountRate(product?.discountRate),
    questions: (product.questions || []).map((question, qIndex) => ({
      ...question,
      __questionKey: `q_${question.id}_${qIndex}`,
      options: (question.options || []).map((option, oIndex) => ({
        ...option,
        discountEnabled: !!option?.discountEnabled,
        discountRate: normalizeDiscountRate(option?.discountRate),
        __optionKey: `q${question.id}_o${option.id}_${oIndex}_${String(option.name || "")
          .replace(/\s+/g, "_")
          .toLowerCase()}`,
      })),
    })),
  };
};


const LANGUAGE_META = {
  tr: { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  en: { code: "en", label: "English", flag: "🇬🇧" },
  ru: { code: "ru", label: "Русский", flag: "🇷🇺" },
  de: { code: "de", label: "Deutsch", flag: "🇩🇪" },
  ar: { code: "ar", label: "العربية", flag: "🇸🇦", dir: "rtl" },
  cy: { code: "cy", label: "Cymraeg", flag: "🏴" },
  fr: { code: "fr", label: "Français", flag: "🇫🇷" },
  es: { code: "es", label: "Español", flag: "🇪🇸" },
  it: { code: "it", label: "Italiano", flag: "🇮🇹" },
};

const normalizeLanguageCode = (value, fallback = "tr") => {
  const raw = String(value || "").trim().toLowerCase();
  const aliases = {
    turkish: "tr", "türkçe": "tr", turkce: "tr",
    english: "en", "ingilizce": "en",
    russian: "ru", "русский": "ru", "rusça": "ru", rusca: "ru",
    german: "de", deutsch: "de", "almanca": "de",
    arabic: "ar", "العربية": "ar", "arapça": "ar", arapca: "ar",
    welsh: "cy", cymraeg: "cy",
    french: "fr", français: "fr",
    spanish: "es", español: "es",
    italian: "it", italiano: "it",
  };
  if (aliases[raw]) return aliases[raw];
  const clean = raw.replace(/[^a-z0-9-]/g, "").slice(0, 20);
  return clean || fallback;
};

const uniqueLanguageCodes = (value) => {
  const source = Array.isArray(value)
    ? value
    : String(value || "").split(",");
  const seen = new Set();
  const out = [];
  source.forEach((item) => {
    const code = normalizeLanguageCode(item, "");
    if (!code || seen.has(code)) return;
    seen.add(code);
    out.push(code);
  });
  return out.length ? out : ["tr", "en", "ru", "de", "ar"];
};

const getLanguageMeta = (code) => {
  const normalized = normalizeLanguageCode(code);
  return getUiLanguageMeta(normalized);
};

const findLocalizedEntry = (item, languageCode) => {
  const code = normalizeLanguageCode(languageCode);
  const list = Array.isArray(item?.localizedData) ? item.localizedData : [];
  return (
    list.find(
      (entry) =>
        normalizeLanguageCode(entry?.languageCode || entry?.language, "") === code
    ) || null
  );
};

const localizeProductForLanguage = (product, languageCode) => {
  if (!product) return null;
  const locale = findLocalizedEntry(product, languageCode);
  const hasLocalizedData =
    Array.isArray(product?.localizedData) && product.localizedData.length > 0;
  if (hasLocalizedData && !locale) return null;

  const localized = locale
    ? {
        ...product,
        name: locale.name || product.name || "",
        desc:
          locale.description ||
          locale.desc ||
          product.desc ||
          product.description ||
          "",
        description:
          locale.description ||
          locale.desc ||
          product.description ||
          product.desc ||
          "",
        videoUrl: locale.videoUrl || product.videoUrl || "",
        videoId: locale.videoId || product.videoId || "",
        videoDescription:
          locale.videoDescription || product.videoDescription || "",
        videoVersion: locale.videoVersion || product.videoVersion || 1,
        videoUpdatedAt: locale.videoUpdatedAt || product.videoUpdatedAt || null,
        questions: Array.isArray(locale.questions)
          ? locale.questions
          : product.questions || [],
        hasQuestions: Array.isArray(locale.questions)
          ? locale.questions.length > 0
          : !!product.hasQuestions,
        basePrice: Number(locale.basePrice ?? product.basePrice ?? parseMoneyToNumber(product.price) ?? 0),
        kdv: Number(locale.kdv ?? product.kdv ?? 0),
        departmanno: normalizeDepartmentNoForPayment(
          locale.departmanno ?? locale.departmentNo ?? locale.departmentno ?? product.departmanno ?? product.departmentNo ?? product.departmentno ?? ""
        ),
        discountEnabled: !!locale.discountEnabled,
        discountRate: normalizeDiscountRate(locale.discountRate),
        personnelEnabled: !!locale.personnelEnabled,
        timeEnabled: !!locale.timeEnabled,
        dayEnabled: !!locale.dayEnabled,
        languageCode: normalizeLanguageCode(languageCode),
        languageName:
          locale.language || getLanguageMeta(languageCode).label,
      }
    : {
        ...product,
        languageCode: normalizeLanguageCode(languageCode),
      };
  return normalizeProductForUI(localized);
};

const localizeCategoryForLanguage = (category, languageCode) => {
  const locale = findLocalizedEntry(category, languageCode);
  const hasLocalizedData =
    Array.isArray(category?.localizedData) && category.localizedData.length > 0;
  if (hasLocalizedData && !locale) return null;
  return {
    ...category,
    label: locale?.label || locale?.name || category?.label || "Kategori",
    languageCode: normalizeLanguageCode(languageCode),
  };
};

const normalizeCategoryMatchValue = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/[^a-z0-9]/g, "");

const collectCategoryAliases = (category) => {
  const aliases = [category?.id, category?.liability, category?.label];
  if (Array.isArray(category?.localizedData)) {
    category.localizedData.forEach((locale) => {
      aliases.push(locale?.label, locale?.name);
    });
  }
  return new Set(aliases.map(normalizeCategoryMatchValue).filter(Boolean));
};

const productMatchesCategory = (product, categoryAliases) => {
  if (!product || !(categoryAliases instanceof Set) || categoryAliases.size === 0) {
    return false;
  }
  const productAliases = [
    product?.categoryId,
    product?.liability,
    product?.category,
  ]
    .map(normalizeCategoryMatchValue)
    .filter(Boolean);
  return productAliases.some((alias) => categoryAliases.has(alias));
};

const getProductsForCategory = (bootstrapData, category) => {
  const rows = [];
  const seen = new Set();
  const add = (product) => {
    if (!product) return;
    const key = String(product?.uniqcode || product?.id || `${product?.name || ""}_${product?.category || ""}`);
    if (!key || seen.has(key)) return;
    seen.add(key);
    rows.push(product);
  };

  const directKeys = [category?.id, category?.liability, category?.label].filter(Boolean);
  directKeys.forEach((key) => {
    const list = bootstrapData?.categoryContent?.[key];
    if (Array.isArray(list)) list.forEach(add);
  });

  const aliases = collectCategoryAliases(category);
  const fallbackProducts = [
    ...Object.values(bootstrapData?.productsByUniqcode || {}),
    ...(Array.isArray(bootstrapData?.products) ? bootstrapData.products : []),
    ...Object.values(bootstrapData?.categoryContent || {}).flatMap((list) =>
      Array.isArray(list) ? list : []
    ),
  ];

  fallbackProducts.forEach((product) => {
    if (productMatchesCategory(product, aliases)) add(product);
  });

  return rows;
};

const localizePersonnelForLanguage = (person, languageCode) => {
  const locale = findLocalizedEntry(person, languageCode);
  if (!locale) return null;
  return {
    ...person,
    ...locale,
    languageCode: normalizeLanguageCode(languageCode),
    personelId: person.personelId || person.personnelId || person.id || "",
    coverPhotoUrl: person.coverPhotoUrl || "",
    schedule: person.schedule || {},
    gender: person.gender || "",
  };
};


const localizeRecommendedProductForLanguage = (product, languageCode) => {
  if (!product) return null;
  const code = normalizeLanguageCode(languageCode);
  const locale = findLocalizedEntry(product, code);
  const hasLocalizedData = Array.isArray(product?.localizedData) && product.localizedData.length > 0;
  if (hasLocalizedData && !locale) return null;
  const source = locale || product;
  if (source?.active === false) return null;
  const legacyMediaType = source?.mediaType === "video" ? "video" : source?.mediaType === "image" ? "image" : "none";
  const legacyMediaUrl = String(source?.mediaUrl || "");
  const imageUrl = String(
    source?.imageUrl || source?.posterUrl || source?.thumbnailUrl || source?.thumbUrl ||
    (legacyMediaType === "image" ? legacyMediaUrl : source?.image || source?.photoUrl || "")
  );
  const videoUrl = String(source?.videoUrl || (legacyMediaType === "video" ? legacyMediaUrl : ""));
  const mediaType = videoUrl ? "video" : imageUrl ? "image" : "none";
  return {
    ...product,
    ...source,
    id: String(product?.id || product?.recommendationId || ""),
    recommendationId: String(product?.id || product?.recommendationId || ""),
    uniqcode: `recommended_${String(product?.id || product?.recommendationId || "item")}`,
    languageCode: code,
    name: String(source?.name || ""),
    description: String(source?.description || source?.desc || ""),
    basePrice: Number(source?.basePrice ?? source?.price ?? 0),
    kdv: Number(source?.kdv || 0),
    discountEnabled: !!source?.discountEnabled,
    discountRate: normalizeDiscountRate(source?.discountRate),
    departmanno: normalizeDepartmentNoForPayment(source?.departmanno ?? source?.departmentNo ?? source?.departmentno ?? ""),
    hasQuantity: !!source?.hasQuantity,
    personnelEnabled: !!source?.personnelEnabled,
    timeEnabled: !!source?.timeEnabled,
    dayEnabled: !!source?.dayEnabled,
    durationEnabled: !!source?.durationEnabled,
    durationVisible: source?.durationVisible !== false,
    durationValue: String(source?.durationValue || ""),
    sharedTimeValue: String(source?.durationValue || ""),
    imageUrl,
    videoUrl,
    mediaType,
    mediaUrl: videoUrl || imageUrl,
    image: imageUrl,
    posterUrl: imageUrl,
    videoId: videoUrl ? String(source?.videoId || "") : "",
    mediaVersion: Math.max(1, Number(source?.mediaVersion || source?.videoVersion || 1)),
    mediaUpdatedAt: source?.mediaUpdatedAt || source?.videoUpdatedAt || null,
  };
};

const getLocalizedHeadingFromField = (preferance, fieldName, languageCode, fallbackText) => {
  const code = normalizeLanguageCode(languageCode);
  const rows = Array.isArray(preferance?.[fieldName]) ? preferance[fieldName] : [];
  const exact = rows.find((row) => normalizeLanguageCode(row?.languageCode || row?.language, "") === code);
  const turkish = rows.find((row) => normalizeLanguageCode(row?.languageCode || row?.language, "") === "tr");
  return String(exact?.text || exact?.title || turkish?.text || fallbackText);
};

const getLocalizedRecommendedHeading = (preferance, languageCode) =>
  getLocalizedHeadingFromField(preferance, "recommendedHeadingLocalizedData", languageCode, "Bu hizmeti alanlar bunları da ekledi");

const getLocalizedReservationCompleteExtraHeading = (preferance, languageCode) =>
  getLocalizedHeadingFromField(preferance, "reservationCompleteExtraHeadingLocalizedData", languageCode, "Rezervasyonunuza bunları da eklemek ister misiniz?");

const getRecommendationUiText = (languageCode = "tr") => {
  const code = normalizeLanguageCode(languageCode);
  const map = {
    tr: { add: "Ekle", added: "Eklendi", remove: "Çıkar", close: "Kapat", noThanks: "Devam Et", recommendation: "Önerilen Ürün", quantity: "Adet" },
    en: { add: "Add", added: "Added", remove: "Remove", close: "Close", noThanks: "Continue", recommendation: "Recommended Product", quantity: "Quantity" },
    ru: { add: "Добавить", added: "Добавлено", remove: "Удалить", close: "Закрыть", noThanks: "Продолжить", recommendation: "Рекомендуемый товар", quantity: "Количество" },
    de: { add: "Hinzufügen", added: "Hinzugefügt", remove: "Entfernen", close: "Schließen", noThanks: "Weiter", recommendation: "Empfohlenes Produkt", quantity: "Menge" },
    ar: { add: "أضف", added: "تمت الإضافة", remove: "إزالة", close: "إغلاق", noThanks: "متابعة", recommendation: "منتج مقترح", quantity: "الكمية" },
  };
  return map[code] || map.en;
};


const getAddedCountText = (count, languageCode = "tr") => {
  const qty = Math.max(0, Math.trunc(Number(count || 0)));
  if (!qty) return "";
  const code = normalizeLanguageCode(languageCode);
  const templates = {
    tr: `${qty} kere eklendi`,
    en: `Added ${qty} ${qty === 1 ? "time" : "times"}`,
    ru: `Добавлено: ${qty}`,
    de: `${qty}× hinzugefügt`,
    ar: `تمت الإضافة ${qty} مرة`,
    cy: `Ychwanegwyd ${qty} gwaith`,
    fr: `Ajouté ${qty} fois`,
    es: `Añadido ${qty} ${qty === 1 ? "vez" : "veces"}`,
    it: `Aggiunto ${qty} ${qty === 1 ? "volta" : "volte"}`,
  };
  return templates[code] || templates.en;
};

const localizeBootstrapForLanguage = (data, languageCode) => {
  if (!data) return DEFAULT_BOOTSTRAP;
  const code = normalizeLanguageCode(languageCode);
  const rawCategories = Array.isArray(data.menuItems)
    ? data.menuItems
    : Array.isArray(data.categories)
    ? data.categories
    : [];

  const localizedCategories = [];
  const categoryLabelMap = new Map();
  rawCategories.forEach((item) => {
    const localized = localizeCategoryForLanguage(item, code);
    if (!localized) return;
    localizedCategories.push(localized);
    [item?.label, item?.liability, item?.id, localized?.liability, localized?.id]
      .filter(Boolean)
      .forEach((key) =>
        categoryLabelMap.set(
          String(key),
          localized.label || item?.label || String(key)
        )
      );
  });

  const localizedProductsByUniqcode = {};
  Object.entries(data.productsByUniqcode || {}).forEach(
    ([uniqcode, product]) => {
      const localized = localizeProductForLanguage(product, code);
      if (localized) localizedProductsByUniqcode[uniqcode] = localized;
    }
  );

  const localizedCategoryContent = {};
  Object.entries(data.categoryContent || {}).forEach(([key, list]) => {
    const rows = (Array.isArray(list) ? list : [])
      .map(
        (product) =>
          localizedProductsByUniqcode[product?.uniqcode] ||
          localizeProductForLanguage(product, code)
      )
      .filter(Boolean);
    if (rows.length) localizedCategoryContent[key] = rows;
  });


  const rawPreferance = data.preferance || {};
  const localizedRecommendedProducts = (Array.isArray(rawPreferance.recommendedProducts) ? rawPreferance.recommendedProducts : [])
    .map((product) => localizeRecommendedProductForLanguage(product, code))
    .filter(Boolean);
  const localizedReservationCompleteExtraProducts = (Array.isArray(rawPreferance.reservationCompleteExtraProducts) ? rawPreferance.reservationCompleteExtraProducts : [])
    .map((product) => localizeRecommendedProductForLanguage(product, code))
    .filter(Boolean)
    .map((product) => {
      const sourceId = String(product.recommendationId || product.id || "");
      const scopedId = `reservation_complete_${sourceId}`;
      return {
        ...product,
        id: scopedId,
        recommendationId: scopedId,
        sourceRecommendationId: sourceId,
        extraCatalogKind: "reservationComplete",
      };
    });
  const localizedPreferance = {
    ...rawPreferance,
    recommendedHeading: getLocalizedRecommendedHeading(rawPreferance, code),
    recommendedProducts: localizedRecommendedProducts,
    reservationCompleteExtraHeading: getLocalizedReservationCompleteExtraHeading(rawPreferance, code),
    reservationCompleteExtraProducts: localizedReservationCompleteExtraProducts,
  };

  const homepageData = {
    ...(data.homepageData || {}),
    categories: (data.homepageData?.categories || [])
      .map((category) => {
        const localized = localizeCategoryForLanguage(category, code);
        if (!localized) return null;
        return {
          ...category,
          ...localized,
          label:
            categoryLabelMap.get(String(category?.label || "")) ||
            localized.label,
        };
      })
      .filter(Boolean),
    products: (data.homepageData?.products || [])
      .map(
        (product) =>
          localizedProductsByUniqcode[product?.uniqcode] ||
          localizeProductForLanguage(product, code)
      )
      .filter(Boolean),
  };

  return {
    ...data,
    selectedLanguageCode: code,
    menuItems: localizedCategories,
    categories: localizedCategories,
    productsByUniqcode: localizedProductsByUniqcode,
    products: Object.values(localizedProductsByUniqcode),
    categoryContent: localizedCategoryContent,
    homepageData,
    preferance: localizedPreferance,
    personnel: (data.personnel || [])
      .map((person) => localizePersonnelForLanguage(person, code))
      .filter(Boolean),
  };
};

const durationValueToMinutes = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "0") return 0;
  if (raw === "all_day") return Infinity;
  if (raw.endsWith("m")) return Number(raw.slice(0, -1)) || 0;
  if (raw.endsWith("h")) return (Number(raw.slice(0, -1)) || 0) * 60;
  return 0;
};

const durationValueToDays = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "0") return 0;
  if (raw === "1mo") return 30;
  if (raw.endsWith("d")) return Number(raw.slice(0, -1)) || 0;
  return 0;
};

const calculateReservationDuration = (product, selections = []) => {
  const isDay = !!product?.dayEnabled;
  let total = isDay
    ? durationValueToDays(product?.sharedTimeValue)
    : durationValueToMinutes(product?.sharedTimeValue);
  let allDay = total === Infinity;
  (Array.isArray(selections) ? selections : []).forEach((selection) => {
    if (!selection?.durationEnabled) return;
    const quantity = Math.max(1, Number(selection.quantity || 1));
    const value = isDay
      ? durationValueToDays(selection.durationValue)
      : durationValueToMinutes(selection.durationValue);
    if (value === Infinity) allDay = true;
    else total += value * quantity;
  });
  if (isDay) {
    return { mode: "day", durationDays: Math.max(1, Number(total || 0)), durationMinutes: 0, allDay: false };
  }
  return {
    mode: "time",
    durationDays: 0,
    durationMinutes: allDay ? 0 : Math.max(0, Number(total || 0)),
    allDay,
    rawDurationMinutes: Number.isFinite(total) ? total : 0,
  };
};

const reservationDurationForItem = (item) => {
  const duration = item?.reservationDuration || {};
  return {
    mode: duration.mode || (duration.durationDays ? "day" : "time"),
    minutes: Math.max(0, Number(duration.durationMinutes || 0)),
    days: Math.max(0, Number(duration.durationDays || 0)),
  };
};

const buildDurationOverview = (entries = [], languageCode = "tr") => {
  let totalMinutes = 0;
  let totalDays = 0;
  const parts = [];
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const item = Array.isArray(entry) ? entry[1] : entry;
    if (!item) return;
    const duration = reservationDurationForItem(item);
    const name = String(item.product || item.name || "").trim();
    if (duration.mode === "day" && duration.days > 0) {
      totalDays += duration.days;
      parts.push(`${name} ${formatDurationValue(`${duration.days}d`, languageCode, { prefix: false, long: true })}`.trim());
      return;
    }
    if (duration.minutes > 0) {
      totalMinutes += duration.minutes;
      parts.push(`${name} ${formatDurationMinutes(duration.minutes, languageCode, { prefix: false })}`.trim());
    }
  });
  return { totalMinutes, totalDays, parts };
};

const shouldIncludeMainProductPrice = (product) =>
  normalizeBooleanFlag(product?.addMainProductToReceipt);

const computeSelectionsAndTotal = (product, selectedOptions) => {
  const selections = [];
  const productPrice = getProductPriceInfo(product);
  // Ana ürün fişe eklenmiyorsa sepet/düzenleme toplamına da ana ürün fiyatını katma.
  // Bu durumda müşteri yalnızca seçtiği opsiyonların toplamını görür ve öder.
  let total = shouldIncludeMainProductPrice(product) ? productPrice.effective : 0;

  (product?.questions || []).forEach((question) => {
    const questionSelected = selectedOptions?.[question.id];
    if (!questionSelected) return;

    (question.options || []).forEach((option) => {
      const optionKey = option.__optionKey || String(option.id);
      const quantity = Number(questionSelected?.[optionKey] || 0);
      if (quantity <= 0) return;

      const optionPrice = getOptionPriceInfo(option);
      const unitPrice = optionPrice.effective;
      const originalUnitPrice = optionPrice.original;
      const totalPrice = roundMoney(quantity * unitPrice);
      const originalTotalPrice = roundMoney(quantity * originalUnitPrice);
      total = roundMoney(total + totalPrice);

      selections.push({
        name: option.name,
        quantity,
        unitPrice,
        originalUnitPrice,
        totalPrice,
        originalTotalPrice,
        discountEnabled: !!option.discountEnabled,
        discountRate: optionPrice.rate,
        discountAmount: roundMoney(originalTotalPrice - totalPrice),
        question: question.text,
        hasQuantity: !!option.hasQuantity,
        optionId: option.id,
        optionKey,
        questionId: question.id,
        durationEnabled: !!option.durationEnabled,
        durationVisible: option.durationVisible !== false,
        durationValue: option.durationValue || "",
        kdvoption: Number(option.kdvoption || 0),
        departmanno: normalizeDepartmentNoForPayment(
          option.departmanno ?? option.departmentNo ?? option.departmentno ?? ""
        ),
      });
    });
  });

  return {
    selections,
    totalPrice: total,
  };
};

const screenStyle = (active, warm = false) => {
  if (active) {
    return {
      position: "relative",
      width: "100%",
      minHeight: "100vh",
      zIndex: 2,
      opacity: 1,
      visibility: "visible",
      pointerEvents: "auto",
      transform: "translate3d(0,0,0)",
    };
  }

  if (warm) {
    return {
      position: "fixed",
      inset: 0,
      width: "100vw",
      height: "100vh",
      minHeight: "100vh",
      // Aktif ekran zIndex 2. Warm ekran arkada ama display/visibility kapalı değil.
      // Böylece browser layout + image decode işini önceden yapabiliyor.
      zIndex: 0,
      opacity: 0.001,
      visibility: "visible",
      pointerEvents: "none",
      overflow: "hidden",
      transform: "translate3d(0,0,0)",
      WebkitTransform: "translate3d(0,0,0)",
      willChange: "opacity, transform",
      contentVisibility: "visible",
      contain: "paint",
    };
  }

  return {
    display: "none",
  };
};

const buildPaymentStatus = (paymentMethod, preferance) => {
  const method = String(paymentMethod || "").trim().toLowerCase();

  // Kartta ödeme henüz POS tarafından onaylanmadıysa awaiting kalır.
  // POS ödeme başarılı döndüğünde server status'u pending yapar.
  if (method === "card") return "awaiting";

  // Nakit ve yemek kartı benzeri ödemelerde preapprove=true ise panel onayı beklediği için pending,
  // preapprove=false ise kasada/operasyonda ödeme bekleyen sipariş olarak awaiting kalır.
  const mealLikeOrCash = ["cash", "meal", "multinet", "sodexo", "setcard", "metropol", "edenred"];
  if (mealLikeOrCash.includes(method)) {
    return preferance?.preapprove ? "pending" : "awaiting";
  }

  return "awaiting";
};

const getPaymentOptions = (preferance, ui) => {
  const options = [];

  // Geriye dönük uyumluluk: cash alanı hiç yoksa Nakit görünmeye devam eder.
  if (preferance?.cash !== false) {
    options.push({
      key: "cash",
      title: ui.cash,
      subtitle: ui.cashSubtitle,
      imageUrl: CASH_IMG,
    });
  }

  // Banka/kredi kartı mevcut sistemde her zaman açıktır.
  options.push({
    key: "card",
    title: ui.card,
    subtitle: ui.cardSubtitle,
    imageUrl: CARD_IMG,
  });

  if (preferance?.multinet) {
    options.push({
      key: "multinet",
      title: "Multinet",
      subtitle: `Multinet ${ui.payWith}`,
      imageUrl: MULTINET_IMG,
    });
  }

  if (preferance?.sodexo) {
    options.push({
      key: "sodexo",
      title: "Sodexo",
      subtitle: `Sodexo ${ui.payWith}`,
      imageUrl: SODEXO_IMG,
    });
  }

  if (preferance?.setcard) {
    options.push({
      key: "setcard",
      title: "Setcard",
      subtitle: `Setcard ${ui.payWith}`,
      imageUrl: SETCARD_IMG,
    });
  }

  if (preferance?.metropol) {
    options.push({
      key: "metropol",
      title: "Metropol",
      subtitle: `Metropol ${ui.payWith}`,
      imageUrl: METROPOL_IMG,
    });
  }

  if (preferance?.edenred) {
    options.push({
      key: "edenred",
      title: "Edenred",
      subtitle: `Edenred ${ui.payWith}`,
      imageUrl: EDENRED_IMG,
    });
  }

  return options;
};

const KeepAliveScreen = React.memo(function KeepAliveScreen({
  active,
  warm,
  children,
}) {
  if (!active && !warm) return null;

  return (
    <div style={screenStyle(active, warm)} aria-hidden={!active}>
      {children}
    </div>
  );
});

const LoadingSpinner = React.memo(function LoadingSpinner({
  message = "YÜKLENİYOR...",
}) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center text-black">
        <div className="w-96 h-96 border-16 border-black border-t-transparent rounded-full animate-spin mx-auto mb-16"></div>
        <p className="text-5xl font-bold mb-4">{message}</p>
      </div>
    </div>
  );
});

const ConfirmModal = React.memo(function ConfirmModal({
  isOpen,
  title,
  text,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-10 text-center">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">{title}</h2>
        <p className="text-xl text-gray-600 mb-10 leading-relaxed">{text}</p>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={onCancel}
            className="py-5 rounded-2xl bg-gray-100 text-gray-800 font-bold text-xl border border-gray-300"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="py-5 rounded-2xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold text-xl"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
});

const InactivityModal = React.memo(function InactivityModal({
  isOpen,
  secondsLeft,
  onContinue,
  paymentMode = false,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-12 text-center border border-gray-200">
        <div className="w-32 h-32 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-8">
          <svg className="w-16 h-16 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.99-1.742 2.99H3.48c-1.53 0-2.492-1.656-1.743-2.99l6.52-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.993.883L9 7v4a1 1 0 001.993.117L11 11V7a1 1 0 00-1-1z" />
          </svg>
        </div>

        <h2 className="text-4xl font-bold text-gray-900 mb-4">{ui.continueSessionTitle}</h2>
        <p className="text-xl text-gray-600 mb-8">
          {paymentMode
            ? ui.continueSessionPaymentText
            : ui.continueSessionText}
        </p>
        <div className="text-6xl font-extrabold text-amber-600 mb-8">{secondsLeft}</div>

        <button
          type="button"
          onClick={onContinue}
          className="px-12 py-5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-2xl"
        >
          {ui.yesContinue}
        </button>
      </div>
    </div>
  );
});

const AddedProductModal = React.memo(function AddedProductModal({
  isOpen,
  productInfo,
  onClose,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(onClose, 6500);
    return () => clearTimeout(t);
  }, [isOpen, onClose]);

  if (!isOpen || !productInfo) return null;

  return (
    <div className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-8 text-center">
        {productInfo.image ? (
          <div className="w-48 h-48 mx-auto mb-6 rounded-2xl overflow-hidden border-4 border-emerald-200 shadow-lg">
            <img
              src={productInfo.image}
              alt={productInfo.product}
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.currentTarget.src = `${process.env.PUBLIC_URL}/coffee.png`;
              }}
            />
          </div>
        ) : null}

        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-5xl">✅</span>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-3">{ui.reservationCreated}</h2>
        <p className="text-lg text-gray-600 mb-6">{ui.reservationCreatedText}</p>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-left space-y-3">
          <p className="text-xl font-bold text-gray-900"><span className="text-emerald-700">{ui.service}:</span> {productInfo.product}</p>
          {productInfo.selectedPersonnel && (
            <p className="text-xl font-bold text-gray-900"><span className="text-emerald-700">{ui.personnel}:</span> {productInfo.selectedPersonnel.firstName || ui.selected}</p>
          )}
          {productInfo.reservationDraft?.mode === "time" && (
            <p className="text-xl font-bold text-gray-900"><span className="text-emerald-700">{ui.time}:</span> {new Date(productInfo.reservationDraft.startAt).toLocaleString(undefined, { timeZone: "Europe/Istanbul", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} – {new Date(productInfo.reservationDraft.endAt).toLocaleTimeString(undefined, { timeZone: "Europe/Istanbul", hour: "2-digit", minute: "2-digit" })}</p>
          )}
          {productInfo.reservationDraft?.mode === "day" && (
            <p className="text-xl font-bold text-gray-900"><span className="text-emerald-700">{ui.days}:</span> {productInfo.reservationDraft.startDate} – {productInfo.reservationDraft.endDate}</p>
          )}
          {productInfo.reservationDuration && (
            <p className="text-xl font-bold text-gray-900"><span className="text-emerald-700">{ui.serviceDuration}:</span> {productInfo.reservationDuration.mode === "day" ? `${productInfo.reservationDuration.durationDays} ${ui.day}` : productInfo.reservationDuration.allDay ? ui.allDay : `${productInfo.reservationDuration.durationMinutes} ${ui.minute}`}</p>
          )}
          <p className="text-xl font-bold text-gray-900"><span className="text-emerald-700">{ui.total}:</span> {formatTRY(productInfo.totalPrice)}</p>
        </div>
        <button type="button" onClick={onClose} className="mt-6 w-full rounded-2xl bg-emerald-600 py-4 text-xl font-extrabold text-white">{ui.close}</button>
      </div>
    </div>
  );
});

const AutoFitText = React.memo(function AutoFitText({
  text,
  className = "",
  maxFontSize = 18,
  minFontSize = 10,
  lineHeight = 1.15,
  maxLines = 2,
}) {
  const ref = useRef(null);
  const value = String(text || "").trim();
  const [fontSize, setFontSize] = useState(maxFontSize);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    let cancelled = false;
    let rafId = 0;

    const fitText = () => {
      if (!el || cancelled) return;

      let nextSize = maxFontSize;
      el.style.fontSize = `${nextSize}px`;
      el.style.lineHeight = String(lineHeight);

      while (
        nextSize > minFontSize &&
        (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1)
      ) {
        nextSize -= 1;
        el.style.fontSize = `${nextSize}px`;
      }

      if (!cancelled) setFontSize(nextSize);
    };

    setFontSize(maxFontSize);
    rafId = window.requestAnimationFrame(fitText);

    return () => {
      cancelled = true;
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [value, maxFontSize, minFontSize, lineHeight, maxLines]);

  return (
    <span
      ref={ref}
      title={value}
      className={className}
      style={{
        display: "block",
        width: "100%",
        height: `${Math.ceil(maxFontSize * lineHeight * maxLines)}px`,
        maxHeight: `${Math.ceil(maxFontSize * lineHeight * maxLines)}px`,
        overflow: "hidden",
        fontSize: `${fontSize}px`,
        lineHeight,
        whiteSpace: maxLines === 1 ? "nowrap" : "normal",
        wordBreak: "normal",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </span>
  );
});

const ProductCard = React.memo(function ProductCard({ product, onClick, languageCode = "tr", addedCount = 0 }) {
  const ui = getUiText(languageCode);
  const productName = product.name || "Ürün";
  const productDescription = product.desc || product.description || "Ürün açıklaması";
  const showOffersInsteadOfPrice = !!product?.showOffersInsteadOfPrice;
  const productPrice = getProductPriceInfo(product);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 text-left w-full max-w-[300px] mx-auto"
    >
      <div className="relative aspect-video overflow-hidden bg-gray-100">
        <img
          src={product.image || `${process.env.PUBLIC_URL}/coffee.png`}
          alt={productName}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onError={(e) => {
            e.currentTarget.src = `${process.env.PUBLIC_URL}/coffee.png`;
          }}
        />
        {addedCount > 0 && (
          <div className="absolute left-2 top-2 z-10 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-black text-white shadow-lg">
            {getAddedCountText(addedCount, languageCode)}
          </div>
        )}
        <div className={`absolute top-2 right-2 bg-white/95 text-gray-900 rounded-2xl shadow font-bold whitespace-nowrap text-center px-3 py-2 ${showOffersInsteadOfPrice ? "max-w-[145px] text-sm" : "max-w-[135px] text-sm"}`}>
          {showOffersInsteadOfPrice ? (
            ui.viewOffers
          ) : product.discountEnabled && productPrice.rate > 0 ? (
            <div className="leading-tight">
              <div className="text-[10px] text-gray-400 line-through">{formatTRY(productPrice.original)}</div>
              <div className="text-rose-600">{formatTRY(productPrice.effective)} · %{productPrice.rate}</div>
            </div>
          ) : (
            formatTRY(productPrice.effective)
          )}
        </div>
      </div>
      <div className="px-3 py-2 h-[72px] flex flex-col justify-start overflow-hidden">
        <h3
          title={productName}
          className="text-xs leading-tight line-clamp-2 min-h-[28px] font-bold text-gray-900 mb-0.5 group-hover:text-emerald-600 transition-colors break-words"
        >
          {productName}
        </h3>
        <p
          title={productDescription}
          className="text-[10px] leading-tight line-clamp-2 text-gray-600 break-words"
        >
          {productDescription}
        </p>
      </div>
    </button>
  );
});


const LATIN_NAME_KEYBOARD_ROWS = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const normalizeCustomerName = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^\s+/, "")
    .slice(0, 80);

const CustomerNameScreen = React.memo(function CustomerNameScreen({
  value,
  onChange,
  onBack,
  onContinue,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  const languageMeta = getUiLanguageMeta(languageCode);
  const cleanValue = normalizeCustomerName(value);
  const canContinue = cleanValue.trim().length > 0;

  const append = useCallback((key) => {
    const current = normalizeCustomerName(value);
    if (current.length >= 80) return;
    if (key === " ") {
      if (!current.trim() || current.endsWith(" ")) return;
      onChange?.(`${current} `);
      return;
    }
    onChange?.(normalizeCustomerName(`${current}${key}`));
  }, [onChange, value]);

  const removeLast = useCallback(() => {
    onChange?.(normalizeCustomerName(cleanValue.slice(0, -1)));
  }, [cleanValue, onChange]);

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-slate-100 via-white to-blue-100" dir={languageMeta.dir || "ltr"}>
      <header className="h-[126px] border-b bg-white/95 px-8 py-5 shadow-sm backdrop-blur flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="rounded-2xl bg-gray-200 px-8 py-4 text-xl font-extrabold text-gray-900 shadow"
        >
          ← {ui.back}
        </button>
        <div className="text-center">
          <h1 className="text-5xl font-black text-gray-950">{ui.customerNameTitle}</h1>
          <p className="mt-2 text-xl font-semibold text-gray-600">{ui.customerNameSubtitle}</p>
        </div>
        <div className="w-32" />
      </header>

      <main className="h-[calc(100vh-126px)] overflow-hidden px-10 py-8 flex items-center justify-center">
        <div className="w-full max-w-6xl rounded-[36px] border border-gray-200 bg-white p-10 shadow-2xl">
          <div className="flex items-stretch gap-5" dir="ltr">
            <div className="flex-1 rounded-3xl border-4 border-blue-300 bg-blue-50 px-7 py-5 shadow-inner">
              <label className="mb-2 block text-left text-lg font-extrabold text-blue-950">
                {ui.customerNameLabel}
              </label>
              <input
                type="text"
                readOnly
                value={cleanValue}
                placeholder={ui.customerNamePlaceholder}
                aria-label={ui.customerNameLabel}
                className="h-20 w-full bg-transparent text-left text-4xl font-black tracking-wide text-gray-950 outline-none placeholder:text-gray-400"
              />
            </div>
            <button
              type="button"
              disabled={!canContinue}
              onClick={() => canContinue && onContinue?.(cleanValue.trim())}
              className={`w-64 rounded-3xl px-8 text-3xl font-black text-white shadow-xl transition ${
                canContinue
                  ? "bg-gradient-to-r from-emerald-500 to-teal-600 active:scale-95"
                  : "cursor-not-allowed bg-gray-300 text-gray-500"
              }`}
            >
              {ui.customerNameContinue}
            </button>
          </div>

          <p className={`mt-3 text-center text-base font-bold ${canContinue ? "text-emerald-700" : "text-red-600"}`}>
            {canContinue ? ui.customerNameReady : ui.customerNameRequired}
          </p>

          <div className="mt-7 space-y-3" dir="ltr">
            {LATIN_NAME_KEYBOARD_ROWS.map((row, rowIndex) => (
              <div key={rowIndex} className="flex justify-center gap-3">
                {row.map((key) => (
                  <button
                    type="button"
                    key={key}
                    onClick={() => append(key)}
                    className="h-16 min-w-[76px] rounded-2xl border-2 border-slate-300 bg-white px-5 text-2xl font-black text-gray-950 shadow-md active:scale-95 active:bg-blue-100"
                  >
                    {key}
                  </button>
                ))}
              </div>
            ))}

            <div className="flex justify-center gap-3 pt-1">
              <button
                type="button"
                onClick={() => onChange?.("")}
                className="h-16 min-w-[180px] rounded-2xl bg-red-100 px-6 text-xl font-black text-red-800 shadow-md active:scale-95"
              >
                {ui.keyboardClear}
              </button>
              <button
                type="button"
                onClick={() => append(" ")}
                className="h-16 min-w-[420px] rounded-2xl border-2 border-slate-300 bg-white px-8 text-xl font-black text-gray-900 shadow-md active:scale-95 active:bg-blue-100"
              >
                {ui.keyboardSpace}
              </button>
              <button
                type="button"
                onClick={removeLast}
                className="h-16 min-w-[180px] rounded-2xl bg-amber-100 px-6 text-xl font-black text-amber-900 shadow-md active:scale-95"
              >
                ⌫ {ui.keyboardDelete}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
});

const HomeScreen = React.memo(function HomeScreen({
  designData,
  onOrderNow,
  bootstrapReady,
  preparingText = "Menü hazırlanıyor...",
  languages = ["tr", "en", "ru", "de", "ar"],
  selectedLanguageCode = "tr",
  onLanguageSelect,
  videoCacheStats = null,
  videoCacheError = "",
  onRetryVideoCache,
}) {
  const [bgError, setBgError] = useState(false);
  const ui = getUiText(selectedLanguageCode);
  const shownLanguages = uniqueLanguageCodes(languages).slice(0, 5);
  const videoTotal = Math.max(0, Number(videoCacheStats?.total || 0));
  const videoCompleted = Math.max(0, Math.min(videoTotal, Number(videoCacheStats?.completed || 0)));
  const videoPercent = videoTotal > 0 ? Math.round((videoCompleted / videoTotal) * 100) : 0;
  const showVideoProgress = !!videoCacheStats?.required &&
    (videoCacheStats?.phase !== "complete" || !!videoCacheError);

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      {designData?.appvideo && !bgError ? (
        <img
          src={designData.appvideo}
          alt="Arka plan"
          className="absolute inset-0 w-full h-full object-cover -z-10"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onError={() => setBgError(true)}
        />
      ) : (
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-black via-gray-900 to-black" />
      )}

      <div className="absolute inset-0 -z-10 bg-black/35" />

      <div className="absolute top-[70px] left-1/2 transform -translate-x-1/2 text-center">
        <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-5 mb-4 border border-black/30">
          <img
            src={
              designData?.applogo ||
              `${process.env.PUBLIC_URL}/cafe-de-colombia-logo-png-transparent.png`
            }
            alt="Uygulama logosu"
            className="h-36 w-auto mx-auto"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            onError={(e) => {
              e.currentTarget.src = `${process.env.PUBLIC_URL}/cafe-de-colombia-logo-png-transparent.png`;
            }}
          />
        </div>
      </div>

      <div className="absolute top-[135px] left-1/2 transform -translate-x-1/2 text-center">
        <h1 className="text-white/90 whitespace-nowrap text-[88px] mt-40 font-bold mb-4 drop-shadow-2xl tracking-tight">
          {designData?.appyazi || "Cafe de Colombia"}
        </h1>
        <p className="text-white/80 text-4xl font-light italic drop-shadow-lg">
          {designData?.appaltyazi || "Premium Deneyim"}
        </p>
      </div>

      <div className="absolute top-[46%] left-1/2 transform -translate-x-1/2 text-center">
        <button
          type="button"
          onClick={() => onOrderNow?.(selectedLanguageCode)}
          disabled={!bootstrapReady}
          className={bootstrapReady ? "cursor-pointer" : "cursor-not-allowed opacity-80"}
        >
          <div
            className={`px-32 py-12 bg-gradient-to-r ${getColorClass(
              designData?.butonrenk
            )} text-white text-6xl font-extrabold rounded-[40px] shadow-2xl border-4 border-amber-400/40 min-w-[560px] text-center flex items-center justify-center gap-6`}
          >
            <span className="block">{ui.reserve}</span>
          </div>
        </button>

        <div className="mt-8 flex items-stretch justify-center gap-4">
          {shownLanguages.map((code) => {
            const meta = getLanguageMeta(code);
            const active = normalizeLanguageCode(selectedLanguageCode) === meta.code;
            return (
              <button
                type="button"
                key={meta.code}
                onClick={() => onLanguageSelect?.(meta.code)}
                disabled={!bootstrapReady}
                dir={meta.dir || "ltr"}
                className={`min-w-[132px] px-4 py-4 rounded-2xl border-2 backdrop-blur-md transition-all ${
                  active
                    ? "bg-white text-gray-950 border-amber-400 shadow-2xl scale-105"
                    : "bg-black/35 text-white border-white/30 hover:bg-white/20"
                }`}
              >
                <img src={meta.flagImage} alt="" className="mx-auto mb-2 h-12 w-20 rounded-lg object-cover shadow-md ring-1 ring-black/10" />
                <span className="block text-lg font-extrabold whitespace-nowrap">{meta.label}</span>
              </button>
            );
          })}
        </div>

        {!bootstrapReady && (
          <div className="mt-5 min-w-[560px] rounded-3xl border border-white/25 bg-black/55 px-7 py-5 text-white shadow-2xl backdrop-blur-md">
            <div className="text-xl font-extrabold">{preparingText}</div>

            {showVideoProgress && videoTotal > 0 && (
              <>
                <div className="mt-4 h-5 overflow-hidden rounded-full bg-white/20 ring-1 ring-white/20">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-[width] duration-300"
                    style={{ width: `${videoPercent}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between text-base font-bold text-white/90">
                  <span>{videoCompleted}/{videoTotal}</span>
                  <span>%{videoPercent}</span>
                </div>
              </>
            )}

            {videoCacheError && (
              <div className="mt-4">
                <p className="text-base font-semibold text-red-200">
                  {videoCacheError === "CACHE_BRIDGE_MISSING"
                    ? ui.videoCacheUnavailable
                    : ui.videoCacheFailed}
                </p>
                {videoCacheError !== "CACHE_BRIDGE_MISSING" && (
                  <button
                    type="button"
                    onClick={() => onRetryVideoCache?.()}
                    className="mt-3 rounded-2xl bg-white px-6 py-3 text-lg font-extrabold text-gray-950 shadow-lg active:scale-95"
                  >
                    {ui.retryVideos}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="absolute bottom-16 left-4 right-4 bg-black/50 backdrop-blur-xl rounded-[32px] p-8 border border-white/40">
        <div className="flex items-center text-white/80">
          <div className="flex-1 flex justify-start items-center">
            <div className="w-5 h-5 bg-green-400 rounded-full"></div>
          </div>
          <div className="flex-1 text-center text-3xl">
            <span className="font-bold">{designData?.openhour || "08:00 - 22:00"}</span>
          </div>
          <div className="flex-1 text-right text-3xl">
            <span className="font-bold">{designData?.addinfo || "Rezervasyon"}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

const MenuScreen = React.memo(function MenuScreen({
  bootstrapData,
  selectedCategoryId,
  setSelectedCategoryId,
  onProductClick,
  onProceed,
  onCancelAll,
  cartCount,
  cartTotal,
  productAddedCounts,
  resetToken,
  onWarmReady,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  const BOTTOM_BAR_HEIGHT_PX = 190;
  const TITLE_SAFE_INSET_PX = 300;
  const SPY_LINE_DOWN_PX = TITLE_SAFE_INSET_PX + 80;
  const SPY_LINE_UP_PX = TITLE_SAFE_INSET_PX + 80;
  const EXTRA_BOTTOM_SPACE_PX = 900;

  const scrollContainerRef = useRef(null);
  const sectionRefs = useRef({});
  const activeCategoryRef = useRef(selectedCategoryId);
  const forcedActiveRef = useRef(null);
  const forcedActiveUntilRef = useRef(0);
  const forcedTimerRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const scrollDirRef = useRef("down");
  const scrollSpyRafRef = useRef(null);
  const scrollAnimRafRef = useRef(null);
  const pinScrollRafRef = useRef(null);
  const scrollLockUntilRef = useRef(0);
  const isScrollingProgrammatically = useRef(false);
  const warmReadySentRef = useRef(false);

  const menuItems = useMemo(() => {
    return [
      { id: "ana-sayfa", label: ui.home, icon: "🏠" },
      ...(bootstrapData?.menuItems || []),
    ];
  }, [bootstrapData, ui.home]);

  const homepageData = bootstrapData?.homepageData || DEFAULT_BOOTSTRAP.homepageData;
  const designData = bootstrapData?.designData || DEFAULT_BOOTSTRAP.designData;

  useEffect(() => {
    activeCategoryRef.current = selectedCategoryId;
  }, [selectedCategoryId]);

  const mainPaneHeightStyle = useMemo(() => {
    return { height: `calc(100vh - 14rem - ${BOTTOM_BAR_HEIGHT_PX}px)` };
  }, []);

  const contentPaddingBottomPx = useMemo(() => {
    return Math.max(120, BOTTOM_BAR_HEIGHT_PX + 80 + EXTRA_BOTTOM_SPACE_PX);
  }, []);

  const forceActiveCategory = useCallback((id, ms) => {
    forcedActiveRef.current = id;
    forcedActiveUntilRef.current = performance.now() + ms;

    if (forcedTimerRef.current) clearTimeout(forcedTimerRef.current);
    forcedTimerRef.current = setTimeout(() => {
      forcedActiveRef.current = null;
      forcedActiveUntilRef.current = 0;
    }, ms);
  }, []);

  useEffect(() => {
    return () => {
      if (forcedTimerRef.current) clearTimeout(forcedTimerRef.current);
    };
  }, []);

  const getSectionEl = useCallback((categoryId) => {
    if (!categoryId) return null;
    const fromRef = sectionRefs.current[categoryId];
    if (fromRef) return fromRef;

    const container = scrollContainerRef.current;
    if (!container) return null;

    const found = container.querySelector(`[data-category-id="${categoryId}"]`);
    if (found) {
      sectionRefs.current[categoryId] = found;
      return found;
    }
    return null;
  }, []);

  const getSectionTopInContainer = useCallback((el) => {
    const container = scrollContainerRef.current;
    if (!container || !el) return 0;

    if (el.offsetParent === container) return el.offsetTop;

    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    return container.scrollTop + (eRect.top - cRect.top);
  }, []);

  const clampScrollTop = useCallback((container, value) => {
    const max = Math.max(0, container.scrollHeight - container.clientHeight);
    if (!Number.isFinite(value)) return 0;
    return Math.round(Math.max(0, Math.min(max, value)));
  }, []);

  const getScrollTopForCategory = useCallback(
    (categoryId) => {
      const container = scrollContainerRef.current;
      if (!container) return 0;
      if (categoryId === "ana-sayfa") return 0;

      const el = getSectionEl(categoryId);
      if (!el) return Math.round(container.scrollTop);

      const sectionTop = getSectionTopInContainer(el);
      const target = sectionTop - TITLE_SAFE_INSET_PX;

      return clampScrollTop(container, target);
    },
    [getSectionEl, getSectionTopInContainer, clampScrollTop]
  );

  const computeActiveByTopLine = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return "ana-sayfa";

    const scrollTop = container.scrollTop;
    const linePx = scrollDirRef.current === "up" ? SPY_LINE_UP_PX : SPY_LINE_DOWN_PX;
    const activation = scrollTop + linePx;

    let active = "ana-sayfa";

    for (let i = 0; i < menuItems.length; i++) {
      const id = menuItems[i].id;
      const el = sectionRefs.current[id];
      if (!el) continue;

      const sectionTop = getSectionTopInContainer(el);

      if (sectionTop > activation) {
        active = i === 0 ? "ana-sayfa" : menuItems[i - 1].id;
        break;
      }
      if (i === menuItems.length - 1) active = id;
    }

    return active;
  }, [menuItems, getSectionTopInContainer]);

  const scheduleScrollSpyUpdate = useCallback(() => {
    const now = performance.now();
    if (isScrollingProgrammatically.current) return;
    if (scrollLockUntilRef.current && now < scrollLockUntilRef.current) return;
    if (forcedActiveRef.current && now < forcedActiveUntilRef.current) return;

    if (scrollSpyRafRef.current) cancelAnimationFrame(scrollSpyRafRef.current);

    scrollSpyRafRef.current = requestAnimationFrame(() => {
      scrollSpyRafRef.current = null;
      const next = computeActiveByTopLine();

      if (next && next !== activeCategoryRef.current) {
        setSelectedCategoryId(next);
        activeCategoryRef.current = next;
      }
    });
  }, [computeActiveByTopLine, setSelectedCategoryId]);

  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const animateScrollTo = useCallback((container, to, durationMs, onDone) => {
    if (scrollAnimRafRef.current) {
      cancelAnimationFrame(scrollAnimRafRef.current);
      scrollAnimRafRef.current = null;
    }

    const finalTo = clampScrollTop(container, to);
    const from = Math.round(container.scrollTop);
    const change = finalTo - from;

    if (Math.abs(change) < 1 || durationMs <= 0) {
      container.scrollTop = finalTo;
      onDone?.(finalTo);
      return;
    }

    const start = performance.now();

    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(t);
      container.scrollTop = Math.round(from + change * eased);

      if (t < 1) {
        scrollAnimRafRef.current = requestAnimationFrame(step);
      } else {
        scrollAnimRafRef.current = null;
        container.scrollTop = finalTo;
        onDone?.(finalTo);
      }
    };

    scrollAnimRafRef.current = requestAnimationFrame(step);
  }, [clampScrollTop]);

  const stopPinnedScroll = useCallback(() => {
    if (pinScrollRafRef.current) {
      cancelAnimationFrame(pinScrollRafRef.current);
      pinScrollRafRef.current = null;
    }
  }, []);

  const pinScrollTop = useCallback(
    (targetTop, durationMs = 550) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      stopPinnedScroll();

      const stableTarget = clampScrollTop(container, targetTop);
      const until = performance.now() + durationMs;
      scrollLockUntilRef.current = Math.max(scrollLockUntilRef.current || 0, until);

      const step = () => {
        const now = performance.now();
        if (now >= until) {
          container.scrollTop = stableTarget;
          pinScrollRafRef.current = null;
          return;
        }

        if (Math.abs(Math.round(container.scrollTop) - stableTarget) <= 140) {
          container.scrollTop = stableTarget;
        }

        pinScrollRafRef.current = requestAnimationFrame(step);
      };

      pinScrollRafRef.current = requestAnimationFrame(step);
    },
    [clampScrollTop, stopPinnedScroll]
  );

  const scrollToCategory = useCallback(
    (categoryId, mode = "slide") => {
      const container = scrollContainerRef.current;
      if (!container) return;

      stopPinnedScroll();

      const tryStart = (triesLeft) => {
        if (categoryId !== "ana-sayfa") {
          const el = getSectionEl(categoryId);
          if (!el && triesLeft > 0) {
            requestAnimationFrame(() => tryStart(triesLeft - 1));
            return;
          }
        }

        forceActiveCategory(categoryId, 1200);
        isScrollingProgrammatically.current = true;

        const targetTopSnap = getScrollTopForCategory(categoryId);
        const dist = Math.abs(Math.round(container.scrollTop) - targetTopSnap);
        const duration =
          mode === "slide" ? Math.min(820, Math.max(260, Math.round(dist * 0.24))) : 0;

        scrollLockUntilRef.current = performance.now() + duration + 700;

        const finish = (stableTargetTop = targetTopSnap) => {
          container.scrollTop = stableTargetTop;
          activeCategoryRef.current = categoryId;
          setSelectedCategoryId(categoryId);

          pinScrollTop(stableTargetTop, 550);

          window.setTimeout(() => {
            isScrollingProgrammatically.current = false;
            scrollLockUntilRef.current = 0;
            scheduleScrollSpyUpdate();
          }, 560);
        };

        if (mode === "auto" || duration === 0) {
          finish(targetTopSnap);
          return;
        }

        animateScrollTo(container, targetTopSnap, duration, finish);
      };

      tryStart(60);
    },
    [
      animateScrollTo,
      forceActiveCategory,
      getScrollTopForCategory,
      getSectionEl,
      pinScrollTop,
      scheduleScrollSpyUpdate,
      setSelectedCategoryId,
      stopPinnedScroll,
    ]
  );

  const handleCategoryChange = useCallback(
    (categoryId) => {
      forceActiveCategory(categoryId, 1200);
      setSelectedCategoryId(categoryId);
      activeCategoryRef.current = categoryId;
      scrollToCategory(categoryId, "slide");
    },
    [forceActiveCategory, scrollToCategory, setSelectedCategoryId]
  );

  const handleHomeCategoryClick = useCallback(
    (label) => {
      const found = menuItems.find(
        (item) =>
          String(item.label || "").trim().toLowerCase() ===
          String(label || "").trim().toLowerCase()
      );
      if (found) {
        handleCategoryChange(found.id);
      }
    },
    [menuItems, handleCategoryChange]
  );

  const handleScroll = useCallback(() => {
    const c = scrollContainerRef.current;
    if (c) {
      const st = c.scrollTop;
      if (st > lastScrollTopRef.current) scrollDirRef.current = "down";
      else if (st < lastScrollTopRef.current) scrollDirRef.current = "up";
      lastScrollTopRef.current = st;
    }

    if (isScrollingProgrammatically.current) return;
    if (scrollLockUntilRef.current && performance.now() < scrollLockUntilRef.current) return;
    scheduleScrollSpyUpdate();
  }, [scheduleScrollSpyUpdate]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setSelectedCategoryId("ana-sayfa");
    activeCategoryRef.current = "ana-sayfa";
    forceActiveCategory("ana-sayfa", 1000);

    requestAnimationFrame(() => {
      container.scrollTop = 0;
      lastScrollTopRef.current = 0;
      isScrollingProgrammatically.current = false;
      scheduleScrollSpyUpdate();
    });
  }, [resetToken, setSelectedCategoryId, forceActiveCategory, scheduleScrollSpyUpdate]);

  useEffect(() => {
    if (!menuItems.length) return;
    scheduleScrollSpyUpdate();
  }, [menuItems.length, scheduleScrollSpyUpdate]);

  useEffect(() => {
    if (warmReadySentRef.current) return;
    if (!menuItems.length) return;

    let raf1 = 0;
    let raf2 = 0;
    let idleId = null;
    let cancelled = false;

    const finish = () => {
      if (cancelled) return;
      warmReadySentRef.current = true;
      onWarmReady?.();
    };

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        if (cancelled) return;
        if (typeof window.requestIdleCallback === "function") {
          idleId = window.requestIdleCallback(finish, { timeout: 400 });
        } else {
          idleId = window.setTimeout(finish, 0);
        }
      });
    });

    return () => {
      cancelled = true;
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (idleId) {
        if (typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
        else window.clearTimeout(idleId);
      }
    };
  }, [menuItems.length, onWarmReady]);

  useEffect(() => {
    return () => {
      if (scrollAnimRafRef.current) cancelAnimationFrame(scrollAnimRafRef.current);
      if (scrollSpyRafRef.current) cancelAnimationFrame(scrollSpyRafRef.current);
      if (pinScrollRafRef.current) cancelAnimationFrame(pinScrollRafRef.current);
    };
  }, []);

  const categoryButtons = useMemo(() => {
    const uiActiveCategory =
      forcedActiveRef.current && performance.now() < forcedActiveUntilRef.current
        ? forcedActiveRef.current
        : selectedCategoryId;

    return menuItems.map((item) => (
      <button
        key={item.id}
        onClick={() => handleCategoryChange(item.id)}
        className={`group w-full flex items-center space-x-4 px-5 py-4 rounded-xl font-semibold transition-all duration-300 ${
          uiActiveCategory === item.id
            ? `bg-gradient-to-r ${getColorClass(
                designData?.menukategorirenk || "emerald"
              )} text-white shadow-lg scale-105`
            : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:scale-102"
        }`}
      >
        <div
          className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
            uiActiveCategory === item.id ? "border-white/50" : "border-gray-200"
          }`}
        >
          {typeof item.icon === "string" &&
          (item.icon.startsWith("http") || item.icon.startsWith("data:image/")) ? (
            <img
              alt={item.label}
              className="w-full h-full object-cover"
              src={item.icon}
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl">
              {item.icon || "🏠"}
            </div>
          )}
        </div>

        <span className="text-lg flex-1 text-left font-medium">{item.label}</span>

        {uiActiveCategory === item.id && (
          <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
        )}
      </button>
    ));
  }, [designData?.menukategorirenk, handleCategoryChange, menuItems, selectedCategoryId]);

  const allSections = useMemo(() => {
    return menuItems.map((item, idx) => {
      const isHome = item.id === "ana-sayfa";
      const isLast = idx === menuItems.length - 1;
      const products = getProductsForCategory(bootstrapData, item);

      return (
        <section
          key={item.id}
          data-category-id={item.id}
          ref={(el) => {
            if (el) sectionRefs.current[item.id] = el;
          }}
          className={`mb-12 last:mb-0 ${isLast ? "mb-10" : ""}`}
          style={{ overflowAnchor: "none" }}
        >
          {!isHome && (
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center pt-2">
              {item.label}
            </h2>
          )}

          {isHome ? (
            <div>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{ui.featuredCategories}</h2>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {(homepageData.categories || []).map((category, index) => (
                    <button
                      key={category.id || index}
                      onClick={() => handleHomeCategoryClick(category.label)}
                      className="group relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 cursor-pointer border border-gray-200"
                    >
                      <div className="flex items-center p-4 bg-white">
                        <div className="w-20 h-20 flex items-center justify-center rounded-xl mr-4 overflow-hidden flex-shrink-0 border border-gray-200">
                          {category.image ? (
                            <img
                              alt={category.label}
                              className="w-full h-full object-cover"
                              src={category.image}
                              loading="eager"
                              decoding="async"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-4xl">
                              🍽️
                            </div>
                          )}
                        </div>

                        <span className="text-gray-900 text-2xl font-bold text-left flex-1">
                          {category.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {homepageData.reklamphoto ? (
                <div className="mb-8">
                  <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 overflow-hidden">
                    <img
                      alt="Reklam"
                      className="w-full h-48 object-cover"
                      src={homepageData.reklamphoto}
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                </div>
              ) : null}

              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-1">
                  {ui.featuredServices}
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {(homepageData.products || []).map((product, index) => (
                  <ProductCard
                    key={`${product.uniqcode || "home"}_${index}`}
                    product={product}
                    languageCode={languageCode}
                    addedCount={Math.max(0, Number(productAddedCounts?.get(String(product.uniqcode || "")) || 0))}
                    onClick={() => product.uniqcode && onProductClick(product.uniqcode)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              {products.map((product, index) => (
                <ProductCard
                  key={`${product.uniqcode || item.id}_${index}`}
                  product={product}
                  languageCode={languageCode}
                  addedCount={Math.max(0, Number(productAddedCounts?.get(String(product.uniqcode || "")) || 0))}
                  onClick={() => product.uniqcode && onProductClick(product.uniqcode)}
                />
              ))}
            </div>
          )}
        </section>
      );
    });
  }, [
    bootstrapData?.categoryContent,
    handleHomeCategoryClick,
    homepageData.baslikyazisi,
    homepageData.categories,
    homepageData.products,
    homepageData.reklamphoto,
    languageCode,
    menuItems,
    onProductClick,
    productAddedCounts,
    ui.featuredCategories,
    ui.featuredServices,
  ]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      <style>{`
        [data-menu-scroll-pane],
        [data-menu-scroll-pane] * {
          overflow-anchor: none !important;
        }

        [data-menu-scroll-pane] {
          scroll-behavior: auto !important;
          overscroll-behavior: contain;
        }
      `}</style>
      <div className="relative h-56 overflow-hidden">
        <div
          className={`absolute inset-0 bg-gradient-to-r ${getColorClass(
            designData?.menupagephotorenk || "emerald"
          )}`}
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative h-full flex flex-col items-center justify-center text-white px-6">
          <h1 className="text-5xl font-bold mb-2">
            {designData?.menupageyazi || "CAFE COLOMBIA"}
          </h1>
        </div>
      </div>

      <div className="flex">
        <div
          className="w-80 bg-white/80 backdrop-blur-sm sticky top-0 border-r border-gray-200 shadow-lg overflow-hidden"
          style={mainPaneHeightStyle}
        >
          <div className="p-6 space-y-4 overflow-y-auto h-full">{categoryButtons}</div>
        </div>

        <div
          ref={scrollContainerRef}
          data-menu-scroll-pane="true"
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto relative"
          style={{
            ...mainPaneHeightStyle,
            overflowAnchor: "none",
            scrollBehavior: "auto",
          }}
        >
          <div
            className="px-8 py-8"
            style={{ paddingBottom: `${contentPaddingBottomPx}px`, overflowAnchor: "none" }}
          >
            {allSections}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-2xl z-30">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={onCancelAll}
              className="px-14 py-6 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl font-bold text-2xl shadow-xl hover:scale-105 transition-all duration-300"
            >
              {ui.cancelOrder}
            </button>

            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl px-8 py-4 border-2 border-emerald-200 shadow-lg">
                <svg className="w-10 h-10 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-700">
                    {cartCount > 0 ? formatTRY(cartTotal) : ui.empty}
                  </div>
                  <div className="text-lg text-emerald-600 font-medium">
                    {cartCount > 0 ? `${cartCount} ${ui.serviceCount}` : ui.cart}
                  </div>
                </div>
              </div>

              {cartCount > 0 && (
                <button
                  onClick={onProceed}
                  className={`px-14 py-6 bg-gradient-to-r ${getColorClass(
                    designData?.menukategorirenk || "emerald"
                  )} text-white rounded-2xl font-bold text-2xl shadow-xl hover:scale-105 transition-all duration-300`}
                >
                  {ui.completeReservation} →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const DesignScreen = React.memo(function DesignScreen({
  product,
  selectedOptions,
  setSelectedOptions,
  questionIndex,
  setQuestionIndex,
  isEditing,
  onCancel,
  onSave,
  cartEntries = [],
  editingOrderKey = "",
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  const [showRequiredError, setShowRequiredError] = useState(false);

  const currentQuestion = product?.questions?.[questionIndex] || null;

  const { totalPrice, selections } = useMemo(() => {
    if (!product) return { totalPrice: 0, selections: [] };
    return computeSelectionsAndTotal(product, selectedOptions);
  }, [product, selectedOptions]);

  const durationOverview = useMemo(() => {
    if (!product) return { totalMinutes: 0, totalDays: 0, parts: [] };
    const currentDuration = calculateReservationDuration(product, selections);
    const others = (Array.isArray(cartEntries) ? cartEntries : []).filter(([key]) => String(key) !== String(editingOrderKey || ""));
    const current = ["__current__", { product: product.name, reservationDuration: currentDuration }];
    return buildDurationOverview([...others, current], languageCode);
  }, [product, selections, cartEntries, editingOrderKey, languageCode]);

  const isRequired = !!currentQuestion?.isRequired;
  const isAnswered =
    !!currentQuestion &&
    !!selectedOptions?.[currentQuestion.id] &&
    Object.keys(selectedOptions[currentQuestion.id] || {}).length > 0;

  const progress =
    product?.questions?.length > 0
      ? ((questionIndex + 1) / product.questions.length) * 100
      : 0;

  const handleOptionSelect = useCallback(
    (question, option) => {
      const optionKey = option.__optionKey || String(option.id);

      setSelectedOptions((prev) => {
        const next = { ...prev };
        const qid = question.id;

        if (question.type === "single") {
          const currentSelectedKey = next[qid] ? Object.keys(next[qid])[0] : null;

          if (currentSelectedKey === optionKey && !option.hasQuantity) {
            delete next[qid];
          } else {
            next[qid] = { [optionKey]: next[qid]?.[optionKey] || 1 };
          }
        } else {
          if (!next[qid]) next[qid] = {};

          if (option.hasQuantity) {
            next[qid][optionKey] = next[qid][optionKey] ? next[qid][optionKey] : 1;
          } else {
            if (next[qid][optionKey]) {
              delete next[qid][optionKey];
              if (Object.keys(next[qid]).length === 0) delete next[qid];
            } else {
              next[qid][optionKey] = 1;
            }
          }
        }

        return next;
      });

      setShowRequiredError(false);
    },
    [setSelectedOptions]
  );

  const changeQty = useCallback(
    (question, option, diff) => {
      const optionKey = option.__optionKey || String(option.id);

      setSelectedOptions((prev) => {
        const next = { ...prev };
        const qid = question.id;

        if (!next[qid]) next[qid] = {};
        next[qid][optionKey] = Number(next[qid][optionKey] || 0) + diff;

        if (next[qid][optionKey] <= 0) {
          delete next[qid][optionKey];
          if (Object.keys(next[qid]).length === 0) delete next[qid];
        }

        return next;
      });

      setShowRequiredError(false);
    },
    [setSelectedOptions]
  );

  const handleBack = useCallback(() => {
    if (questionIndex > 0) setQuestionIndex((p) => p - 1);
    setShowRequiredError(false);
  }, [questionIndex, setQuestionIndex]);

  const handleNext = useCallback(() => {
    if (isRequired && !isAnswered) {
      setShowRequiredError(true);
      return;
    }

    if (questionIndex < (product?.questions?.length || 0) - 1) {
      setQuestionIndex((p) => p + 1);
      setShowRequiredError(false);
      return;
    }

    onSave();
  }, [isRequired, isAnswered, questionIndex, product, setQuestionIndex, onSave]);

  if (!product || !currentQuestion) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-5xl font-bold">{ui.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex flex-col">
      <div className="relative min-h-[330px] px-8 pt-8">
        <div className="flex min-w-0 justify-center">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl blur opacity-30 transition duration-500"></div>
            <img
              src={product.image || `${process.env.PUBLIC_URL}/coffee.png`}
              alt={product.name}
              className="relative w-96 h-72 object-cover rounded-2xl shadow-2xl"
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.currentTarget.src = `${process.env.PUBLIC_URL}/coffee.png`;
              }}
            />
          </div>
        </div>
        <AppointmentDurationBanner
          totalMinutes={durationOverview.totalMinutes}
          totalDays={durationOverview.totalDays}
          parts={durationOverview.parts}
          languageCode={languageCode}
          compact
          className="absolute right-5 top-5 z-20 aspect-square w-[200px] max-w-[24vw] overflow-hidden text-center flex flex-col justify-center"
        />
      </div>

      <div className="w-full px-6 mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="absolute h-full bg-gradient-to-r from-emerald-400 to-cyan-500 rounded-full"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>
              {ui.question} {questionIndex + 1} / {product.questions.length}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>
      </div>

      <div className="text-center mb-6 px-6">
        <h2 className="text-4xl font-bold text-gray-900 mb-3">
          {currentQuestion.text}
          {currentQuestion.isRequired && <span className="text-red-500 ml-1">*</span>}
        </h2>

        {isEditing && (
          <div className="inline-block mt-3 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-semibold shadow-lg">
            {ui.editMode}
          </div>
        )}
      </div>

      {showRequiredError && (
        <div className="mx-6 mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl shadow-lg">
          <div className="flex items-center text-red-700">
            <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 002 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium text-lg">{ui.chooseOption}</span>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-6 pb-48">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {(currentQuestion.options || []).map((option, index) => {
            const optionKey = option.__optionKey || String(option.id);
            const quantity = Number(selectedOptions?.[currentQuestion.id]?.[optionKey] || 0);
            const isSelected = quantity > 0;
            const optionPrice = getOptionPriceInfo(option);

            const optionImage =
              option.image ||
              currentQuestion.image ||
              product.image ||
              `${process.env.PUBLIC_URL}/coffee.png`;

            return (
              <button
                key={`${currentQuestion.id}_${optionKey}_${index}`}
                onClick={() => handleOptionSelect(currentQuestion, option)}
                className={`group relative border-3 rounded-3xl p-6 cursor-pointer transition-all duration-300 flex flex-col h-[420px] ${
                  isSelected
                    ? "border-emerald-400 bg-gradient-to-br from-emerald-50 to-cyan-50 shadow-2xl shadow-emerald-500/30 ring-4 ring-purple-400"
                    : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-xl"
                }`}
              >
                <div className="absolute top-4 left-4 z-20">
                  <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-xl rounded-2xl shadow-lg">
                    {optionPrice.original > 0 ? (
                      option.discountEnabled && optionPrice.rate > 0 ? (
                        <span className="flex flex-col leading-tight">
                          <span className="text-xs line-through opacity-80">{`${product.addMainProductToReceipt ? "+" : ""}${formatTRY(optionPrice.original)}`}</span>
                          <span>{`${product.addMainProductToReceipt ? "+" : ""}${formatTRY(optionPrice.effective)} · %${optionPrice.rate}`}</span>
                        </span>
                      ) : `${product.addMainProductToReceipt ? "+" : ""}${formatTRY(optionPrice.effective)}`
                    ) : ui.free}
                  </span>
                </div>

                {isSelected && (
                  <div className="absolute top-4 right-4 z-20 w-12 h-12 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full flex items-center justify-center shadow-xl">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}

                <div className="overflow-hidden rounded-2xl mb-4 h-[252px] w-full flex-shrink-0 bg-gray-100">
                  <img
                    src={optionImage}
                    alt={option.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    onError={(e) => {
                      e.currentTarget.src = product.image || `${process.env.PUBLIC_URL}/coffee.png`;
                    }}
                  />
                </div>

                <div className="flex flex-col mt-auto">
                  {option.durationEnabled && option.durationVisible !== false && (
                    <div className="mb-3">
                      <span className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-lg font-extrabold text-white shadow-lg">
                        ⏱ {ui.optionDuration}: {formatDurationValue(option.durationValue || "0", languageCode, { prefix: false })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-gray-900 text-2xl">{option.name}</h3>
                    {option.hasQuantity && quantity > 0 && (
                      <span className="inline-block px-3 py-1 bg-emerald-500 text-white font-bold text-lg rounded-full">
                        {quantity}
                      </span>
                    )}
                  </div>

                  <div className="flex justify-end items-center">
                    {option.hasQuantity && (
                      <div className="flex items-center gap-3 bg-black/10 rounded-full px-4 py-2">
                        <button
                          className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl font-bold text-gray-800 shadow-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            changeQty(currentQuestion, option, -1);
                          }}
                        >
                          −
                        </button>

                        <span className="font-bold text-2xl text-gray-900 min-w-[3rem] text-center">
                          {quantity || 0}
                        </span>

                        <button
                          className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl font-bold text-gray-800 shadow-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            changeQty(currentQuestion, option, 1);
                          }}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-2xl py-8">
        <div className="flex justify-between items-center max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6">
            <button
              className={`px-12 py-6 rounded-2xl font-bold text-2xl transition-all duration-300 ${
                questionIndex > 0
                  ? "bg-gray-100 text-gray-700"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
              onClick={handleBack}
              disabled={questionIndex === 0}
            >
              ← {ui.back}
            </button>

            <button
              className={`px-12 py-6 rounded-2xl font-bold text-2xl text-white ${
                isEditing
                  ? "bg-gradient-to-r from-orange-500 to-amber-500"
                  : "bg-gradient-to-r from-red-500 to-pink-500"
              }`}
              onClick={onCancel}
            >
              {isEditing ? ui.cancelEdit : ui.cancelReservation}
            </button>
          </div>

          <div className="flex items-center gap-8">
            <div className="text-right">
              <div className="text-5xl font-bold text-emerald-600">{formatTRY(totalPrice)}</div>
              <div className="text-2xl text-gray-700 font-semibold mt-2">{product.name}</div>
              {isEditing && (
                <div className="text-xl text-purple-600 font-semibold mt-2">{ui.editMode}</div>
              )}
            </div>

            <button
              className={`px-12 py-6 rounded-2xl font-bold text-2xl transition-all duration-300 ${
                isRequired && !isAnswered
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-xl"
              }`}
              onClick={handleNext}
              disabled={isRequired && !isAnswered}
            >
              {questionIndex < product.questions.length - 1
                ? `${ui.continue} →`
                : isEditing
                ? `${ui.update} →`
                : `${ui.complete} →`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});


const RecommendedProductPreviewModal = React.memo(function RecommendedProductPreviewModal({
  product,
  onAdd,
  onClose,
  languageCode = "tr",
}) {
  const [mediaReady, setMediaReady] = useState(true);
  useEffect(() => {
    // Önerilen ürün medyaları kiosk başlangıcında preload edildiği için
    // tanıtım modalında iki frame bekletmeye gerek yok.
    setMediaReady(true);
  }, [product]);

  if (!product) return null;
  const text = getRecommendationUiText(languageCode);
  const priceInfo = getDiscountPrice(product.basePrice, product.discountEnabled, product.discountRate);
  return (
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/60 p-5">
      <div className="max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white p-7 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-black uppercase tracking-widest text-violet-600">{text.recommendation}</div>
            <h2 className="mt-1 text-4xl font-black leading-tight text-gray-900">{product.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="h-12 w-12 rounded-full bg-gray-100 text-2xl font-black text-gray-600">×</button>
        </div>

        {!mediaReady && (product.videoUrl || product.imageUrl || product.mediaUrl) ? (
          <div className="mt-6 aspect-video overflow-hidden rounded-3xl bg-gray-100 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin" />
          </div>
        ) : mediaReady && product.videoUrl ? (
          <div className="mt-6 aspect-video overflow-hidden rounded-3xl bg-black">
            <video
              src={product.videoUrl}
              poster={product.imageUrl || product.posterUrl || undefined}
              className="h-full w-full object-contain"
              controls
              autoPlay
              preload="auto"
              playsInline
            />
          </div>
        ) : mediaReady && (product.imageUrl || product.posterUrl || product.image) ? (
          <div className="mt-6 aspect-video overflow-hidden rounded-3xl bg-gray-100">
            <img src={product.imageUrl || product.posterUrl || product.image} alt={product.name} className="h-full w-full object-cover" loading="eager" decoding="async" />
          </div>
        ) : null}

        {product.description ? (
          <div className="mt-6 rounded-2xl border border-violet-100 bg-violet-50 p-5 text-xl font-semibold leading-relaxed text-gray-800">
            {product.description}
          </div>
        ) : null}

        <div className="mt-7 flex flex-col gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {priceInfo.rate > 0 && <div className="text-sm font-bold text-gray-400 line-through">{formatTRY(priceInfo.original)}</div>}
            <div className="text-3xl font-black text-emerald-700">{formatTRY(priceInfo.effective)}</div>
          </div>
          <button
            type="button"
            onClick={() => onAdd?.(product)}
            className="min-w-48 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-5 text-2xl font-black text-white shadow-xl"
          >
            {text.add}
          </button>
        </div>
      </div>
    </div>
  );
});


const RecommendedProductCatalogCard = React.memo(function RecommendedProductCatalogCard({
  product,
  currentQty = 0,
  onRecommendationAdd,
  onRecommendationQuantityChange,
  languageCode = "tr",
}) {
  const recommendationUi = getRecommendationUiText(languageCode);
  const priceInfo = getDiscountPrice(product.basePrice, product.discountEnabled, product.discountRate);
  const cardImageUrl = String(product.imageUrl || product.posterUrl || product.image || "").trim();
  const recommendationId = product.recommendationId || product.id;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onRecommendationAdd?.(product)}
      className="mx-auto w-full max-w-[300px] cursor-pointer overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-lg"
      style={{
        contentVisibility: "auto",
        contain: "layout paint style",
        containIntrinsicSize: "300px 270px",
      }}
    >
      <div className="relative aspect-video overflow-hidden bg-gray-100">
        {cardImageUrl ? (
          <div className="relative h-full w-full bg-slate-100">
            <img
              src={cardImageUrl}
              alt={product.name}
              className="h-full w-full object-cover"
              loading="eager"
              decoding="async"
              fetchPriority="auto"
            />
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-5xl">✨</div>
        )}
        {product.durationEnabled && product.durationVisible !== false && product.durationValue && (
          <div className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-extrabold text-orange-700 shadow">
            {formatDurationValue(product.durationValue, languageCode, { prefix: true })}
          </div>
        )}
        <div className="absolute right-3 top-3 max-w-[160px] rounded-2xl bg-white/95 px-3 py-1.5 text-center text-sm font-black text-gray-900 shadow-lg">
          {priceInfo.rate > 0 ? (
            <div className="leading-tight">
              <div className="text-xs font-bold text-gray-400 line-through">{formatTRY(priceInfo.original)}</div>
              <div className="text-rose-600">{formatTRY(priceInfo.effective)} · %{priceInfo.rate}</div>
            </div>
          ) : (
            formatTRY(priceInfo.effective)
          )}
        </div>
        {currentQty > 0 && (
          <div className="absolute bottom-3 right-3 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-black text-white shadow-lg">
            {getAddedCountText(currentQty, languageCode)}
          </div>
        )}
      </div>
      <div className="px-3.5 py-3">
        <h3 className="line-clamp-2 min-h-[42px] text-lg font-extrabold leading-tight text-gray-900">{product.name}</h3>
        <p className="mt-1 line-clamp-2 min-h-[34px] text-xs leading-snug text-gray-600">{product.description || ""}</p>
        <div className="mt-3 flex items-end justify-between gap-3">
          <div className="text-sm font-bold text-gray-500">
            {product.hasQuantity ? recommendationUi.quantity : recommendationUi.recommendation}
          </div>
          {product.hasQuantity && currentQty > 0 ? (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={() => onRecommendationQuantityChange?.(recommendationId, -1)} className="h-10 w-10 rounded-xl bg-gray-100 text-xl font-black text-gray-700">−</button>
              <div className="min-w-12 text-center"><div className="text-[10px] font-bold text-gray-400">{recommendationUi.quantity}</div><div className="text-xl font-black text-violet-800">{currentQty}</div></div>
              <button type="button" onClick={() => onRecommendationAdd?.(product)} className="h-10 w-10 rounded-xl bg-violet-600 text-xl font-black text-white">+</button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRecommendationAdd?.(product); }}
              className="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-black text-white shadow-lg"
            >
              {recommendationUi.add}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

const RecommendedProductsCatalogModal = React.memo(function RecommendedProductsCatalogModal({
  open,
  cartEntries,
  recommendedProducts = [],
  recommendedHeading = "",
  onRecommendationAdd,
  onRecommendationQuantityChange,
  onNoThanks,
  languageCode = "tr",
}) {
  const recommendationUi = getRecommendationUiText(languageCode);
  const scrollAreaRef = useRef(null);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  // Tanıtım modalını katalog component'i içinde tutuyoruz. Böylece bir ekstra karta
  // dokunulduğunda tüm büyük App ağacı değil, yalnızca öneri modalı yeniden render olur.
  const [localPreviewProduct, setLocalPreviewProduct] = useState(null);
  const seenPreviewIdsRef = useRef(new Set());

  useEffect(() => {
    if (!open) {
      setLocalPreviewProduct(null);
      return;
    }
    try {
      const sessionId = ensureOrderSessionIdStorage();
      const stored = getStorageJSON(`recommendedPreviewSeen:${sessionId}`, []);
      seenPreviewIdsRef.current = new Set(Array.isArray(stored) ? stored.map(String) : []);
    } catch {
      seenPreviewIdsRef.current = new Set();
    }
  }, [open]);

  const markLocalPreviewSeen = useCallback((recommendationId) => {
    const id = String(recommendationId || "");
    if (!id) return;
    if (seenPreviewIdsRef.current.has(id)) return;
    seenPreviewIdsRef.current.add(id);
    try {
      const sessionId = ensureOrderSessionIdStorage();
      setStorageJSON(`recommendedPreviewSeen:${sessionId}`, Array.from(seenPreviewIdsRef.current));
    } catch {}
  }, []);

  const handleLocalRecommendationAdd = useCallback((product) => {
    if (!product) return;
    const recommendationId = String(product.recommendationId || product.id || "");
    const hasPreviewContent = !!String(product.videoUrl || product.imageUrl || product.mediaUrl || "").trim() || !!String(product.description || "").trim();
    if (hasPreviewContent && !seenPreviewIdsRef.current.has(recommendationId)) {
      setLocalPreviewProduct(product);
      return;
    }
    markLocalPreviewSeen(recommendationId);
    onRecommendationAdd?.(product);
  }, [markLocalPreviewSeen, onRecommendationAdd]);

  const confirmLocalPreviewAdd = useCallback((product) => {
    const target = product || localPreviewProduct;
    if (!target) return;
    markLocalPreviewSeen(target.recommendationId || target.id);
    setLocalPreviewProduct(null);
    // Modalın kapanışı önce paint olsun; ağır rezervasyon/sepet state değişimi bir sonraki frame'e kalsın.
    window.requestAnimationFrame(() => onRecommendationAdd?.(target));
  }, [localPreviewProduct, markLocalPreviewSeen, onRecommendationAdd]);

  useEffect(() => {
    if (!open) return;
    const raf = window.requestAnimationFrame(() => {
      const el = scrollAreaRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 8) setHasReachedBottom(true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open, recommendedProducts.length]);

  const handleCatalogScroll = useCallback((event) => {
    const el = event.currentTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= 18) setHasReachedBottom(true);
  }, []);

  const recommendedCartMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(cartEntries) ? cartEntries : []).forEach(([orderKey, item]) => {
      if (!item?.isRecommendedExtra || !item?.recommendationId) return;
      const id = String(item.recommendationId);
      const previous = map.get(id) || { orderKey, item, entries: [], quantity: 0 };
      previous.entries.push({ orderKey, item });
      previous.quantity += Math.max(1, Number(item?.recommendedQuantity || 1));
      previous.orderKey = orderKey;
      previous.item = item;
      map.set(id, previous);
    });
    return map;
  }, [cartEntries]);

  if (!Array.isArray(recommendedProducts) || recommendedProducts.length === 0) return null;

  return (
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-[10010] items-center justify-center bg-black/60 p-5 ${open ? "flex" : "hidden"}`}
    >
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 shadow-2xl">
        <div className="border-b border-violet-100 bg-white px-7 py-6 text-center">
          <div className="text-sm font-black uppercase tracking-[0.22em] text-violet-600">{recommendationUi.recommendation}</div>
          <h2 className="mt-2 text-3xl font-black leading-tight text-violet-950 md:text-4xl">
            {recommendedHeading || "Bu hizmeti alanlar bunları da ekledi"}
          </h2>
        </div>

        <div
          ref={scrollAreaRef}
          onScroll={handleCatalogScroll}
          className="relative overflow-y-auto px-6 py-6 md:px-8"
        >
          {!hasReachedBottom && (
            <div className="pointer-events-none sticky top-4 z-30 ml-auto mb-3 w-fit rounded-full border border-violet-200 bg-white/95 px-4 py-2 text-sm font-black text-violet-700 shadow-lg">
              {normalizeLanguageCode(languageCode) === "tr" ? "Devam etmek için aşağıya kaydırın ↓" : "Scroll down to continue ↓"}
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            {recommendedProducts.map((product) => {
              const current = recommendedCartMap.get(String(product.recommendationId || product.id));
              const currentQty = Math.max(0, Number(current?.quantity || 0));
              return (
                <RecommendedProductCatalogCard
                  key={product.recommendationId || product.id}
                  product={product}
                  currentQty={currentQty}
                  onRecommendationAdd={handleLocalRecommendationAdd}
                  onRecommendationQuantityChange={onRecommendationQuantityChange}
                  languageCode={languageCode}
                />
              );
            })}
          </div>

          <div className="mt-8 border-t border-violet-100 pt-6 pb-2">
            <button
              type="button"
              onClick={() => hasReachedBottom && onNoThanks?.()}
              disabled={!hasReachedBottom}
              className={`w-full rounded-2xl border-2 px-6 py-5 text-xl font-black shadow-sm ${
                hasReachedBottom
                  ? "border-gray-300 bg-white text-gray-700"
                  : "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
              }`}
            >
              {recommendationUi.noThanks}
            </button>
            {!hasReachedBottom && (
              <div className="mt-3 text-center text-sm font-bold text-violet-600">
                {normalizeLanguageCode(languageCode) === "tr" ? "Devam etmek için listenin en altına kaydırın." : "Scroll to the bottom of the list to continue."}
              </div>
            )}
          </div>
        </div>

        <RecommendedProductPreviewModal
          product={localPreviewProduct}
          onAdd={confirmLocalPreviewAdd}
          onClose={() => setLocalPreviewProduct(null)}
          languageCode={languageCode}
        />
      </div>
    </div>
  );
});

const SummaryScreen = React.memo(function SummaryScreen({
  cartEntries,
  totalAmount,
  onBackToMenu,
  onAddNewProduct,
  onEdit,
  onRemove,
  onProceedPayment,
  recommendedProducts = [],
  recommendedHeading = "",
  onRecommendationAdd,
  onRecommendationQuantityChange,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  const recommendationUi = getRecommendationUiText(languageCode);
  const durationOverview = useMemo(() => buildDurationOverview(cartEntries, languageCode), [cartEntries, languageCode]);
  const recommendedCartMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(cartEntries) ? cartEntries : []).forEach(([orderKey, item]) => {
      if (!item?.isRecommendedExtra || !item?.recommendationId) return;
      const id = String(item.recommendationId);
      const previous = map.get(id) || { orderKey, item, entries: [], quantity: 0 };
      previous.entries.push({ orderKey, item });
      previous.quantity += Math.max(1, Number(item?.recommendedQuantity || 1));
      previous.orderKey = orderKey;
      previous.item = item;
      map.set(id, previous);
    });
    return map;
  }, [cartEntries]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 pb-48">
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">{ui.reservationSummary}</h1>
          <p className="text-gray-600 text-center text-lg">
            {ui.cartHas} {cartEntries.length} {ui.serviceCount}.
          </p>
          <p className="text-3xl font-bold text-center text-emerald-600 mt-4">
            {ui.total}: {formatTRY(totalAmount)}
          </p>
          <AppointmentDurationBanner
            totalMinutes={durationOverview.totalMinutes}
            totalDays={durationOverview.totalDays}
            parts={durationOverview.parts}
            languageCode={languageCode}
            compact
            showTotalSentence
            className="mx-auto mt-6 max-w-4xl text-center"
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {Array.isArray(recommendedProducts) && recommendedProducts.length > 0 && (
          <div className="mb-7 overflow-hidden rounded-3xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-6 shadow-xl">
            <h2 className="text-center text-3xl font-black leading-tight text-violet-950">
              {recommendedHeading || "Bu hizmeti alanlar bunları da ekledi"}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
              {recommendedProducts.map((product) => {
                const current = recommendedCartMap.get(String(product.recommendationId || product.id));
                const currentQty = Math.max(0, Number(current?.quantity || 0));
                const priceInfo = getDiscountPrice(product.basePrice, product.discountEnabled, product.discountRate);
                const cardImageUrl = String(product.imageUrl || product.posterUrl || product.image || "").trim();
                return (
                  <div
                    key={product.recommendationId || product.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      onRecommendationAdd?.(product);
                    }}
                    className="group mx-auto w-full max-w-[300px] cursor-pointer overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-lg transition-all duration-300 hover:-translate-y-1 hover:border-violet-300 hover:shadow-2xl"
                  >
                    <div className="relative aspect-video overflow-hidden bg-gray-100">
                      {cardImageUrl ? (
                        <div className="relative h-full w-full bg-slate-100">
                          <img src={cardImageUrl} alt={product.name} className="h-full w-full object-cover" loading="eager" decoding="async" />
                        </div>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-5xl">✨</div>
                      )}
                      {product.durationEnabled && product.durationVisible !== false && product.durationValue && (
                        <div className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-extrabold text-orange-700 shadow">
                          {formatDurationValue(product.durationValue, languageCode, { prefix: true })}
                        </div>
                      )}
                      <div className="absolute right-3 top-3 max-w-[160px] rounded-2xl bg-white/95 px-3 py-1.5 text-center text-sm font-black text-gray-900 shadow-lg">
                        {priceInfo.rate > 0 ? (
                          <div className="leading-tight">
                            <div className="text-xs font-bold text-gray-400 line-through">{formatTRY(priceInfo.original)}</div>
                            <div className="text-rose-600">{formatTRY(priceInfo.effective)} · %{priceInfo.rate}</div>
                          </div>
                        ) : (
                          formatTRY(priceInfo.effective)
                        )}
                      </div>
                    </div>
                    <div className="px-4 py-3">
                      <h3 className="line-clamp-2 min-h-[50px] text-xl font-extrabold leading-tight text-gray-900 transition-colors group-hover:text-violet-700">{product.name}</h3>
                      <p className="mt-1 line-clamp-2 min-h-[34px] text-xs leading-snug text-gray-600">{product.description || ""}</p>
                      <div className="mt-3 flex items-end justify-between gap-3">
                        <div className="text-sm font-bold text-gray-500">
                          {product.hasQuantity ? recommendationUi.quantity : recommendationUi.recommendation}
                        </div>
                        {product.hasQuantity && current ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => onRecommendationQuantityChange?.(product.recommendationId || product.id, -1)} className="h-10 w-10 rounded-xl bg-gray-100 text-xl font-black text-gray-700">−</button>
                            <div className="min-w-12 text-center"><div className="text-[10px] font-bold text-gray-400">{recommendationUi.quantity}</div><div className="text-xl font-black text-violet-800">{currentQty}</div></div>
                            <button type="button" onClick={() => onRecommendationAdd?.(product)} className="h-10 w-10 rounded-xl bg-violet-600 text-xl font-black text-white">+</button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRecommendationAdd?.(product); }}
                            className="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-black text-white shadow-lg"
                          >
                            {recommendationUi.add}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200/50 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{ui.allServicesSummary}</h2>

          <div className="max-h-[52vh] overflow-y-auto pr-3 pb-28 overscroll-contain" style={{ scrollbarGutter: "stable" }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {cartEntries.map(([orderKey, item]) => (
              <div
                key={orderKey}
                className="group relative overflow-hidden rounded-2xl p-6 border-2 min-h-[320px] flex flex-col bg-gradient-to-br from-emerald-50 to-cyan-50 border-emerald-300"
              >
                <div className="flex justify-between items-start mb-4 gap-4">
                  <div className="flex items-center flex-1">
                    <img
                      src={item.image || `${process.env.PUBLIC_URL}/coffee.png`}
                      alt={item.product}
                      className="w-28 h-28 object-cover rounded-xl mr-4 shadow-md"
                      loading="eager"
                      decoding="async"
                      onError={(e) => {
                        e.currentTarget.src = `${process.env.PUBLIC_URL}/coffee.png`;
                      }}
                    />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">{item.product}</h3>
                      <p className="text-sm text-gray-600">{ui.serviceOrder}: {item.countingnumber}</p>
                      {item.addMainProductToReceipt && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            {ui.basePrice}: <span className="font-semibold">{formatTRY(item.basePrice)}</span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex w-[150px] flex-shrink-0 flex-col items-end gap-2">
                    <div className="text-2xl font-bold text-emerald-600">
                      {formatTRY(item.totalPrice)}
                    </div>

                    <div className="flex w-full flex-col gap-2">
                      {item.isRecommendedExtra ? (
                        <>
                          {item.hasQuantity && (
                            <div className="rounded-lg bg-violet-100 px-3 py-2 text-center text-sm font-extrabold text-violet-800">
                              {recommendationUi.quantity}: {Math.max(1, Number(item.recommendedQuantity || 1))}
                            </div>
                          )}
                          <button
                            onClick={() => onRecommendationQuantityChange?.(item.recommendationId, -1)}
                            className="w-full px-3 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg text-sm font-semibold shadow-lg leading-tight"
                          >
                            {recommendationUi.remove}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => onEdit(orderKey)}
                            className="w-full px-3 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg text-sm font-semibold shadow-lg leading-tight"
                          >
                            {ui.editReservation}
                          </button>
                          <button
                            onClick={() => onRemove(orderKey)}
                            className="w-full px-3 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg text-sm font-semibold shadow-lg leading-tight"
                          >
                            {ui.cancelReservation}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <h4 className="font-bold text-gray-800 mb-3">{ui.selections}:</h4>
                  {item.selections?.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {item.selections.map((selection, index) => (
                        <div
                          key={index}
                          className="flex justify-between bg-white rounded-lg px-3 py-2 shadow-sm"
                        >
                          <span className="text-gray-700">
                            {selection.name}
                            {selection.quantity > 1 && (
                              <span className="text-gray-500"> (x{selection.quantity})</span>
                            )}
                          </span>
                          <span className="flex flex-col items-end gap-1 font-semibold text-emerald-600 ml-2">
                            <span>
                              {selection.totalPrice > 0
                                ? `${item.addMainProductToReceipt ? "+" : ""}${formatTRY(selection.totalPrice)}`
                                : ui.free}
                            </span>
                            {selection.durationEnabled && selection.durationVisible !== false && (
                              <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-extrabold text-orange-700">
                                ⏱ {formatDurationValue(selection.durationValue || "0", languageCode, { prefix: false })}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 bg-white rounded-lg px-3 py-2 shadow-sm">
                      {ui.noSelection}
                    </div>
                  )}
                </div>

                {item.reservationDuration && (
                  <div className="mt-4 rounded-xl border-2 border-orange-200 bg-orange-50 p-4">
                    <p className="text-base font-extrabold text-orange-900">
                      ⏱ {ui.serviceDuration}: {item.reservationDuration.mode === "day"
                        ? formatDurationValue(`${item.reservationDuration.durationDays || 0}d`, languageCode, { prefix: false, long: true })
                        : formatDurationMinutes(item.reservationDuration.durationMinutes || 0, languageCode, { prefix: false, zeroAsDuration: true })}
                    </p>
                  </div>
                )}

                {(item.selectedPersonnel || item.reservationDraft) && (
                  <div className="mt-4 rounded-xl border border-blue-200 bg-white/80 p-4">
                    {item.selectedPersonnel && (
                      <p className="text-base font-bold text-blue-950">
                        {ui.personnel}: {item.selectedPersonnel.firstName || ui.selected}
                      </p>
                    )}
                    {item.reservationDraft?.mode === "time" && (
                      <p className="mt-1 text-sm font-semibold text-gray-700">
                        {ui.dateTime}: {String(item.reservationDraft.startAt || "").slice(0, 10)} ·{" "}
                        {new Date(item.reservationDraft.startAt).toLocaleTimeString("tr-TR", {
                          timeZone: "Europe/Istanbul",
                          hour: "2-digit",
                          minute: "2-digit",
                        })} –{" "}
                        {new Date(item.reservationDraft.endAt).toLocaleTimeString("tr-TR", {
                          timeZone: "Europe/Istanbul",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    )}
                    {item.reservationDraft?.mode === "day" && (
                      <p className="mt-1 text-sm font-semibold text-gray-700">
                        {ui.days}: {item.reservationDraft.startDate} – {item.reservationDraft.endDate} (
                        {item.reservationDraft.durationDays} {ui.day})
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-800">{ui.total}:</span>
                    <span className="text-xl font-bold text-emerald-600">
                      {formatTRY(item.totalPrice)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-5 left-5 right-5 z-30 rounded-[28px] bg-white/90 backdrop-blur-xl border border-gray-200/70 shadow-2xl">
        <div className="max-w-7xl mx-auto px-7 py-7">
          <div className="flex gap-5">
            <button
              onClick={onBackToMenu}
              className="px-10 py-7 bg-gray-200 text-gray-800 rounded-2xl font-bold text-2xl shadow-lg"
            >
              ← {ui.back}
            </button>

            <button
              onClick={onAddNewProduct}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-7 rounded-2xl font-bold text-2xl shadow-xl"
            >
              {ui.addNewService}
            </button>

            <button
              onClick={onProceedPayment}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-7 rounded-2xl font-bold text-2xl shadow-xl"
            >
              {ui.completeReservation} ({formatTRY(totalAmount)})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const PaymentCard = React.memo(function PaymentCard({
  title,
  subtitle,
  imageUrl,
  onClick,
  disabled,
  tapText,
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`relative w-full rounded-[1.5rem] border-4 shadow-xl transition-all duration-300 bg-white/90 backdrop-blur-sm overflow-hidden ${
        disabled ? "pointer-events-none opacity-60" : "hover:-translate-y-1 hover:shadow-2xl"
      } border-gray-200`}
    >
      <div className="p-7 h-[300px] flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-3xl font-extrabold text-gray-900">{title}</div>
            {subtitle ? <div className="text-base text-gray-600 mt-1">{subtitle}</div> : null}
          </div>
        </div>

        <div className="mt-5 flex-1 flex items-center justify-center">
          <div className="w-full h-[150px] rounded-2xl bg-white shadow-inner border border-gray-100 flex items-center justify-center overflow-hidden">
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-contain"
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.currentTarget.src =
                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDcwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZyI+PHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNGM0Y0RjYiLz48cGF0aCBkPSJNMzAwIDE3MEgzOTBWMjMwSDMwMFYxNzBaIiBmaWxsPSIjOUNBMEFGIi8+PC9zdmc+";
              }}
            />
          </div>
        </div>

        <div className="mt-5 text-center">
          <div className="text-sm text-gray-500">{tapText}</div>
        </div>
      </div>
    </button>
  );
});

const PaymentScreen = React.memo(function PaymentScreen({
  totalAmount,
  preferance,
  loading,
  error,
  onBack,
  onPay,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  const paymentOptions = useMemo(() => getPaymentOptions(preferance, ui), [preferance, ui]);
  const localPayLockRef = useRef(false);
  const [localSelectedMethod, setLocalSelectedMethod] = useState("");

  useEffect(() => {
    if (!loading) {
      localPayLockRef.current = false;
      setLocalSelectedMethod("");
    }
  }, [loading]);

  const handlePaymentClick = useCallback((key) => {
    if (loading || localPayLockRef.current) return;
    localPayLockRef.current = true;
    setLocalSelectedMethod(key);

    // V47: onPay, devam eden ödeme veya eski bir kilit nedeniyle erken dönerse
    // PaymentScreen'in kendi tıklama kilidi sonsuza kadar açık kalmasın.
    // Gerçek istek devam ederken kilit promise sonuçlanana kadar korunur.
    Promise.resolve()
      .then(() => onPay(key))
      .catch((error) => {
        console.error("Ödeme seçiminde beklenmeyen hata:", error);
      })
      .finally(() => {
        localPayLockRef.current = false;
        setLocalSelectedMethod("");
      });
  }, [loading, onPay]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 pb-32">
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{ui.paymentMethod}</h1>
            <p className="text-lg text-gray-600 mb-4">{ui.paymentSubtitle}</p>

            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-full inline-flex items-center text-xl font-semibold shadow-lg">
              <span>{ui.totalAmount}: {formatTRY(totalAmount)}</span>
            </div>

            {error && (
              <div className="mt-6 max-w-3xl mx-auto bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700">
                <div className="font-bold mb-1">{ui.orderCreationError}</div>
                <div className="text-sm">{error}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch">
          {paymentOptions.map((option) => (
            <PaymentCard
              key={option.key}
              title={option.title}
              subtitle={option.subtitle}
              imageUrl={option.imageUrl}
              disabled={loading || !!localSelectedMethod}
              tapText={ui.tapToContinue}
              onClick={() => handlePaymentClick(option.key)}
            />
          ))}
        </div>

        <div className="flex justify-center mt-12">
          <button
            onClick={onBack}
            className="px-10 py-5 bg-gradient-to-r from-gray-600 to-gray-800 text-white rounded-2xl font-bold text-xl"
          >
            ← {ui.backToSummary}
          </button>
        </div>
      </div>
    </div>
  );
});

const FullScreenProcessModal = React.memo(function FullScreenProcessModal({
  isOpen,
  title,
  text,
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10020] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-white rounded-[2rem] shadow-2xl max-w-3xl w-full p-12 text-center border border-gray-200">
        <div className="w-28 h-28 mx-auto mb-8 rounded-full border-8 border-emerald-100 border-t-emerald-500 animate-spin" />
        <h2 className="text-5xl font-extrabold text-gray-900 mb-5">{title}</h2>
        <p className="text-2xl text-gray-600 leading-relaxed">{text}</p>
      </div>
    </div>
  );
});

const PaymentRetryScreen = React.memo(function PaymentRetryScreen({
  info,
  onRetry,
  onEditOrder,
  onCancelOrder,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  const message = info?.message || ui.paymentFailedDefault;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-amber-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-[2rem] shadow-2xl border border-red-100 max-w-4xl w-full p-12 text-center">
        <div className="w-28 h-28 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
          <svg className="w-16 h-16 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-5a1 1 0 112 0 1 1 0 01-2 0zm.293-7.707a1 1 0 011.414 0L11 5.586l.293-.293a1 1 0 111.414 1.414L12.414 7l.293.293a1 1 0 01-1.414 1.414L11 8.414l-.293.293a1 1 0 01-1.414-1.414L9.586 7l-.293-.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>

        <h1 className="text-5xl font-extrabold text-gray-900 mb-5">{ui.paymentFailed}</h1>
        <p className="text-2xl text-gray-700 leading-relaxed mb-6">{message}</p>
        <p className="text-xl text-gray-500 mb-10">{ui.paymentFailedHelp}</p>


        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <button
            type="button"
            onClick={onRetry}
            className="py-6 rounded-3xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-3xl font-black shadow-xl"
          >
            {ui.retry}
          </button>
          <button
            type="button"
            onClick={onEditOrder}
            className="py-6 rounded-3xl bg-gradient-to-r from-orange-500 to-amber-500 text-white text-3xl font-black shadow-xl"
          >
            {ui.editOrder}
          </button>
          <button
            type="button"
            onClick={onCancelOrder}
            className="py-6 rounded-3xl bg-gradient-to-r from-red-500 to-pink-600 text-white text-3xl font-black shadow-xl"
          >
            {ui.cancelAction}
          </button>
        </div>
      </div>
    </div>
  );
});

const FinalScreen = React.memo(function FinalScreen({
  notice,
  languageCode = "tr",
}) {
  const ui = getUiText(languageCode);
  const isError = notice?.type === "error" || notice?.printerOk === false;
  const rawMessage = String(notice?.message || "").trim();
  const isTechnicalProgressMessage = /pos\s*belge|belge\s*(kapatma|kapama|kapatılıyor|kapatiliyor)|document\s*close|fiscal|arka\s*planda\s*devam/i.test(rawMessage);
  const warningMessage = isError && !isTechnicalProgressMessage ? rawMessage : "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-8">
      <div className="bg-white rounded-[2.25rem] shadow-2xl border border-gray-200 max-w-5xl w-full p-16 text-center">
        <div className="w-36 h-36 rounded-full flex items-center justify-center mx-auto mb-10 bg-green-100">
          <svg className="w-24 h-24 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>

        <h1 className="text-6xl font-black leading-tight tracking-wide text-gray-900">
          {ui.reservationSuccessTitle}
        </h1>
        <p className="mt-7 text-3xl font-bold leading-relaxed text-emerald-700">
          {ui.reservationSuccessText}
        </p>

        {warningMessage ? (
          <div className="mt-10 rounded-3xl border-2 border-amber-300 bg-amber-50 px-8 py-6 text-2xl font-bold leading-relaxed text-amber-900">
            {warningMessage}
          </div>
        ) : null}
      </div>
    </div>
  );
});

function App() {
  const runningInsideElectron =
    typeof window !== "undefined" &&
    (/Electron/i.test(String(window.navigator?.userAgent || "")) || Boolean(window?.kioskExit));
  const electronVideoCacheAvailable =
    typeof window !== "undefined" && Boolean(window?.kioskVideoCache?.sync);

  const [bootstrapData, setBootstrapData] = useState(DEFAULT_BOOTSTRAP);
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [bootstrapError, setBootstrapError] = useState("");
  const [menuWarmReady, setMenuWarmReady] = useState(false);
  const [menuImagesReady, setMenuImagesReady] = useState(false);
  const [menuPreloadStats, setMenuPreloadStats] = useState({
    total: 0,
    completed: 0,
    loaded: 0,
    failed: 0,
  });
  const [videoCacheReady, setVideoCacheReady] = useState(
    () => !runningInsideElectron
  );
  const [videoCacheError, setVideoCacheError] = useState("");
  const [videoCacheStats, setVideoCacheStats] = useState({
    required: runningInsideElectron,
    phase: runningInsideElectron
      ? electronVideoCacheAvailable
        ? "idle"
        : "error"
      : "unsupported",
    total: 0,
    completed: 0,
    downloaded: 0,
    failed: 0,
    current: "",
  });

  const [recommendedMediaReady, setRecommendedMediaReady] = useState(false);
  const [recommendedMediaStats, setRecommendedMediaStats] = useState({
    total: 0,
    completed: 0,
    loaded: 0,
    failed: 0,
  });

  const [screen, setScreen] = useState("home");
  const [selectedCategoryId, setSelectedCategoryId] = useState("ana-sayfa");
  const [selectedLanguageCode, setSelectedLanguageCode] = useState("tr");
  const [selectedOrderType, setSelectedOrderType] = useState(
    localStorage.getItem("selectedOrderType") || "reservation"
  );
  const [productPreview, setProductPreview] = useState(null);
  const [pendingOrderDraft, setPendingOrderDraft] = useState(null);

  const reservationSelectionContextName = useMemo(() => {
    const draft = pendingOrderDraft;
    if (!draft) return "";
    const mainName = String(draft.product || "").trim();
    if (draft.isRecommendedExtra) return mainName;

    const selectedNames = (Array.isArray(draft.selections) ? draft.selections : [])
      .filter((selection) => Number(selection?.quantity || 0) > 0 && String(selection?.name || "").trim())
      .map((selection) => String(selection.name).trim());

    if (selectedNames.length === 1) return selectedNames[0];
    if (selectedNames.length > 1) {
      const joined = selectedNames.slice(0, 3).join(" + ");
      return mainName ? `${mainName} · ${joined}` : joined;
    }
    return mainName;
  }, [pendingOrderDraft]);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [reservationBusy, setReservationBusy] = useState(false);
  const [reservationError, setReservationError] = useState("");
  const [personnelOverview, setPersonnelOverview] = useState([]);
  // Personel sırası ekrana çıktıktan sonra değişmesin diye özet verisi
  // kiosk hazırlanırken önceden yüklenir. Hazır olmadan kartlar gösterilmez.
  const [personnelOverviewLoading, setPersonnelOverviewLoading] = useState(true);
  const [personnelOverviewReady, setPersonnelOverviewReady] = useState(false);
  const [customerName, setCustomerName] = useState(() =>
    normalizeCustomerName(localStorage.getItem(CUSTOMER_NAME_STORAGE_KEY) || "")
  );

  const [cart, setCart] = useState(() => getStorageJSON("existingOrders", {}));
  // Önerilen ürün tıklamalarında callback kimliklerini sabit tutmak için güncel sepeti ref'te de tutuyoruz.
  // Bu, tek bir ürün değiştiğinde tüm öneri kartlarının gereksiz yeniden render olmasını engeller.
  const cartRef = useRef(cart);
  cartRef.current = cart;

  const [selectedUniqcode, setSelectedUniqcode] = useState("");
  const [editingOrderKey, setEditingOrderKey] = useState(null);
  const [designSelections, setDesignSelections] = useState({});
  const [designQuestionIndex, setDesignQuestionIndex] = useState(0);

  const [confirmCancelAllOpen, setConfirmCancelAllOpen] = useState(false);
  const [addedModalOpen, setAddedModalOpen] = useState(false);
  const [addedProductInfo, setAddedProductInfo] = useState(null);
  const [recommendedCatalogModalOpen, setRecommendedCatalogModalOpen] = useState(false);
  const [reservationCompleteExtraModalOpen, setReservationCompleteExtraModalOpen] = useState(false);

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [modifyOrderLoading, setModifyOrderLoading] = useState(false);
  const [inactivityClosingLoading, setInactivityClosingLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentRetryInfo, setPaymentRetryInfo] = useState(null);
  const [paymentNotice, setPaymentNotice] = useState(null);
  const [paymentFlowActive, setPaymentFlowActive] = useState(false);
  const [finalOrderNumber, setFinalOrderNumber] = useState("");
  const [finalPaymentMethod, setFinalPaymentMethod] = useState("");
  const [finalPreapprove, setFinalPreapprove] = useState(false);

  const [isInactivityModalOpen, setIsInactivityModalOpen] = useState(false);
  const [inactivitySecondsLeft, setInactivitySecondsLeft] = useState(30);

  const [menuResetToken, setMenuResetToken] = useState(0);

  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const paymentSubmitLockRef = useRef(false);
  const reservationSubmitLockRef = useRef(false);
  const paymentFlowActiveRef = useRef(false);
  const inactivityTimeoutCancelStartedRef = useRef(false);
  const personnelRandomRanksRef = useRef(new Map());
  // Saat ekranı açılmadan önce personel bazlı müsaitlik verilerini hazır tutar.
  // Aynı istek devam ederken ikinci bir ağ isteği açılmaz.
  const availabilityCacheRef = useRef(new Map());

  const STANDARD_INACTIVITY_LIMIT_MS = 90 * 1000;
  const STANDARD_INACTIVITY_MODAL_SECONDS = 30;
  const PAYMENT_INACTIVITY_LIMIT_MS = 70 * 1000;
  const PAYMENT_INACTIVITY_MODAL_SECONDS = 15;

  useEffect(() => {
    paymentFlowActiveRef.current = paymentFlowActive;
  }, [paymentFlowActive]);

  useEffect(() => {
    const bridge = window?.kioskVideoCache;
    if (!bridge?.onProgress) return undefined;

    return bridge.onProgress((stats = {}) => {
      setVideoCacheStats((current) => ({
        ...current,
        ...stats,
        required: true,
      }));
    });
  }, []);

  const getInactivityConfig = useCallback(() => {
    if (paymentFlowActiveRef.current) {
      return {
        limitMs: PAYMENT_INACTIVITY_LIMIT_MS,
        modalSeconds: PAYMENT_INACTIVITY_MODAL_SECONDS,
        paymentMode: true,
      };
    }

    return {
      limitMs: STANDARD_INACTIVITY_LIMIT_MS,
      modalSeconds: STANDARD_INACTIVITY_MODAL_SECONDS,
      paymentMode: false,
    };
  }, []);

  const cartEntries = useMemo(() => sortCartEntries(cart), [cart]);
  const menuProductAddedCounts = useMemo(() => {
    const map = new Map();
    cartEntries.forEach(([, item]) => {
      if (!item || item.isRecommendedExtra) return;
      const uniqcode = String(item.uniqcode || "").trim();
      if (!uniqcode) return;
      map.set(uniqcode, (map.get(uniqcode) || 0) + 1);
    });
    return map;
  }, [cartEntries]);
  const cartTotal = useMemo(
    () => cartEntries.reduce((sum, [, item]) => sum + Number(item?.totalPrice || 0), 0),
    [cartEntries]
  );

  // Menü bootstrap sonrası sıcak (mounted) tutuluyor. Fakat summary/öneri modalındayken
  // sepet değişimleri görünmeyen tüm menü kartlarını yeniden render etmemeli. Menü görünür
  // değilken son görünür sunumu donduruyor, menüye dönüldüğü anda güncel değeri veriyoruz.
  const frozenMenuCartPresentationRef = useRef({
    cartCount: cartEntries.length,
    cartTotal,
    productAddedCounts: menuProductAddedCounts,
  });
  if (screen === "menu") {
    frozenMenuCartPresentationRef.current = {
      cartCount: cartEntries.length,
      cartTotal,
      productAddedCounts: menuProductAddedCounts,
    };
  }
  const menuCartPresentation = screen === "menu"
    ? frozenMenuCartPresentationRef.current
    : frozenMenuCartPresentationRef.current;

  const pendingDurationOverview = useMemo(() => {
    if (!pendingOrderDraft) return buildDurationOverview(cartEntries, selectedLanguageCode);
    const excludeKey = pendingOrderDraft.editingOrderKey || "";
    const others = cartEntries.filter(([key]) => String(key) !== String(excludeKey));
    return buildDurationOverview([...others, [pendingOrderDraft.orderKey || "__pending__", pendingOrderDraft]], selectedLanguageCode);
  }, [cartEntries, pendingOrderDraft, selectedLanguageCode]);

  // Saat seçim ekranı toplam sepet süresini değil, yalnızca o anda
  // randevusu seçilen hizmetin kendi süresini göstermelidir.
  const pendingServiceDurationOverview = useMemo(() => {
    if (!pendingOrderDraft) return { totalMinutes: 0, totalDays: 0, parts: [] };
    return buildDurationOverview(
      [[pendingOrderDraft.orderKey || "__pending__", pendingOrderDraft]],
      selectedLanguageCode
    );
  }, [pendingOrderDraft, selectedLanguageCode]);


  useEffect(() => {
    paymentDebug("PAYMENT STATE CHANGE", {
      screen,
      paymentLoading,
      paymentFlowActive,
      paymentError,
      paymentRetryInfo,
      finalOrderNumber,
      finalPaymentMethod,
      cartCount: cartEntries.length,
      cartTotal,
      storage: readPaymentLocalStorageDebug(),
    });
  }, [
    screen,
    paymentLoading,
    paymentFlowActive,
    paymentError,
    paymentRetryInfo,
    finalOrderNumber,
    finalPaymentMethod,
    cartEntries.length,
    cartTotal,
  ]);

  const localizedBootstrapData = useMemo(
    () => localizeBootstrapForLanguage(bootstrapData, selectedLanguageCode),
    [bootstrapData, selectedLanguageCode]
  );

  const currentProduct = useMemo(() => {
    const raw = localizedBootstrapData?.productsByUniqcode?.[selectedUniqcode] || null;
    return normalizeProductForUI(raw);
  }, [localizedBootstrapData, selectedUniqcode]);

  const availableLanguageCodes = useMemo(
    () => uniqueLanguageCodes(bootstrapData?.languages || ["tr", "en", "ru", "de", "ar"]),
    [bootstrapData?.languages]
  );


  const recommendationsEnabledForCart = useMemo(
    () => cartEntries.some(([, item]) => !item?.isRecommendedExtra && !!item?.showRecommendedProducts),
    [cartEntries]
  );

  const allRecommendedProductsForLanguage = useMemo(
    () => Array.isArray(localizedBootstrapData?.preferance?.recommendedProducts)
      ? localizedBootstrapData.preferance.recommendedProducts
      : [],
    [localizedBootstrapData]
  );

  const recommendedProductsForSummary = recommendationsEnabledForCart
    ? allRecommendedProductsForLanguage
    : [];

  const recommendedHeadingForSummary = recommendationsEnabledForCart
    ? String(localizedBootstrapData?.preferance?.recommendedHeading || "Bu hizmeti alanlar bunları da ekledi")
    : "";

  const reservationCompleteExtraProductsForLanguage = useMemo(
    () => Array.isArray(localizedBootstrapData?.preferance?.reservationCompleteExtraProducts)
      ? localizedBootstrapData.preferance.reservationCompleteExtraProducts
      : [],
    [localizedBootstrapData]
  );
  const reservationCompleteExtraHeading = String(
    localizedBootstrapData?.preferance?.reservationCompleteExtraHeading ||
    "Rezervasyonunuza bunları da eklemek ister misiniz?"
  );

  const reservationCompleteExtrasSeenKey = useCallback(() =>
    `reservationCompleteExtrasSeen:${ensureOrderSessionIdStorage()}`, []);

  const hasSeenReservationCompleteExtras = useCallback(() => {
    try { return localStorage.getItem(reservationCompleteExtrasSeenKey()) === "1"; } catch { return false; }
  }, [reservationCompleteExtrasSeenKey]);

  const markReservationCompleteExtrasSeen = useCallback(() => {
    try { localStorage.setItem(reservationCompleteExtrasSeenKey(), "1"); } catch {}
  }, [reservationCompleteExtrasSeenKey]);

  const buildRecommendedReservationDuration = useCallback((product, quantity = 1) => {
    const qty = Math.max(1, Number(quantity || 1));
    if (product?.dayEnabled) {
      const days = product?.durationEnabled && product?.durationValue
        ? durationValueToDays(product.durationValue)
        : 0;
      return {
        mode: "day",
        durationDays: Math.max(1, Number(days || 0)) * qty,
        durationMinutes: 0,
        allDay: false,
        rawDurationMinutes: 0,
      };
    }
    if (!product?.durationEnabled || !product?.durationValue) {
      return { mode: "time", durationDays: 0, durationMinutes: 0, allDay: false, rawDurationMinutes: 0 };
    }
    const raw = durationValueToMinutes(product.durationValue);
    if (raw === Infinity) {
      return { mode: "time", durationDays: 0, durationMinutes: 0, allDay: true, rawDurationMinutes: 0 };
    }
    return {
      mode: "time",
      durationDays: 0,
      durationMinutes: Math.max(0, Number(raw || 0)) * qty,
      allDay: false,
      rawDurationMinutes: Math.max(0, Number(raw || 0)) * qty,
    };
  }, []);

  const releaseRecommendedHoldDirect = useCallback(async (holdId) => {
    const clean = String(holdId || "").trim();
    if (!clean) return;
    try {
      await makeApiRequest(`${API_BASE_URL}/reservation/release`, {
        method: "POST",
        body: JSON.stringify({
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          holdIds: [clean],
        }),
      });
    } catch (error) {
      console.warn("Önerilen ürün rezervasyon bekletmesi bırakılamadı:", error?.message || error);
    }
  }, []);

  const addRecommendedProductToCart = useCallback((product) => {
    if (!product) return;
    const recommendationId = String(product.recommendationId || product.id || "");
    if (!recommendationId) return;
    const priceInfo = getDiscountPrice(product.basePrice, product.discountEnabled, product.discountRate);

    setCart((previous) => {
      const next = { ...previous };
      const existingEntry = Object.entries(next).find(([, item]) =>
        item?.isRecommendedExtra && String(item?.recommendationId || "") === recommendationId
      );

      if (existingEntry && product.hasQuantity) {
        const [key, item] = existingEntry;
        const quantity = Math.max(1, Number(item.recommendedQuantity || 1)) + 1;
        next[key] = {
          ...item,
          recommendedQuantity: quantity,
          totalPrice: roundMoney(priceInfo.effective * quantity),
          reservationDuration: buildRecommendedReservationDuration(product, quantity),
        };
        return next;
      }

      // Adet kapalı öneriler de birden fazla kez eklenebilir. Her tıklama yeni bir
      // sepet/hizmet satırı oluşturur; böylece fiş/POS tarafında da ayrı görünür.
      const orderKey = createOrderKey(`recommended_${recommendationId}`);
      next[orderKey] = {
        orderKey,
        uniqcode: `recommended_${recommendationId}`,
        product: String(product.name || "Önerilen Ürün"),
        productDescription: String(product.description || ""),
        image: String(product.imageUrl || product.posterUrl || product.image || ""),
        videoUrl: String(product.videoUrl || ""),
        basePrice: priceInfo.effective,
        originalBasePrice: priceInfo.original,
        discountEnabled: !!product.discountEnabled,
        discountRate: priceInfo.rate,
        discountAmount: priceInfo.amount,
        totalPrice: priceInfo.effective,
        showOffersInsteadOfPrice: false,
        menuProductIsMain: false,
        addMainProductToReceipt: false,
        showRecommendedProducts: false,
        selections: [],
        selectedOptions: {},
        countingnumber: getNextCountingNumber(previous),
        hasQuestion: false,
        kdv: Number(product.kdv || 0),
        departmanno: normalizeDepartmentNoForPayment(product.departmanno ?? product.departmentNo ?? product.departmentno ?? ""),
        languageCode: selectedLanguageCode,
        subeId: getAuthSubeId(),
        branchName: getAuthBranchName(),
        timestamp: new Date().toISOString(),
        isRecommendedExtra: true,
        extraCatalogKind: String(product?.extraCatalogKind || "recommended"),
        recommendationId,
        recommendedQuantity: 1,
        hasQuantity: !!product.hasQuantity,
        personnelEnabled: false,
        timeEnabled: false,
        dayEnabled: false,
        durationEnabled: !!product.durationEnabled,
        durationVisible: product.durationVisible !== false,
        durationValue: String(product.durationValue || ""),
        sharedTimeValue: String(product.durationValue || ""),
        reservationDuration: buildRecommendedReservationDuration(product, 1),
      };
      return next;
    });
  }, [selectedLanguageCode, buildRecommendedReservationDuration]);

  const startRecommendedSelectionFlow = useCallback((product) => {
    if (!product) return;
    const recommendationId = String(product.recommendationId || product.id || "");
    if (!recommendationId) return;
    const priceInfo = getDiscountPrice(product.basePrice, product.discountEnabled, product.discountRate);
    const orderKey = createOrderKey(`recommended_${recommendationId}`);
    const prepared = {
      orderKey,
      uniqcode: `recommended_${recommendationId}`,
      product: String(product.name || "Önerilen Ürün"),
      productDescription: String(product.description || ""),
      image: String(product.imageUrl || product.posterUrl || product.image || ""),
      videoUrl: String(product.videoUrl || ""),
      basePrice: priceInfo.effective,
      originalBasePrice: priceInfo.original,
      discountEnabled: !!product.discountEnabled,
      discountRate: priceInfo.rate,
      discountAmount: priceInfo.amount,
      totalPrice: priceInfo.effective,
      showOffersInsteadOfPrice: false,
      menuProductIsMain: false,
      addMainProductToReceipt: false,
      showRecommendedProducts: false,
      selections: [],
      selectedOptions: {},
      countingnumber: getNextCountingNumber(cartRef.current),
      hasQuestion: false,
      kdv: Number(product.kdv || 0),
      departmanno: normalizeDepartmentNoForPayment(product.departmanno ?? product.departmentNo ?? product.departmentno ?? ""),
      languageCode: selectedLanguageCode,
      subeId: getAuthSubeId(),
      branchName: getAuthBranchName(),
      timestamp: new Date().toISOString(),
      isRecommendedExtra: true,
      extraCatalogKind: String(product?.extraCatalogKind || "recommended"),
      recommendationId,
      recommendedQuantity: 1,
      hasQuantity: !!product.hasQuantity,
      personnelEnabled: !!product.personnelEnabled,
      timeEnabled: !!product.timeEnabled,
      dayEnabled: !!product.dayEnabled,
      durationEnabled: !!product.durationEnabled,
      durationVisible: product.durationVisible !== false,
      durationValue: String(product.durationValue || ""),
      sharedTimeValue: String(product.durationValue || ""),
      reservationDuration: buildRecommendedReservationDuration(product, 1),
      editingOrderKey: "",
    };

    setPendingOrderDraft(prepared);
    setSelectedPersonnel(null);
    setAvailabilityData(null);
    setAvailabilityError("");
    setReservationError("");
    setEditingOrderKey(null);
    setSelectedUniqcode("");

    if (prepared.dayEnabled) {
      setScreen("dayReservation");
      return;
    }
    if (prepared.personnelEnabled || prepared.timeEnabled) {
      setScreen("personnel");
      return;
    }

    // Güvenlik: seçim bayrakları kapalıysa normal öneri ekleme akışına dön.
    addRecommendedProductToCart(product);
  }, [selectedLanguageCode, buildRecommendedReservationDuration, addRecommendedProductToCart]);

  const continueRecommendedAdd = useCallback((product) => {
    if (!product) return;
    if (product.personnelEnabled || product.timeEnabled || product.dayEnabled) {
      startRecommendedSelectionFlow(product);
      return;
    }
    addRecommendedProductToCart(product);
  }, [startRecommendedSelectionFlow, addRecommendedProductToCart]);

  const changeRecommendedQuantity = useCallback((recommendationId, diff) => {
    const id = String(recommendationId || "");
    const matches = sortCartEntries(cartRef.current).filter(([, item]) =>
      item?.isRecommendedExtra && String(item?.recommendationId || "") === id
    );
    if (!matches.length) return;

    const hasReservationUnits = matches.some(([, item]) =>
      !!item?.reservationDraft || !!item?.selectedPersonnel || !!item?.personnelEnabled || !!item?.timeEnabled || !!item?.dayEnabled
    );

    if (Number(diff || 0) < 0 && hasReservationUnits) {
      const [orderKey, item] = matches[matches.length - 1];
      if (item?.reservationDraft?.holdId) releaseRecommendedHoldDirect(item.reservationDraft.holdId);
      setCart((previous) => {
        const next = { ...previous };
        delete next[orderKey];
        return next;
      });
      return;
    }

    setCart((previous) => {
      const next = { ...previous };
      const entry = Object.entries(next).find(([, item]) => item?.isRecommendedExtra && String(item?.recommendationId || "") === id);
      if (!entry) return previous;
      const [key, item] = entry;
      const currentQty = Math.max(1, Number(item.recommendedQuantity || 1));
      const nextQty = currentQty + Number(diff || 0);
      if (!item.hasQuantity || nextQty <= 0) {
        delete next[key];
        return next;
      }
      const unitPrice = Number(item.basePrice || 0);
      next[key] = {
        ...item,
        recommendedQuantity: nextQty,
        totalPrice: roundMoney(unitPrice * nextQty),
        reservationDuration: item.durationEnabled
          ? buildRecommendedReservationDuration(item, nextQty)
          : item.reservationDuration,
      };
      return next;
    });
  }, [buildRecommendedReservationDuration, releaseRecommendedHoldDirect]);

  const menuImageUrls = useMemo(() => {
    if (!bootstrapReady) return [];
    return collectImageUrls(bootstrapData);
  }, [bootstrapData, bootstrapReady]);

  const personnelForSelection = useMemo(() => {
    const overviewMap = new Map(
      (Array.isArray(personnelOverview) ? personnelOverview : []).map((item) => [
        String(item?.personelId || ""),
        item,
      ])
    );

    const rows = (localizedBootstrapData.personnel || []).map((person) => {
      const personelId = String(person?.personelId || person?.personnelId || person?.id || "");
      let fallbackRank = personnelRandomRanksRef.current.get(personelId);
      if (!Number.isFinite(fallbackRank)) {
        fallbackRank = Math.random();
        personnelRandomRanksRef.current.set(personelId, fallbackRank);
      }
      const overview = overviewMap.get(personelId) || {
        personelId,
        reservationCount: 0,
        fullDates: [],
        randomRank: fallbackRank,
      };
      return { ...person, reservationOverview: overview };
    });

    rows.sort((a, b) =>
      Number(a?.reservationOverview?.reservationCount || 0) - Number(b?.reservationOverview?.reservationCount || 0) ||
      Number(a?.reservationOverview?.randomRank || 0) - Number(b?.reservationOverview?.randomRank || 0)
    );
    return rows;
  }, [localizedBootstrapData.personnel, personnelOverview]);

  const AVAILABILITY_CACHE_TTL_MS = 5 * 60 * 1000;

  const getAvailabilityCacheKeyForDuration = useCallback((person, duration = {}) => {
    const personelId = String(person?.personelId || person?.personnelId || person?.id || "");
    if (!personelId) return "";
    return [
      getAuthSubeId(),
      personelId,
      Number(duration?.durationMinutes || 0),
      duration?.allDay ? "all-day" : "timed",
    ].join("|");
  }, []);

  const getAvailabilityCacheKey = useCallback(
    (person, draft = pendingOrderDraft) =>
      getAvailabilityCacheKeyForDuration(person, draft?.reservationDuration || {}),
    [pendingOrderDraft, getAvailabilityCacheKeyForDuration]
  );

  const invalidateAvailabilityCacheForPersonnel = useCallback((person) => {
    const personelId = String(person?.personelId || person?.personnelId || person?.id || "");
    if (!personelId) return;
    const prefix = `${getAuthSubeId()}|${personelId}|`;
    for (const key of Array.from(availabilityCacheRef.current.keys())) {
      if (String(key).startsWith(prefix)) availabilityCacheRef.current.delete(key);
    }
  }, []);

  const requestAvailabilityForPersonAndDuration = useCallback(
    async (person, duration = {}, { force = false } = {}) => {
      if (!person) throw new Error("Müsaitlik için personel seçilmedi");

      const cacheKey = getAvailabilityCacheKeyForDuration(person, duration);
      if (!cacheKey) throw new Error("Personel bilgisi eksik");

      const now = Date.now();
      const cached = availabilityCacheRef.current.get(cacheKey);
      // Rezervasyon başlangıcında yapılan ön yükleme birkaç dakika bellekte kalır.
      // Gerçek rezervasyon HOLD isteği yine sunucuda slotu doğruladığı için yarış durumunda
      // eski bir slot seçilse bile güvenli şekilde reddedilir ve force refresh yapılır.
      if (!force && cached?.data && now - Number(cached.cachedAt || 0) < AVAILABILITY_CACHE_TTL_MS) {
        return cached.data;
      }
      if (!force && cached?.promise) return cached.promise;

      const personelId = String(person?.personelId || person?.personnelId || person?.id || "");
      const requestPromise = makeApiRequest(`${API_BASE_URL}/availability`, {
        method: "POST",
        body: JSON.stringify({
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          subeId: getAuthSubeId(),
          personelId,
          durationMinutes: Number(duration?.durationMinutes || 0),
          allDay: !!duration?.allDay,
          days: 45,
        }),
      })
        .then((res) => {
          if (!res?.success) throw new Error(res?.message || "Müsaitlik alınamadı");
          const data = res.data || res;
          availabilityCacheRef.current.set(cacheKey, {
            data,
            cachedAt: Date.now(),
            promise: null,
          });
          return data;
        })
        .catch((error) => {
          const latest = availabilityCacheRef.current.get(cacheKey);
          if (latest?.promise === requestPromise) availabilityCacheRef.current.delete(cacheKey);
          throw error;
        });

      availabilityCacheRef.current.set(cacheKey, {
        data: force ? null : cached?.data || null,
        cachedAt: force ? 0 : Number(cached?.cachedAt || 0),
        promise: requestPromise,
      });
      return requestPromise;
    },
    [getAvailabilityCacheKeyForDuration]
  );

  const requestAvailabilityForPerson = useCallback(
    async (person, { force = false } = {}) => {
      if (!pendingOrderDraft || !person) {
        throw new Error("Müsaitlik için personel veya hizmet seçilmedi");
      }
      return requestAvailabilityForPersonAndDuration(
        person,
        pendingOrderDraft.reservationDuration || {},
        { force }
      );
    },
    [pendingOrderDraft, requestAvailabilityForPersonAndDuration]
  );

  // Rezervasyon akışının EN BAŞINDA hangi sürelerin lazım olabileceğini çıkar.
  // Böylece kullanıcı menüde gezerken availability cevapları arka planda cache'e dolar.
  const reservationWarmDurations = useMemo(() => {
    const unique = new Map();
    const sourceProducts = [
      ...Object.values(localizedBootstrapData?.productsByUniqcode || {}),
      ...(Array.isArray(localizedBootstrapData?.preferance?.recommendedProducts)
        ? localizedBootstrapData.preferance.recommendedProducts
        : []),
      ...(Array.isArray(localizedBootstrapData?.preferance?.reservationCompleteExtraProducts)
        ? localizedBootstrapData.preferance.reservationCompleteExtraProducts
        : []),
    ];

    sourceProducts.forEach((rawProduct) => {
      const product = normalizeProductForUI(rawProduct);
      if (!product?.timeEnabled || product?.dayEnabled) return;

      const addDuration = (duration) => {
        if (!duration || duration.mode === "day") return;
        const normalized = {
          mode: "time",
          durationDays: 0,
          durationMinutes: Math.max(0, Number(duration.durationMinutes || 0)),
          allDay: !!duration.allDay,
        };
        const key = `${normalized.durationMinutes}|${normalized.allDay ? 1 : 0}`;
        if (!unique.has(key)) unique.set(key, normalized);
      };

      // Ana ürünün varsayılan süresi.
      addDuration(calculateReservationDuration(product, []));

      // Opsiyon süresi olan ürünlerde en sık senaryo olan "tek opsiyon seçildi" sürelerini de ısıt.
      // Tam kombinasyonlar seçildikten hemen sonra aşağıdaki exact-prefetch zaten devreye girer.
      (Array.isArray(product.questions) ? product.questions : []).forEach((question) => {
        (Array.isArray(question?.options) ? question.options : []).forEach((option) => {
          if (!option?.durationEnabled) return;
          addDuration(
            calculateReservationDuration(product, [
              {
                durationEnabled: true,
                durationValue: option.durationValue,
                quantity: 1,
              },
            ])
          );
        });
      });
    });

    // Aşırı sayıda ürün/süre varsa kiosk açılışında gereksiz istek patlaması olmasın.
    return Array.from(unique.values()).slice(0, 24);
  }, [localizedBootstrapData]);

  // Kullanıcı "Rezervasyon Yap" akışına girer girmez personel+süre availability verilerini hazırla.
  // İstekler personel seçildikten SONRA değil, müşteri adı / menü ekranında başlar.
  useEffect(() => {
    if (!["customerName", "menu"].includes(screen)) return undefined;
    if (!bootstrapReady || !personnelForSelection.length || !reservationWarmDurations.length) return undefined;

    let cancelled = false;
    const tasks = [];
    reservationWarmDurations.forEach((duration) => {
      personnelForSelection.forEach((person) => tasks.push({ person, duration }));
    });

    // Sunucuya aynı anda yüzlerce istek açmak yerine 4 işçiyle sessizce cache'i doldur.
    const timer = window.setTimeout(() => {
      let cursor = 0;
      const worker = async () => {
        while (!cancelled) {
          const index = cursor++;
          if (index >= tasks.length) return;
          const task = tasks[index];
          try {
            await requestAvailabilityForPersonAndDuration(task.person, task.duration);
          } catch (error) {
            if (!cancelled) {
              console.warn(
                "Rezervasyon başlangıcı availability ön yükleme hatası",
                task.person?.personelId || task.person?.id || "",
                error?.message || error
              );
            }
          }
        }
      };
      Promise.all(Array.from({ length: Math.min(4, tasks.length) }, () => worker())).catch(() => {});
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    screen,
    bootstrapReady,
    personnelForSelection,
    reservationWarmDurations,
    requestAvailabilityForPersonAndDuration,
  ]);

  // Ürün/opsiyon seçimi tamamlanıp kesin hizmet süresi belli olduğunda exact duration için de
  // bütün personelleri hemen ön yükle. Başlangıç cache'i aynı süreyi zaten getirdiyse ağ isteği açılmaz.
  useEffect(() => {
    if (!pendingOrderDraft?.timeEnabled || !personnelForSelection.length) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      personnelForSelection.forEach((person) => {
        requestAvailabilityForPerson(person).catch((error) => {
          if (!cancelled) {
            console.warn(
              "Personel saatleri exact süre için önceden hazırlanamadı",
              person?.personelId || person?.id || "",
              error?.message || error
            );
          }
        });
      });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    pendingOrderDraft?.orderKey,
    pendingOrderDraft?.timeEnabled,
    pendingOrderDraft?.reservationDuration?.durationMinutes,
    pendingOrderDraft?.reservationDuration?.allDay,
    personnelForSelection,
    requestAvailabilityForPerson,
  ]);

  useEffect(() => {
    if (!bootstrapReady) return undefined;

    let cancelled = false;
    setPersonnelOverviewLoading(true);
    setPersonnelOverviewReady(false);

    // Personel ekranı açılmadan önce rezervasyon sayıları ve nihai sıra alınır.
    // Böylece önce rastgele sıra gösterilip birkaç saniye sonra kartlar yer değiştirmez.
    makeApiRequest(`${API_BASE_URL}/personnel/overview`, {
      method: "POST",
      body: JSON.stringify({
        card: getAuthCard(),
        kiosk: getAuthKiosk(),
        subeId: getAuthSubeId(),
        // Bugün + sonraki üç gün: toplam dört günlük İstanbul saati penceresi.
        days: 4,
      }),
    })
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data?.personnel || response?.personnel || [];
        setPersonnelOverview(Array.isArray(rows) ? rows : []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("Personel rezervasyon özeti alınamadı; sabit yerel sıralama kullanılıyor", error);
        setPersonnelOverview([]);
      })
      .finally(() => {
        if (cancelled) return;
        setPersonnelOverviewReady(true);
        setPersonnelOverviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bootstrapReady]);

  const warmMenuScreen = bootstrapReady;
  const menuReadyToEnter =
    bootstrapReady && menuWarmReady && menuImagesReady && videoCacheReady && recommendedMediaReady;
  const currentUi = getUiText(selectedLanguageCode);
  const menuPreparingText = !bootstrapReady
    ? currentUi.preparing
    : !videoCacheReady && runningInsideElectron
      ? videoCacheError
        ? currentUi.videoCacheFailed
        : `${currentUi.videosPreparing}... ${Math.min(
            Number(videoCacheStats.completed || 0),
            Number(videoCacheStats.total || 0)
          )}/${Number(videoCacheStats.total || 0)}`
      : !recommendedMediaReady && recommendedMediaStats.total > 0
        ? `${currentUi.photosPreparing}... ${Math.min(recommendedMediaStats.completed, recommendedMediaStats.total)}/${recommendedMediaStats.total}`
      : menuPreloadStats.total > 0
        ? `${currentUi.photosPreparing}... ${Math.min(menuPreloadStats.completed, menuPreloadStats.total)}/${menuPreloadStats.total}`
        : currentUi.preparing;

  const markMenuWarmReady = useCallback(() => {
    setMenuWarmReady(true);
  }, []);

  const handleDomImagePreloadProgress = useCallback((stats) => {
    setMenuPreloadStats(stats);
  }, []);

  const handleDomImagesReady = useCallback((stats) => {
    setMenuPreloadStats(stats);
    setMenuImagesReady(true);
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    // localStorage JSON.stringify + setItem senkron çalışır. Sepet büyüdükçe tıklama sonrası
    // ana thread'i kısa süreli kilitlemesin diye yazmayı idle/frame sonrasına erteliyoruz.
    let idleId = null;
    let timeoutId = null;
    const persist = () => setStorageJSON("existingOrders", cart);
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(persist, { timeout: 250 });
    } else {
      timeoutId = window.setTimeout(persist, 24);
    }
    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [cart]);

  useEffect(() => {
    const flushCartBeforeLeave = () => setStorageJSON("existingOrders", cartRef.current);
    window.addEventListener("pagehide", flushCartBeforeLeave);
    return () => window.removeEventListener("pagehide", flushCartBeforeLeave);
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    const config = getInactivityConfig();
    idleTimerRef.current = setTimeout(() => {
      const latestConfig = getInactivityConfig();
      setInactivitySecondsLeft(latestConfig.modalSeconds);
      setIsInactivityModalOpen(true);
    }, config.limitMs);
  }, [clearIdleTimer, getInactivityConfig]);

  const resetToHomeKeepAuth = useCallback(() => {
    const storedCart = getStorageJSON("existingOrders", {});
    const holdIds = Object.values(storedCart || {})
      .map((item) => item?.reservationDraft?.holdId)
      .filter(Boolean);
    if (holdIds.length) {
      makeApiRequest(`${API_BASE_URL}/reservation/release`, {
        method: "POST",
        body: JSON.stringify({
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          holdIds,
        }),
      }).catch(() => {});
    }

    setCart({});
    localStorage.removeItem("existingOrders");
    clearOrderAttemptState();

    setSelectedOrderType("reservation");
    localStorage.setItem("selectedOrderType", "reservation");
    setSelectedCategoryId("ana-sayfa");
    setSelectedUniqcode("");
    setEditingOrderKey(null);
    setDesignSelections({});
    setDesignQuestionIndex(0);
    setProductPreview(null);
    setPendingOrderDraft(null);
    setSelectedPersonnel(null);
    setAvailabilityData(null);
    setAvailabilityLoading(false);
    setAvailabilityError("");
    setReservationBusy(false);
    setReservationError("");
    setPaymentError("");
    setPaymentRetryInfo(null);
    setPaymentNotice(null);
    setPaymentFlowActive(false);
    setRecommendedCatalogModalOpen(false);
    setReservationCompleteExtraModalOpen(false);
    setModifyOrderLoading(false);
    setInactivityClosingLoading(false);
    setFinalOrderNumber("");
    setFinalPaymentMethod("");
    setFinalPreapprove(false);
    setCustomerName("");
    localStorage.removeItem(CUSTOMER_NAME_STORAGE_KEY);
    setSelectedLanguageCode("tr");
    localStorage.setItem("selectedLanguageCode", "tr");
    setScreen("home");
    setMenuResetToken((p) => p + 1);
  }, []);

  const cancelPosDocumentForActiveOrder = useCallback(async (reason = "customer_edit_or_timeout") => {
    const activeOrder = getStorageJSON(ACTIVE_ORDER_MODIFY_KEY, null);
    const attemptId = String(activeOrder?.attemptId || localStorage.getItem("paymentAttemptId") || "").trim();
    const orderNumber = String(activeOrder?.orderNumber || finalOrderNumber || "").trim();
    const orderSessionId = String(
      activeOrder?.orderSessionId || localStorage.getItem(ORDER_SESSION_ID_KEY) || ""
    ).trim();

    if (!attemptId || !orderNumber) return null;

    try {
      return await makeApiRequest(`${API_BASE_URL}/pos/cancel`, {
        method: "POST",
        body: JSON.stringify({
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          attemptId,
          orderNumber,
          orderSessionId,
          reason,
        }),
      });
    } catch (error) {
      console.warn("⚠️ POS CANCEL FAILED / SKIPPED", {
        message: error?.message,
        status: error?.status,
        payload: error?.payload,
      });
      return null;
    }
  }, [finalOrderNumber]);

  const cancelActiveOrderBecauseInactivity = useCallback(async () => {
    const activeOrder = getStorageJSON(ACTIVE_ORDER_MODIFY_KEY, null);

    const attemptId = String(
      activeOrder?.attemptId || localStorage.getItem("paymentAttemptId") || ""
    ).trim();

    const orderNumber = String(activeOrder?.orderNumber || finalOrderNumber || "").trim();
    const orderSessionId = String(
      activeOrder?.orderSessionId || localStorage.getItem(ORDER_SESSION_ID_KEY) || ""
    ).trim();

    // Henüz order document oluşmadıysa iptal edilecek bir şey yok.
    if (!attemptId || !orderNumber) {
      console.log("⏱️ INACTIVITY CANCEL SKIPPED: active order yok", {
        attemptId: Boolean(attemptId),
        orderNumber: Boolean(orderNumber),
      });
      return;
    }

    try {
      await cancelPosDocumentForActiveOrder("inactivity_timeout");

      console.log("⏱️ INACTIVITY CANCEL ORDER REQUEST", {
        attemptId,
        orderNumber,
      });

      const res = await makeApiRequest(`${API_BASE_URL}/cancelorder`, {
        method: "POST",
        body: JSON.stringify({
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          attemptId,
          orderNumber,
          orderSessionId,
          cancelReason: "inactivity_timeout",
        }),
      });

      console.log("✅ INACTIVITY CANCEL ORDER RESPONSE", res);
    } catch (error) {
      // Ana sayfaya dönüşü kilitlemeyelim. Order printed/approved olduysa server zaten iptali reddeder.
      console.warn("⚠️ INACTIVITY CANCEL ORDER FAILED / SKIPPED", {
        message: error?.message,
        status: error?.status,
        payload: error?.payload,
      });
    }
  }, [finalOrderNumber, cancelPosDocumentForActiveOrder]);

  const handleInactivityTimeout = useCallback(() => {
    if (inactivityTimeoutCancelStartedRef.current) return;
    inactivityTimeoutCancelStartedRef.current = true;

    clearCountdownTimer();
    setIsInactivityModalOpen(false);
    setInactivityClosingLoading(true);

    Promise.resolve()
      .then(() => cancelActiveOrderBecauseInactivity())
      .finally(() => {
        resetToHomeKeepAuth();
        setInactivityClosingLoading(false);
        inactivityTimeoutCancelStartedRef.current = false;
      });
  }, [clearCountdownTimer, cancelActiveOrderBecauseInactivity, resetToHomeKeepAuth]);

  useEffect(() => {
    if (screen === "home" || !bootstrapReady || (paymentLoading && !paymentFlowActive)) {
      clearIdleTimer();
      clearCountdownTimer();
      setIsInactivityModalOpen(false);
      return;
    }

    startIdleTimer();

    const activity = () => {
      if (!isInactivityModalOpen) startIdleTimer();
    };

    const events = ["click", "mousemove", "touchstart", "keydown", "scroll"];
    events.forEach((evt) => window.addEventListener(evt, activity, { passive: true }));

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, activity));
      clearIdleTimer();
      clearCountdownTimer();
    };
  }, [
    bootstrapReady,
    paymentLoading,
    paymentFlowActive,
    isInactivityModalOpen,
    screen,
    startIdleTimer,
    clearIdleTimer,
    clearCountdownTimer,
  ]);

  useEffect(() => {
    if (!isInactivityModalOpen) {
      clearCountdownTimer();
      return;
    }

    setInactivitySecondsLeft(getInactivityConfig().modalSeconds);

    countdownTimerRef.current = setInterval(() => {
      setInactivitySecondsLeft((prev) => {
        if (prev <= 1) {
          handleInactivityTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearCountdownTimer();
  }, [isInactivityModalOpen, clearCountdownTimer, handleInactivityTimeout, getInactivityConfig]);

  const continueSession = useCallback(() => {
    inactivityTimeoutCancelStartedRef.current = false;
    clearCountdownTimer();
    setIsInactivityModalOpen(false);
    setInactivitySecondsLeft(getInactivityConfig().modalSeconds);
    startIdleTimer();
  }, [clearCountdownTimer, startIdleTimer, getInactivityConfig]);

  const syncInitialVideoCache = useCallback(async (data) => {
    const bridge = window?.kioskVideoCache;
    const videos = collectVideoAssets(data);

    if (!bridge?.sync) {
      if (runningInsideElectron) {
        setVideoCacheError("CACHE_BRIDGE_MISSING");
        setVideoCacheReady(false);
        setVideoCacheStats({
          required: true,
          phase: "error",
          total: videos.length,
          completed: 0,
          downloaded: 0,
          failed: videos.length,
          current: "",
        });
        return { success: false, total: videos.length, completed: 0, failed: videos.length, items: [] };
      }

      setVideoCacheError("");
      setVideoCacheReady(true);
      setVideoCacheStats({
        required: false,
        phase: "unsupported",
        total: videos.length,
        completed: 0,
        downloaded: 0,
        failed: 0,
        current: "",
      });
      return { success: true, total: videos.length, completed: 0, failed: 0, items: [] };
    }

    if (videos.length === 0) {
      setVideoCacheError("");
      setVideoCacheReady(true);
      setVideoCacheStats({
        required: true,
        phase: "complete",
        total: 0,
        completed: 0,
        downloaded: 0,
        failed: 0,
        current: "",
      });
      return { success: true, total: 0, completed: 0, failed: 0, items: [] };
    }

    setVideoCacheReady(false);
    setVideoCacheError("");
    setVideoCacheStats({
      required: true,
      phase: "start",
      total: videos.length,
      completed: 0,
      downloaded: 0,
      failed: 0,
      current: "",
    });

    try {
      const cacheResult = await warmElectronVideoCache(data);
      const failed = Math.max(0, Number(cacheResult?.failed || 0));
      const total = Math.max(0, Number(cacheResult?.total || videos.length));
      const completed = Math.max(0, Number(cacheResult?.completed || total));

      setVideoCacheStats((current) => ({
        ...current,
        ...cacheResult,
        required: true,
        phase: "complete",
        total,
        completed,
        failed,
      }));

      if (cacheResult?.items?.length) {
        setBootstrapData((current) =>
          replaceVideoUrlsWithLocalCache(current, cacheResult.items)
        );
      }

      if (failed > 0 || cacheResult?.success === false) {
        setVideoCacheError(`${failed} video indirilemedi. İnternet bağlantısını kontrol edip tekrar deneyin.`);
        setVideoCacheReady(false);
      } else {
        setVideoCacheError("");
        setVideoCacheReady(true);
      }

      console.log("🎬 VIDEO CACHE INITIAL SYNC", {
        total,
        completed,
        downloaded: cacheResult?.downloaded || 0,
        failed,
        elapsedMs: cacheResult?.elapsedMs,
      });

      return cacheResult;
    } catch (error) {
      console.warn("Video ön indirme tamamlanamadı:", error);
      setVideoCacheError(error?.message || "Videolar indirilemedi. Tekrar deneyin.");
      setVideoCacheReady(false);
      setVideoCacheStats((current) => ({
        ...current,
        required: true,
        phase: "error",
      }));
      return { success: false, failed: videos.length, error: error?.message || String(error) };
    }
  }, [runningInsideElectron]);

  useEffect(() => {
    const init = async () => {
      if (!checkAuthentication()) {
        redirectToLogin();
        return;
      }

      try {
        setMenuWarmReady(false);
        setMenuImagesReady(false);
        setMenuPreloadStats({ total: 0, completed: 0, loaded: 0, failed: 0 });
        setVideoCacheError("");
        setVideoCacheReady(!runningInsideElectron);
        setVideoCacheStats({
          required: runningInsideElectron,
          phase: runningInsideElectron
            ? electronVideoCacheAvailable
              ? "idle"
              : "error"
            : "unsupported",
          total: 0,
          completed: 0,
          downloaded: 0,
          failed: 0,
          current: "",
        });
        setRecommendedMediaReady(false);
        setRecommendedMediaStats({ total: 0, completed: 0, loaded: 0, failed: 0 });

        const res = await makeApiRequest(`${API_BASE_URL}/kioskbootstrap`, {
          method: "POST",
          body: JSON.stringify({
            card: getAuthCard(),
            kiosk: getAuthKiosk(),
            subeId: getAuthSubeId(),
          }),
        });

        if (!res?.success || !res?.data) {
          throw new Error("Menü verisi alınamadı");
        }

        const mergedBootstrap = {
          ...DEFAULT_BOOTSTRAP,
          ...res.data,
          designData: { ...DEFAULT_DESIGN_DATA, ...(res.data?.designData || {}) },
          homepageData: {
            ...DEFAULT_BOOTSTRAP.homepageData,
            ...(res.data?.homepageData || {}),
          },
          preferance: {
            ...DEFAULT_BOOTSTRAP.preferance,
            ...(res.data?.preferance || {}),
          },
        };

        const imageUrls = collectImageUrls(mergedBootstrap);

        // ÖNEMLİ: Görsel hazırlığı burada başlıyor; NEXT tıklamasında başlamıyor.
        // Böylece kullanıcı sipariş tipini seçene kadar menü fotoğrafları tarayıcı cache/decode tarafında hazır oluyor.
        preloadImages(imageUrls, {
          priorityCount: IMAGE_PRELOAD_HIGH_PRIORITY_COUNT,
          timeoutMs: IMAGE_PRELOAD_TIMEOUT_MS,
          onProgress: (stats) => {
            setMenuPreloadStats(stats);
          },
        }).then((stats) => {
          // Bu sadece network cache ısıtmasıdır. Gerçek hazır bilgisi
          // AggressiveImagePreloader DOM decode bitince gelir.
          setMenuPreloadStats(stats);
        });


        // Önerilen/ekstra ürün medyalarını modal açılmadan önce hazırla.
        // Görseller mevcut genel preload sistemine dahil; burada özellikle video mediaUrl
        // alanlarını browser media cache'e ısıtıyoruz. Electron'da aşağıdaki kalıcı video
        // cache senkronu aynı dosyaları diske de indirecek.
        const recommendedMedia = collectRecommendedMediaAssets(mergedBootstrap);
        const recommendationImagePromise = preloadImages(recommendedMedia.images, {
          priorityCount: recommendedMedia.images.length,
          timeoutMs: IMAGE_PRELOAD_TIMEOUT_MS,
        });
        const recommendationVideoPromise = runningInsideElectron
          ? Promise.resolve({ total: recommendedMedia.videos.length, completed: recommendedMedia.videos.length, loaded: recommendedMedia.videos.length, failed: 0 })
          : preloadRecommendedVideos(recommendedMedia.videos, (stats) => {
              setRecommendedMediaStats((current) => ({
                ...current,
                total: recommendedMedia.images.length + stats.total,
                completed: Math.min(recommendedMedia.images.length, current.completed) + stats.completed,
                loaded: current.loaded + 0,
                failed: current.failed + 0,
              }));
            });

        Promise.all([recommendationImagePromise, recommendationVideoPromise])
          .then(([imageStats, videoStats]) => {
            setRecommendedMediaStats({
              total: Number(imageStats?.total || 0) + Number(videoStats?.total || 0),
              completed: Number(imageStats?.completed || 0) + Number(videoStats?.completed || 0),
              loaded: Number(imageStats?.loaded || 0) + Number(videoStats?.loaded || 0),
              failed: Number(imageStats?.failed || 0) + Number(videoStats?.failed || 0),
            });
            setRecommendedMediaReady(true);
          })
          .catch((error) => {
            console.warn("Önerilen ürün medya ön-yüklemesi tamamlanamadı:", error);
            // Tek bir medya dosyası kiosk girişini sonsuza kadar kilitlemesin.
            setRecommendedMediaReady(true);
          });

        const bootstrapLanguages = uniqueLanguageCodes(
          mergedBootstrap?.languages || ["tr", "en", "ru", "de", "ar"]
        );
        // Her yeni kiosk oturumu Türkçe başlar. Türkçe panelden kaldırılmışsa
        // ilk tanımlı dil güvenli geri dönüş olarak kullanılır.
        const effectiveLanguage = bootstrapLanguages.includes("tr")
          ? "tr"
          : bootstrapLanguages[0] || "tr";
        setSelectedLanguageCode(effectiveLanguage);
        localStorage.setItem("selectedLanguageCode", effectiveLanguage);

        if (mergedBootstrap?.subeId && !getAuthSubeId()) {
          localStorage.setItem("subeId", String(mergedBootstrap.subeId));
        }
        if (mergedBootstrap?.selectedBranch?.name && !getAuthBranchName()) {
          localStorage.setItem("branchName", String(mergedBootstrap.selectedBranch.name));
        }

        setBootstrapData(mergedBootstrap);
        setBootstrapReady(true);

        // Electron içindeyse bütün ürün/personel videolarını kullanıcı menüye
        // girmeden kalıcı diske indir. İndirme tamamlanana kadar rezervasyon butonu kilitli kalır.
        await syncInitialVideoCache(mergedBootstrap);
      } catch (error) {
        setBootstrapError(error?.message || "Başlangıç verisi alınamadı");
      }
    };

    init();
  }, [syncInitialVideoCache]);

  useEffect(() => {
    if (!bootstrapReady || menuWarmReady) return;

    const fallback = window.setTimeout(() => {
      setMenuWarmReady(true);
    }, 2500);

    return () => window.clearTimeout(fallback);
  }, [bootstrapReady, menuWarmReady]);

  useEffect(() => {
    if (!bootstrapReady || menuImagesReady) return;

    // Bozuk/yavaş CDN görseli varsa kiosk kilitlenmesin.
    // Sağlam görseller yine önceden cachelenmiş olur.
    const fallback = window.setTimeout(() => {
      // En kötü durumda yavaş/bozuk CDN yüzünden kiosk tamamen kilitlenmesin.
      // Normal hazır olma AggressiveImagePreloader tarafından yapılır.
      setMenuImagesReady(true);
    }, DOM_IMAGE_PRELOAD_TIMEOUT_MS + 2500);

    return () => window.clearTimeout(fallback);
  }, [bootstrapReady, menuImagesReady]);

  const releaseReservationHolds = useCallback(async (holdIds = []) => {
    const clean = [...new Set((Array.isArray(holdIds) ? holdIds : [holdIds]).filter(Boolean))];
    if (!clean.length) return;
    try {
      await makeApiRequest(`${API_BASE_URL}/reservation/release`, {
        method: "POST",
        body: JSON.stringify({
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          holdIds: clean,
        }),
      });
    } catch (error) {
      console.warn("Rezervasyon bekletmesi bırakılamadı:", error?.message || error);
    }
  }, []);

  const resetReservationFlowState = useCallback(() => {
    setProductPreview(null);
    setPendingOrderDraft(null);
    setSelectedPersonnel(null);
    setAvailabilityData(null);
    setAvailabilityLoading(false);
    setAvailabilityError("");
    setReservationBusy(false);
    setReservationError("");
    // Availability cache rezervasyon oturumu boyunca korunur; ürünler arasında tekrar kullanılır.
    reservationSubmitLockRef.current = false;
  }, []);

  const handleLanguageSelect = useCallback((code) => {
    const normalized = normalizeLanguageCode(code);
    setSelectedLanguageCode(normalized);
    localStorage.setItem("selectedLanguageCode", normalized);
  }, []);

  const startReservationAfterCustomerName = useCallback((name, requestedLanguageCode) => {
    if (!menuReadyToEnter) return;

    const cleanName = normalizeCustomerName(name).trim();
    if (!cleanName) return;

    const code = normalizeLanguageCode(requestedLanguageCode || selectedLanguageCode);
    setSelectedLanguageCode(code);
    localStorage.setItem("selectedLanguageCode", code);
    setCustomerName(cleanName);
    localStorage.setItem(CUSTOMER_NAME_STORAGE_KEY, cleanName);

    const oldCart = getStorageJSON("existingOrders", {});
    const holdIds = Object.values(oldCart || {}).map((item) => item?.reservationDraft?.holdId).filter(Boolean);
    releaseReservationHolds(holdIds);

    setCart({});
    localStorage.removeItem("existingOrders");
    clearOrderAttemptState();

    ensureOrderSessionIdStorage();
    setSelectedOrderType("reservation");
    localStorage.setItem("selectedOrderType", "reservation");
    setSelectedCategoryId("ana-sayfa");
    setSelectedUniqcode("");
    setEditingOrderKey(null);
    setDesignSelections({});
    setDesignQuestionIndex(0);
    setPaymentError("");
    setPaymentRetryInfo(null);
    setPaymentNotice(null);
    setPaymentFlowActive(false);
    setModifyOrderLoading(false);
    setInactivityClosingLoading(false);
    setFinalOrderNumber("");
    setFinalPaymentMethod("");
    setFinalPreapprove(false);
    resetReservationFlowState();

    paymentSubmitLockRef.current = false;
    setScreen("menu");
  }, [
    menuReadyToEnter,
    selectedLanguageCode,
    releaseReservationHolds,
    resetReservationFlowState,
  ]);

  const handleHomeOrderNow = useCallback((requestedLanguageCode) => {
    if (!menuReadyToEnter) return;
    const code = normalizeLanguageCode(requestedLanguageCode || selectedLanguageCode);
    setSelectedLanguageCode(code);
    localStorage.setItem("selectedLanguageCode", code);
    setCustomerName("");
    localStorage.removeItem(CUSTOMER_NAME_STORAGE_KEY);
    // Yeni müşteri/rezervasyon oturumu: eski availability verisini temizle;
    // customerName ekranındaki warm-prefetch bunu anında yeniden doldurur.
    availabilityCacheRef.current.clear();
    setScreen("customerName");
  }, [menuReadyToEnter, selectedLanguageCode]);

  const handleCustomerNameContinue = useCallback((name) => {
    startReservationAfterCustomerName(name, selectedLanguageCode);
  }, [selectedLanguageCode, startReservationAfterCustomerName]);

  const handleCustomerNameBack = useCallback(() => {
    setCustomerName("");
    localStorage.removeItem(CUSTOMER_NAME_STORAGE_KEY);
    setSelectedLanguageCode("tr");
    localStorage.setItem("selectedLanguageCode", "tr");
    setScreen("home");
  }, []);

  const handleLanguageAndStart = useCallback(
    (code) => {
      handleLanguageSelect(code);
      handleHomeOrderNow(code);
    },
    [handleLanguageSelect, handleHomeOrderNow]
  );

  const retryInitialVideoCache = useCallback(() => {
    if (!bootstrapReady) return;
    syncInitialVideoCache(bootstrapData);
  }, [bootstrapReady, bootstrapData, syncInitialVideoCache]);

  const goToMenuPreserve = useCallback(() => {
    setScreen("menu");
  }, []);

  const finalizeConfiguredOrder = useCallback(
    (order, editingKey = "") => {
      if (!order) return;
      const orderKey = editingKey || order.orderKey || createOrderKey(order.uniqcode);
      const finalized = {
        ...order,
        orderKey,
        languageCode: selectedLanguageCode,
        subeId: getAuthSubeId(),
        branchName: getAuthBranchName(),
        timestamp: new Date().toISOString(),
      };

      setCart((prev) => ({
        ...prev,
        [orderKey]: finalized,
      }));

      const hasReservationSelection = !!finalized.reservationDraft || !!finalized.selectedPersonnel;
      setAddedProductInfo(finalized);
      if (finalized.isRecommendedExtra) {
        // Önerilen ekstra ürün tamamlandıktan sonra özet/düzenleme ekranına dön.
        // Katalog modalını burada tekrar açma; kullanıcı aynı akışta ikinci kez
        // otomatik olarak önerilen ürünler ekranını görmemeli.
        setAddedModalOpen(false);
        setRecommendedCatalogModalOpen(false);
        setReservationCompleteExtraModalOpen(false);
        setScreen("summary");
      } else {
        // Önerilen ürün kataloğu yalnızca ana ürün ilk kez sepete eklendiğinde açılır.
        // Mevcut bir ürün "Düzenle" akışından kaydediliyorsa öneri modalını tekrar gösterme.
        const isEditingExistingOrder = !!editingKey;
        const shouldOpenRecommendations = !isEditingExistingOrder && !!finalized.showRecommendedProducts;

        setAddedModalOpen(!isEditingExistingOrder && !shouldOpenRecommendations);
        setRecommendedCatalogModalOpen(shouldOpenRecommendations);
        setScreen(isEditingExistingOrder || hasReservationSelection || shouldOpenRecommendations ? "summary" : "menu");
      }

      setSelectedUniqcode("");
      setEditingOrderKey(null);
      setDesignSelections({});
      setDesignQuestionIndex(0);
      resetReservationFlowState();
    },
    [selectedLanguageCode, resetReservationFlowState]
  );

  const prepareConfiguredOrder = useCallback(
    (order, product, editingKey = "") => {
      if (!order || !product) return;
      const duration = calculateReservationDuration(product, order.selections || []);
      const prepared = {
        ...order,
        orderKey: editingKey || order.orderKey || createOrderKey(product.uniqcode),
        languageCode: selectedLanguageCode,
        subeId: getAuthSubeId(),
        branchName: getAuthBranchName(),
        personnelEnabled: !!product.personnelEnabled,
        timeEnabled: !!product.timeEnabled,
        dayEnabled: !!product.dayEnabled,
        sharedTimeValue: product.sharedTimeValue || "",
        reservationDuration: duration,
        editingOrderKey: editingKey || "",
      };

      setPendingOrderDraft(prepared);
      setSelectedPersonnel(null);
      setAvailabilityData(null);
      setAvailabilityError("");
      setReservationError("");

      if (product.dayEnabled) {
        setScreen("dayReservation");
        return;
      }
      if (product.personnelEnabled || product.timeEnabled) {
        setScreen("personnel");
        return;
      }
      finalizeConfiguredOrder(prepared, editingKey);
    },
    [selectedLanguageCode, finalizeConfiguredOrder]
  );

  const makeOrderDraft = useCallback(
    (product, selections = [], selectedOptions = {}, hasQuestion = false, editingKey = "") => {
      const existing = editingKey ? cart?.[editingKey] : null;
      const productPrice = getProductPriceInfo(product);
      const totalPrice = Number(
        hasQuestion
          ? computeSelectionsAndTotal(product, selectedOptions).totalPrice
          : shouldIncludeMainProductPrice(product)
            ? productPrice.effective
            : 0
      );
      return {
        orderKey: editingKey || createOrderKey(product.uniqcode),
        uniqcode: product.uniqcode,
        product: product.name,
        productDescription: product.description || product.desc || "",
        image: product.image,
        basePrice: productPrice.effective,
        originalBasePrice: productPrice.original,
        discountEnabled: !!product.discountEnabled,
        discountRate: productPrice.rate,
        discountAmount: productPrice.amount,
        totalPrice,
        showOffersInsteadOfPrice: !!product.showOffersInsteadOfPrice,
        addMainProductToReceipt: normalizeBooleanFlag(product.addMainProductToReceipt),
        menuProductIsMain:
          normalizeBooleanFlag(product.addMainProductToReceipt) && normalizeBooleanFlag(product.menuProductIsMain),
        showRecommendedProducts: !!product.showRecommendedProducts,
        selections,
        selectedOptions,
        countingnumber: existing?.countingnumber || getNextCountingNumber(cart),
        currentQuestionIndex: designQuestionIndex,
        hasQuestion,
        kdv: Number(product.kdv || 0),
        departmanno: normalizeDepartmentNoForPayment(
          product.departmanno ?? product.departmentNo ?? product.departmentno ?? ""
        ),
      };
    },
    [cart, designQuestionIndex]
  );

  const openProductFlow = useCallback(
    (uniqcode) => {
      const product = normalizeProductForUI(localizedBootstrapData?.productsByUniqcode?.[uniqcode]);
      if (!product) return;
      setProductPreview(product);
      setReservationError("");
    },
    [localizedBootstrapData]
  );

  const confirmProductPreview = useCallback(() => {
    const product = productPreview;
    if (!product) return;
    setProductPreview(null);
    setSelectedUniqcode(product.uniqcode);
    setEditingOrderKey(null);
    setDesignSelections({});
    setDesignQuestionIndex(0);

    const hasQuestions =
      !!product.hasQuestions &&
      Array.isArray(product.questions) &&
      product.questions.length > 0;

    if (hasQuestions) {
      setScreen("design");
      return;
    }

    const order = makeOrderDraft(product, [], {}, false, "");
    prepareConfiguredOrder(order, product, "");
  }, [productPreview, makeOrderDraft, prepareConfiguredOrder]);

  const startEditOrder = useCallback(
    async (orderKey) => {
      const order = cart?.[orderKey];
      if (!order) return;

      if (order?.reservationDraft?.holdId) {
        await releaseReservationHolds([order.reservationDraft.holdId]);
      }

      const product = normalizeProductForUI(
        localizedBootstrapData?.productsByUniqcode?.[order.uniqcode]
      );
      if (!product) return;

      setSelectedUniqcode(order.uniqcode);
      setEditingOrderKey(orderKey);
      setDesignSelections(order.selectedOptions || {});
      setDesignQuestionIndex(0);
      setReservationError("");

      const hasQuestions =
        !!product.hasQuestions &&
        Array.isArray(product.questions) &&
        product.questions.length > 0;

      if (hasQuestions) {
        setScreen("design");
      } else {
        const cleanOrder = {
          ...order,
          reservationDraft: null,
          selectedPersonnel: null,
        };
        prepareConfiguredOrder(cleanOrder, product, orderKey);
      }
    },
    [cart, localizedBootstrapData, releaseReservationHolds, prepareConfiguredOrder]
  );

  const cancelDesign = useCallback(() => {
    setScreen(editingOrderKey ? "summary" : "menu");
    setSelectedUniqcode("");
    setEditingOrderKey(null);
    setDesignSelections({});
    setDesignQuestionIndex(0);
    resetReservationFlowState();
  }, [editingOrderKey, resetReservationFlowState]);

  const saveDesignOrder = useCallback(() => {
    if (!currentProduct) return;

    const { selections } = computeSelectionsAndTotal(currentProduct, designSelections);
    const order = makeOrderDraft(
      currentProduct,
      selections,
      designSelections,
      true,
      editingOrderKey || ""
    );
    prepareConfiguredOrder(order, currentProduct, editingOrderKey || "");
  }, [
    currentProduct,
    designSelections,
    editingOrderKey,
    makeOrderDraft,
    prepareConfiguredOrder,
  ]);

  const loadAvailability = useCallback(
    async (person, { force = false } = {}) => {
      if (!pendingOrderDraft || !person) return;

      setSelectedPersonnel(person);
      setAvailabilityError("");
      setReservationError("");
      setScreen("availability");

      const cacheKey = getAvailabilityCacheKey(person);
      const cached = cacheKey ? availabilityCacheRef.current.get(cacheKey) : null;
      const hasCachedData = !!cached?.data;

      // Ön yüklenmiş veri varsa saat ekranını ANINDA onunla aç. Cache yalnızca
      // ilk görüntüyü hızlandırır; aşağıda server her girişte sessizce yeniden
      // sorgulanır. Başka kiosk/müşteri bir saati doldurduysa cevap gelir gelmez
      // slot ekrandan düşer.
      if (!force && hasCachedData) {
        setAvailabilityData(cached.data);
        setAvailabilityLoading(false);

        requestAvailabilityForPerson(person, { force: true })
          .then((freshData) => {
            setAvailabilityData(freshData);
            setAvailabilityError("");
            setReservationError("");
          })
          .catch((error) => {
            // Cache ekranda kullanılabilir durumda olduğu için sessiz yenileme
            // hatasında ekranı bozma; sadece logla. HOLD aşamasında server yine
            // slotu kesin olarak doğrular.
            console.warn("Availability arka plan yenileme hatası", error?.message || error);
          });
        return;
      }

      setAvailabilityData(null);
      setAvailabilityLoading(true);
      try {
        const data = await requestAvailabilityForPerson(person, { force });
        setAvailabilityData(data);
      } catch (error) {
        const message = error?.message || "Müsaitlik alınamadı";
        setAvailabilityError(message);
        setReservationError(message);
      } finally {
        setAvailabilityLoading(false);
      }
    },
    [pendingOrderDraft, requestAvailabilityForPerson, getAvailabilityCacheKey]
  );

  // Kullanıcı saat ekranında beklerken başka kiosk/müşteri rezervasyon yaparsa
  // ekrandaki slotlar bayat kalmasın. Her 10 saniyede server'dan sessiz refresh
  // yapılır; mevcut saatler ekranda kalır, yalnızca yeni cevapla güncellenir.
  useEffect(() => {
    if (screen !== "availability" || !pendingOrderDraft?.timeEnabled || !selectedPersonnel) return undefined;
    let cancelled = false;
    let refreshing = false;

    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const freshData = await requestAvailabilityForPerson(selectedPersonnel, { force: true });
        if (!cancelled) {
          setAvailabilityData(freshData);
          setAvailabilityError("");
        }
      } catch (error) {
        if (!cancelled) console.warn("Availability canlı yenileme hatası", error?.message || error);
      } finally {
        refreshing = false;
      }
    };

    const timer = window.setInterval(refresh, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [screen, pendingOrderDraft?.timeEnabled, selectedPersonnel, requestAvailabilityForPerson]);

  const handlePersonnelChoose = useCallback(
    (person) => {
      if (!pendingOrderDraft) return;
      if (pendingOrderDraft.timeEnabled) {
        loadAvailability(person);
        return;
      }
      finalizeConfiguredOrder(
        { ...pendingOrderDraft, selectedPersonnel: person },
        pendingOrderDraft.editingOrderKey || ""
      );
    },
    [pendingOrderDraft, loadAvailability, finalizeConfiguredOrder]
  );

  const holdAndFinalize = useCallback(
    async (reservation) => {
      if (!pendingOrderDraft || reservationBusy || reservationSubmitLockRef.current) return;
      reservationSubmitLockRef.current = true;
      setReservationBusy(true);
      setReservationError("");
      try {
        const res = await makeApiRequest(`${API_BASE_URL}/reservation/hold`, {
          method: "POST",
          body: JSON.stringify({
            card: getAuthCard(),
            kiosk: getAuthKiosk(),
            orderSessionId: ensureOrderSessionIdStorage(),
            reservation,
          }),
        });
        if (!res?.success || !(res.hold || res.reservation)) {
          throw new Error(res?.message || "Rezervasyon ayrılamadı");
        }
        const held = res.hold || res.reservation;
        // Bu slot artık HOLD edildi. Aynı personelin yalnızca bu süre cache'ini
        // değil TÜM süre cache'lerini temizle; 30 dk'lık bir rezervasyon 45/60 dk
        // gibi diğer sürelerin müsaitliğini de etkileyebilir.
        invalidateAvailabilityCacheForPersonnel(selectedPersonnel);
        finalizeConfiguredOrder(
          {
            ...pendingOrderDraft,
            selectedPersonnel: selectedPersonnel || null,
            reservationDraft: held,
          },
          pendingOrderDraft.editingOrderKey || ""
        );
      } catch (error) {
        setReservationError(error?.message || "Rezervasyon ayrılamadı");
        if (reservation.mode === "time") {
          setAvailabilityError(error?.message || "Seçilen saat artık dolu");
          await loadAvailability(selectedPersonnel, { force: true });
        }
      } finally {
        reservationSubmitLockRef.current = false;
        setReservationBusy(false);
      }
    },
    [
      pendingOrderDraft,
      reservationBusy,
      selectedPersonnel,
      finalizeConfiguredOrder,
      loadAvailability,
      invalidateAvailabilityCacheForPersonnel,
    ]
  );

  const handleTimeChoose = useCallback(
    (slot) => {
      if (!pendingOrderDraft || !selectedPersonnel || !slot) return;
      holdAndFinalize({
        mode: "time",
        subeId: getAuthSubeId(),
        personelId: selectedPersonnel.personelId,
        languageCode: selectedLanguageCode,
        cartItemKey: pendingOrderDraft.orderKey,
        durationMinutes: pendingOrderDraft.reservationDuration?.durationMinutes || 0,
        allDay: !!pendingOrderDraft.reservationDuration?.allDay,
        startAt: slot.startAt,
        endAt: slot.endAt,
        selectedPersonnel,
        product: {
          uniqcode: pendingOrderDraft.uniqcode,
          name: pendingOrderDraft.product,
          image: pendingOrderDraft.image,
        },
        totalSelectedDurationMinutes: pendingDurationOverview.totalMinutes,
        totalSelectedDurationDays: pendingDurationOverview.totalDays,
        durationBreakdown: pendingDurationOverview.parts,
        selectedAt: new Date().toISOString(),
      });
    },
    [pendingOrderDraft, selectedPersonnel, selectedLanguageCode, holdAndFinalize, pendingDurationOverview]
  );

  const handleDayChoose = useCallback(
    (period) => {
      if (!pendingOrderDraft || !period) return;
      holdAndFinalize({
        mode: "day",
        subeId: getAuthSubeId(),
        languageCode: selectedLanguageCode,
        cartItemKey: pendingOrderDraft.orderKey,
        durationDays: period.durationDays,
        startDate: period.startDate,
        endDate: period.endDate,
        product: {
          uniqcode: pendingOrderDraft.uniqcode,
          name: pendingOrderDraft.product,
          image: pendingOrderDraft.image,
        },
        totalSelectedDurationMinutes: pendingDurationOverview.totalMinutes,
        totalSelectedDurationDays: pendingDurationOverview.totalDays,
        durationBreakdown: pendingDurationOverview.parts,
        selectedAt: new Date().toISOString(),
      });
    },
    [pendingOrderDraft, selectedLanguageCode, holdAndFinalize, pendingDurationOverview]
  );

  const removeOrder = useCallback(
    (orderKey) => {
      const item = cart?.[orderKey];
      if (item?.reservationDraft?.holdId) {
        releaseReservationHolds([item.reservationDraft.holdId]);
      }
      setCart((prev) => {
        const next = { ...prev };
        delete next[orderKey];
        return next;
      });
    },
    [cart, releaseReservationHolds]
  );

  const refreshPreferance = useCallback(async () => {
    const res = await makeApiRequest(`${API_BASE_URL}/getpreferance`, {
      method: "POST",
      body: JSON.stringify({
        card: getAuthCard(),
        kiosk: getAuthKiosk(),
      }),
    });

    if (res?.success && res?.data) {
      setBootstrapData((prev) => ({
        ...prev,
        preferance: {
          ...prev.preferance,
          ...res.data,
        },
      }));
      return res.data;
    }

    return bootstrapData?.preferance || {};
  }, [bootstrapData]);

  const buildOrderItemsForBackend = useCallback(() => {
    return cartEntries.flatMap(([orderKey, item]) => {
      const buildPayload = (payloadKey, unitTotal = null) => ({
        cartItemKey: String(payloadKey || orderKey || item.orderKey || ""),
        uniqcode: String(item.uniqcode || ""),
        languageCode: String(item.languageCode || selectedLanguageCode),
        subeId: String(item.subeId || getAuthSubeId()),
        countingnumber: Number(item.countingnumber || 0),
        name: String(item.product || ""),
        image: String(item.image || ""),
        basePrice: Number(item.basePrice ?? 0),
        originalBasePrice: Number(item.originalBasePrice ?? item.basePrice ?? 0),
        discountEnabled: !!item.discountEnabled,
        discountRate: normalizeDiscountRate(item.discountRate),
        discountAmount: Number(item.discountAmount ?? 0),
        showOffersInsteadOfPrice: !!item.showOffersInsteadOfPrice,
        addMainProductToReceipt: normalizeBooleanFlag(item.addMainProductToReceipt),
        menuProductIsMain:
          normalizeBooleanFlag(item.addMainProductToReceipt) && normalizeBooleanFlag(item.menuProductIsMain),
        kdv: Number(item.kdv || 0),
        selectedPersonnel: item.selectedPersonnel || null,
        reservationDraft: item.reservationDraft || null,
        departmanno: normalizeDepartmentNoForPayment(
          item.departmanno ?? item.departmentNo ?? item.departmentno ?? ""
        ),
        options: (item.selections || []).map((s) => ({
          optionId: s.optionId,
          questionId: s.questionId,
          name: String(s.name || ""),
          quantity: Number(s.quantity || 0),
          unitPrice: Number(s.unitPrice || 0),
          originalUnitPrice: Number(s.originalUnitPrice ?? s.unitPrice ?? 0),
          totalPrice: Number(s.totalPrice || 0),
          originalTotalPrice: Number(s.originalTotalPrice ?? s.totalPrice ?? 0),
          discountEnabled: !!s.discountEnabled,
          discountRate: normalizeDiscountRate(s.discountRate),
          discountAmount: Number(s.discountAmount || 0),
          durationEnabled: !!s.durationEnabled,
          durationVisible: s.durationVisible !== false,
          durationValue: String(s.durationValue || ""),
          kdvoption: Number(s.kdvoption || 0),
          departmanno: normalizeDepartmentNoForPayment(
            s.departmanno ?? s.departmentNo ?? s.departmentno ?? ""
          ),
        })),
        totalPrice: Number(unitTotal ?? item.totalPrice ?? 0),
      });

      if (!item?.isRecommendedExtra) return [buildPayload(orderKey)];

      // Önerilen üründe adet açıksa mali POS ve fiş tarafına her adet ayrı hizmet satırı
      // gönderilir. Böylece mevcut printer/POS kodu değiştirilmeden toplam birebir korunur.
      const quantity = Math.max(1, Math.trunc(Number(item.recommendedQuantity || 1)));
      const unitTotal = Number(item.basePrice || 0);
      return Array.from({ length: quantity }, (_, index) =>
        buildPayload(`${String(orderKey)}_qty_${index + 1}`, unitTotal)
      );
    });
  }, [cartEntries, selectedLanguageCode]);

  const ensureAttemptId = useCallback(() => {
    let attemptId = localStorage.getItem("paymentAttemptId");
    if (!attemptId) {
      attemptId =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("paymentAttemptId", attemptId);
    }
    return attemptId;
  }, []);

  const ensurePaymentOrderNumber = useCallback(() => {
    let orderNumber = String(localStorage.getItem("paymentOrderNumber") || "").trim();

    // Aynı müşteri/sipariş akışında orderNumber değişmemeli.
    // Böylece yanlışlıkla ikinci createorder request'i atılsa bile backend aynı
    // orderNumber/attemptId üzerinden duplicate olarak yakalar ve ikinci document açmaz.
    if (!/^\d{30}$/.test(orderNumber)) {
      orderNumber = generateOrderNumber30();
      localStorage.setItem("paymentOrderNumber", orderNumber);
    }

    return orderNumber;
  }, []);

  const handlePay = useCallback(
    async (method, retryOptions = {}) => {
      const inFlight = isPaymentInFlightStillFresh();

      const paymentFlowDebugId = makePaymentDebugId("flow");
      const paymentFlowStartedAt = getPerfMs();
      const paymentStep = (step, extra = {}) => {
        paymentDebug(step, {
          flowId: paymentFlowDebugId,
          elapsedMs: Math.round(getPerfMs() - paymentFlowStartedAt),
          method,
          screen,
          paymentLoading,
          paymentFlowActive: paymentFlowActiveRef.current,
          storage: readPaymentLocalStorageDebug(),
          ...extra,
        });
      };
      paymentStep("HANDLE_PAY_ENTER", {
        cartCount: cartEntries.length,
        cartTotal,
        paymentSubmitLocked: paymentSubmitLockRef.current,
        inFlight,
      });

      console.log("🔥 HANDLE PAY CALLED", {
        at: new Date().toISOString(),
        method,
        paymentLoading,
        paymentSubmitLocked: paymentSubmitLockRef.current,
        inFlight,
        paymentAttemptId: localStorage.getItem("paymentAttemptId"),
      });

      if (paymentSubmitLockRef.current || inFlight || paymentLoading) {
        console.warn("⛔ ORDER BLOCKED: order request already in progress", {
          method,
          paymentLoading,
          paymentSubmitLocked: paymentSubmitLockRef.current,
          inFlight,
        });
        // Sessizce hiçbir şey yapmamak kullanıcıya POS'un çalışmadığı izlenimini
        // veriyordu. Canlı isteğin kilidi ASLA bu noktada zorla kaldırılmıyor.
        if (!paymentLoading && !paymentSubmitLockRef.current && inFlight) {
          setPaymentError("Önceki kart işleminin sonucu bekleniyor. Tekrar ödeme göndermeden önce işlemin tamamlanmasını bekleyin.");
        }
        return;
      }

      paymentSubmitLockRef.current = true;
      localStorage.setItem("paymentAttemptInFlight", "1");
      localStorage.setItem("paymentAttemptInFlightAt", new Date().toISOString());

      setPaymentLoading(true);
      setPaymentError("");
      setPaymentRetryInfo(null);
      setPaymentNotice(null);
      setPaymentFlowActive(method === "card");

      paymentStep("LOCK_SET_AND_LOADING_TRUE", {
        paymentAttemptId: localStorage.getItem("paymentAttemptId"),
        paymentOrderNumber: localStorage.getItem("paymentOrderNumber"),
      });

      try {
        if (cartEntries.length === 0) {
          throw new Error("Sepette ürün yok");
        }

        paymentStep("REFRESH_PREFERANCE_START");
        const freshPreferance = await refreshPreferance();
        paymentStep("REFRESH_PREFERANCE_DONE", { freshPreferance });

        const mealMethods = ["multinet", "sodexo", "setcard", "metropol", "edenred"];
        if (mealMethods.includes(method) && !freshPreferance?.[method]) {
          throw new Error("Bu ödeme seçeneği şu anda aktif değil.");
        }

        const activeModifyOrder = getStorageJSON(ACTIVE_ORDER_MODIFY_KEY, null);
        const existingSessionId = String(
          activeModifyOrder?.orderSessionId || localStorage.getItem(ORDER_SESSION_ID_KEY) || ""
        ).trim();
        const hasExistingOrderReference =
          !!activeModifyOrder?.attemptId && !!activeModifyOrder?.orderNumber;

        // V47: Sadece server'ın POS_EFT_FAILED (HTTP 409) olarak kesinleştirdiği
        // ve aynı siparişe ait başarısız kart ödemesi yeniden deneniyorsa
        // /modifyorder çağrısını atla. Böylece iptalden sonra yeni POS komutunun
        // gönderilmesini gereksiz bir sipariş güncellemesine bağlamayız.
        // 503 / timeout / sonucu belirsiz EFT için bu kısayol KULLANILMAZ.
        const retryExistingFailedCardOrder =
          method === "card" &&
          retryOptions?.confirmedEftFailure === true &&
          hasExistingOrderReference &&
          String(activeModifyOrder.attemptId) === String(retryOptions.attemptId || "") &&
          String(activeModifyOrder.orderNumber) === String(retryOptions.orderNumber || "");

        // ÖNEMLİ:
        // Document bir kere oluştuktan sonra kullanıcı geri gidip ürün/sepet/order type/ödeme
        // değiştirebilir. Bu durumda yeni order açmamalıyız; activeOrderModify varsa HER ZAMAN
        // aynı attemptId + orderNumber ile /modifyorder çalışmalı.
        // ACTIVE_ORDER_MODIFY_MODE_KEY sadece "Modify Order" butonundan ödeme sayfasına dönüldüğünü
        // işaretler; update kararı artık bu flag'e bağlı değil.
        const isModifyOrder = hasExistingOrderReference;
        const orderSessionId = isModifyOrder
          ? existingSessionId || ensureOrderSessionIdStorage()
          : ensureOrderSessionIdStorage();
        if (orderSessionId) localStorage.setItem(ORDER_SESSION_ID_KEY, orderSessionId);

        const attemptId = isModifyOrder ? String(activeModifyOrder.attemptId) : ensureAttemptId();
        if (attemptId) localStorage.setItem("paymentAttemptId", attemptId);

        const orderNumber = isModifyOrder
          ? String(activeModifyOrder.orderNumber)
          : ensurePaymentOrderNumber();

        if (orderNumber) localStorage.setItem("paymentOrderNumber", orderNumber);

        const items = buildOrderItemsForBackend();
        const status = buildPaymentStatus(method, freshPreferance);

        const orderPayload = {
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          attemptId,
          orderNumber,
          orderSessionId,

          // Sepette / sipariş detaylarında değişebilecek alanlar.
          // Normal createorder'da ilk kez yazılır, modifyorder'da mevcut document üzerinde güncellenir.
          selectedOrderType: "reservation",
          orderType: "reservation",
          languageCode: selectedLanguageCode,
          subeId: getAuthSubeId(),
          branchName: getAuthBranchName(),
          customerName: normalizeCustomerName(customerName || localStorage.getItem(CUSTOMER_NAME_STORAGE_KEY) || "").trim(),
          reservationDrafts: cartEntries
            .map(([, item]) => item?.reservationDraft)
            .filter(Boolean),
          totalPrice: cartTotal,
          items,

          // Ödeme ve merchant tercihine göre değişebilecek alanlar.
          paymentMethod: method,
          preapprove: !!freshPreferance?.preapprove,
          status,

          // Modify sonrası eski ödeme/print izleri temiz kalsın.
          printed: false,
          printedAt: null,
          transactionid: "",
          documentid: "",
          eku: "",
          zno: "",
          fisno: "",
        };

        const orderEndpoint = isModifyOrder ? "modifyorder" : "createorder";
        const orderUrl = `${API_BASE_URL}/${orderEndpoint}`;

        console.log(retryExistingFailedCardOrder ? "🔁 CONFIRMED EFT FAILURE: REUSE EXISTING ORDER" : "🧾 ORDER REQUEST ABOUT TO SEND", {
          at: new Date().toISOString(),
          url: orderUrl,
          endpoint: orderEndpoint,
          isModifyOrder,
          attemptId,
          orderNumber,
          orderSessionId,
          method,
        });

        console.log("🧾 ORDER PAYLOAD", orderPayload);

        paymentStep("ORDER_REQUEST_START", {
          endpoint: orderEndpoint,
          url: orderUrl,
          isModifyOrder,
          attemptId,
          orderNumber,
          orderSessionId,
          status,
          itemCount: items.length,
          totalPrice: cartTotal,
        });

        let effectiveAttemptId = attemptId;
        let effectiveOrderNumber = orderNumber;
        let res = null;

        try {
          if (retryExistingFailedCardOrder) {
            // Aynı orderNumber + attemptId ile server'ın /pos/cardpayment/start
            // endpoint'ine git. Yeni sipariş/document veya yeni ödeme kimliği açma.
            res = {
              success: true,
              orderNumber: activeModifyOrder.orderNumber,
              orderDisplayNumber: activeModifyOrder.orderDisplayNumber,
              ordersayi: activeModifyOrder.ordersayi,
              data: { ordersayi: activeModifyOrder.ordersayi },
            };
            paymentStep("CONFIRMED_EFT_FAILURE_DIRECT_RETRY", {
              orderNumber: activeModifyOrder.orderNumber,
              attemptId: activeModifyOrder.attemptId,
              skippedOrderEndpoint: orderEndpoint,
            });
          } else {
            res = await makeApiRequest(orderUrl, {
              method: "POST",
              body: JSON.stringify(orderPayload),
            });
          }
        } catch (orderRequestError) {
          // Kritik koruma:
          // Modify akışında server mevcut documenti bulamazsa ASLA otomatik createorder fallback
          // yapmıyoruz. Eski fallback, bazı edge-case'lerde aynı sipariş için yeni
          // orderNumber/ordersayi ile ikinci document açabiliyordu.
          if (isModifyOrder) {
            console.error("⛔ MODIFY ORDER FAILED - create fallback disabled", {
              status: orderRequestError?.status,
              payload: orderRequestError?.payload,
              attemptId,
              orderNumber,
            });
          }

          throw orderRequestError;
        }

        console.log("✅ ORDER FINAL RESPONSE", res);

        paymentStep("ORDER_RESPONSE_DONE", {
          success: !!res?.success,
          code: res?.code,
          message: res?.message,
          orderNumber: res?.orderNumber,
          orderDisplayNumber: res?.orderDisplayNumber || res?.ordersayi || res?.orderSayi,
          raw: res,
        });

        if (!res?.success) {
          const error = Object.assign(
            new Error(res?.message || (isModifyOrder ? "Sipariş güncellenemedi" : "Sipariş oluşturulamadı")),
            { payload: res }
          );
          throw error;
        }

        localStorage.removeItem("paymentAttemptCompleted");
        // Bu lock POS ödeme sonucu gelene kadar temizlenmemeli.
        // Önceden burada temizleniyordu; bu da /pos/cardpayment/start isteğinin
        // aynı sipariş için ikinci kez başlamasına sebep olabiliyordu.
        localStorage.removeItem(ACTIVE_ORDER_MODIFY_MODE_KEY);

        const finalOrderNumberValue = res.orderNumber || effectiveOrderNumber;
        const finalOrderDisplayNumberValue = getOrderDisplayNumberFromResponse(res, finalOrderNumberValue);
        setStorageJSON(ACTIVE_ORDER_MODIFY_KEY, {
          attemptId: effectiveAttemptId,
          orderNumber: finalOrderNumberValue,
          orderDisplayNumber: finalOrderDisplayNumberValue,
          ordersayi: res?.data?.ordersayi ?? res?.ordersayi ?? null,
          orderSessionId,
          customerName: normalizeCustomerName(customerName || localStorage.getItem(CUSTOMER_NAME_STORAGE_KEY) || "").trim(),
          paymentMethod: method,
          preapprove: !!freshPreferance?.preapprove,
          updatedAt: new Date().toISOString(),
        });

        setFinalOrderNumber(finalOrderDisplayNumberValue);
        setFinalPaymentMethod(method);
        setFinalPreapprove(!!freshPreferance?.preapprove);

        const commonPaymentPayload = {
          card: getAuthCard(),
          kiosk: getAuthKiosk(),
          attemptId: effectiveAttemptId,
          orderNumber: finalOrderNumberValue,
          orderSessionId,
        };

        if (method === "card") {
          paymentStep("CARD_PAYMENT_START_REQUEST", {
            url: `${API_BASE_URL}/pos/cardpayment/start`,
            payload: commonPaymentPayload,
            frontendTimeoutMs: 90000,
          });

          const paymentRes = await makeApiRequest(`${API_BASE_URL}/pos/cardpayment/start`, {
            method: "POST",
            body: JSON.stringify(commonPaymentPayload),
            timeoutMs: 90000,
          });

          // HTTP 200 olsa bile success:false ise ödeme BAŞARILI kabul edilmez.
          // Böyle bir yanıtı final ekranına taşımak yanlış tahsilat izlenimi verir.
          if (paymentRes?.success !== true) {
            throw Object.assign(
              new Error(paymentRes?.message || "Kartlı ödeme tamamlanamadı"),
              { status: 409, payload: paymentRes || {} }
            );
          }

          try { window["__lastKioskCardPaymentStartResponse"] = paymentRes; } catch {}
          paymentStep("CARD_PAYMENT_START_RESPONSE", {
            success: !!paymentRes?.success,
            code: paymentRes?.code,
            message: paymentRes?.message,
            printerOk: paymentRes?.printerOk,
            orderDisplayNumber: paymentRes?.orderDisplayNumber || paymentRes?.ordersayi || paymentRes?.orderSayi,
            raw: paymentRes,
          });

          setPaymentFlowActive(false);
          setPaymentLoading(false);
          clearPaymentInFlightState();
          setFinalOrderNumber(getOrderDisplayNumberFromResponse(paymentRes, finalOrderDisplayNumberValue));
          setPaymentNotice({
            type: paymentRes?.printerOk === false ? "error" : "success",
            printerOk: paymentRes?.printerOk !== false,
            message: paymentRes?.message ||
              "Ödeme tamamlandı! Sipariş numaranızı aklınızda tutun. Termal yazıcıda kağıt bittiyse veya yazıcı çalışmıyorsa bu sipariş numarası ile siparişinizi takip edebilirsiniz.",
          });
          setScreen("final");

          paymentStep("CARD_PAYMENT_SET_FINAL_SCREEN", {
            finalOrderNumber: getOrderDisplayNumberFromResponse(paymentRes, finalOrderDisplayNumberValue),
            paymentRes,
          });

          window.setTimeout(() => {
            resetToHomeKeepAuth();
          }, 10000);
          return;
        }

        setPaymentNotice({
          type: "printing",
          message: "Siparişiniz fişe bastırılıyor. Lütfen sol taraftaki termal yazıcıdan fişinizi alın.",
        });
        setScreen("final");

        try {
          const printRes = await makeApiRequest(`${API_BASE_URL}/print/start`, {
            method: "POST",
            body: JSON.stringify(commonPaymentPayload),
          });

          paymentStep("PRINT_START_RESPONSE", { printRes });

          setFinalOrderNumber(getOrderDisplayNumberFromResponse(printRes, finalOrderDisplayNumberValue));
          setPaymentNotice({
            type: printRes?.success ? "success" : "error",
            printerOk: !!printRes?.success,
            message: printRes?.message ||
              (printRes?.success
                ? "Fişiniz bastırılıyor. Lütfen sol taraftaki termal yazıcıdan fişinizi alın."
                : `Termal yazıcıda hata var. Sipariş numaranız ${getOrderDisplayNumberFromResponse(printRes, finalOrderDisplayNumberValue)} ile siparişinizi takip edebilirsiniz.`),
          });
        } catch (printError) {
          setPaymentNotice({
            type: "error",
            printerOk: false,
            message: printError?.message ||
              `Termal yazıcıda hata var. Sipariş numaranız ${finalOrderDisplayNumberValue} ile siparişinizi takip edebilirsiniz.`,
          });
        }

        clearPaymentInFlightState();
        window.setTimeout(() => {
          resetToHomeKeepAuth();
        }, 10000);
      } catch (error) {
        try {
          paymentStep("CATCH_CLEAR_INFLIGHT", {
            errorMessage: error?.message,
            errorStatus: error?.status,
            payload: error?.payload,
          });
        } catch {}

        console.error("❌ HANDLE PAY / ORDER ERROR", {
          message: error?.message,
          status: error?.status,
          payload: error?.payload,
          rawText: error?.rawText,
          fullError: error,
        });

        clearPaymentInFlightState();
        setPaymentFlowActive(false);

        const payload = error?.payload || {};
        const message = payload?.message || error?.message || "Sipariş işlemi başarısız oldu";
        setPaymentError(message);

        if (method === "card" && (payload?.retryAllowed || payload?.editAllowed || error?.status === 409 || error?.status === 503)) {
          setPaymentRetryInfo({
            message,
            code: payload?.code || "",
            attemptId: localStorage.getItem("paymentAttemptId") || "",
            orderNumber: localStorage.getItem("paymentOrderNumber") || "",
            // Yalnızca kesin EFT ret/iptal sonucu; timeout veya bağlantı kesintisi değil.
            confirmedEftFailure:
              error?.status === 409 &&
              payload?.retryAllowed === true &&
              String(payload?.code || "").toUpperCase() === "POS_EFT_FAILED",
            customerCardError: !!payload?.customerCardError,
          });
          setScreen("paymentRetry");
        }
      } finally {
        try {
          paymentStep("FINALLY_UNLOCK_AND_LOADING_FALSE", {
            beforeUnlockSubmitLocked: paymentSubmitLockRef.current,
            storageBefore: readPaymentLocalStorageDebug(),
          });
        } catch {}
        paymentSubmitLockRef.current = false;
        setPaymentLoading(false);
        window.setTimeout(() => {
          paymentDebug("POST_FINALLY_STATE_SNAPSHOT", {
            flowId: paymentFlowDebugId,
            storage: readPaymentLocalStorageDebug(),
          });
        }, 0);
      }
    },
    [
      paymentLoading,
      cartEntries,
      refreshPreferance,
      ensureAttemptId,
      ensurePaymentOrderNumber,
      buildOrderItemsForBackend,
      cartTotal,
      selectedOrderType,
      selectedLanguageCode,
      customerName,
      resetToHomeKeepAuth,
    ]
  );

  const handleStartNewOrder = useCallback(() => {
    setCart({});
    localStorage.removeItem("existingOrders");
    localStorage.removeItem("selectedOrderType");
    clearOrderAttemptState();

    setSelectedOrderType("");
    setSelectedCategoryId("ana-sayfa");
    setSelectedUniqcode("");
    setEditingOrderKey(null);
    setDesignSelections({});
    setDesignQuestionIndex(0);
    setPaymentError("");
    setPaymentRetryInfo(null);
    setPaymentNotice(null);
    setPaymentFlowActive(false);
    setModifyOrderLoading(false);
    setInactivityClosingLoading(false);
    setFinalOrderNumber("");
    setFinalPaymentMethod("");
    setFinalPreapprove(false);
    setCustomerName("");
    localStorage.removeItem(CUSTOMER_NAME_STORAGE_KEY);
    setSelectedLanguageCode("tr");
    localStorage.setItem("selectedLanguageCode", "tr");
    setMenuResetToken((p) => p + 1);
    setScreen("home");
  }, []);

  const handleModifyOrder = useCallback(async () => {
    if (modifyOrderLoading) return;

    setModifyOrderLoading(true);
    setPaymentError("");
    setPaymentRetryInfo(null);
    setPaymentFlowActive(false);

    try {
      await cancelPosDocumentForActiveOrder("customer_edit");
      const activeModifyOrder = getStorageJSON(ACTIVE_ORDER_MODIFY_KEY, null);

      if (activeModifyOrder?.attemptId) {
        localStorage.setItem("paymentAttemptId", String(activeModifyOrder.attemptId));
        if (activeModifyOrder?.orderNumber) {
          localStorage.setItem("paymentOrderNumber", String(activeModifyOrder.orderNumber));
        }
        if (activeModifyOrder?.orderSessionId) {
          localStorage.setItem(ORDER_SESSION_ID_KEY, String(activeModifyOrder.orderSessionId));
        } else {
          ensureOrderSessionIdStorage();
        }
        localStorage.setItem(ACTIVE_ORDER_MODIFY_MODE_KEY, "1");
      }

      // Düzenleme akışı ilk ürünü opsiyonlardan başlatır; opsiyonlardan sonra
      // personel ve saat/gün ekranları yeniden açılır.
      const firstOrderKey = cartEntries[0]?.[0];
      if (firstOrderKey) {
        await startEditOrder(firstOrderKey);
      } else {
        setScreen("menu");
      }
    } finally {
      setModifyOrderLoading(false);
    }
  }, [
    cancelPosDocumentForActiveOrder,
    modifyOrderLoading,
    cartEntries,
    startEditOrder,
  ]);

  const cancelActiveOrderAndReturnHome = useCallback(async () => {
    if (inactivityClosingLoading) return;

    const activeOrder = getStorageJSON(ACTIVE_ORDER_MODIFY_KEY, null);
    const attemptId = String(
      activeOrder?.attemptId || localStorage.getItem("paymentAttemptId") || ""
    ).trim();
    const orderNumber = String(activeOrder?.orderNumber || finalOrderNumber || "").trim();
    const orderSessionId = String(
      activeOrder?.orderSessionId || localStorage.getItem(ORDER_SESSION_ID_KEY) || ""
    ).trim();

    setInactivityClosingLoading(true);
    setPaymentError("");
    setPaymentRetryInfo(null);
    setPaymentFlowActive(false);

    try {
      await cancelPosDocumentForActiveOrder("customer_cancel_button");

      if (attemptId && orderNumber) {
        await makeApiRequest(`${API_BASE_URL}/cancelorder`, {
          method: "POST",
          body: JSON.stringify({
            card: getAuthCard(),
            kiosk: getAuthKiosk(),
            attemptId,
            orderNumber,
            orderSessionId,
            cancelReason: "customer_cancel_button",
          }),
        });
      }
    } catch (error) {
      console.warn("⚠️ CUSTOMER CANCEL ORDER FAILED / SKIPPED", {
        message: error?.message,
        status: error?.status,
        payload: error?.payload,
      });
    } finally {
      resetToHomeKeepAuth();
      setInactivityClosingLoading(false);
    }
  }, [
    inactivityClosingLoading,
    finalOrderNumber,
    cancelPosDocumentForActiveOrder,
    resetToHomeKeepAuth,
  ]);

  const retryCardPayment = useCallback(() => {
    // V47: Başarısız EFT'nin kesin server cevabı varsa aynı siparişle yeniden dene;
    // sonucu belirsiz bir işlemi kesin iptal edilmiş gibi varsayma.
    const retryOptions = {
      confirmedEftFailure: paymentRetryInfo?.confirmedEftFailure === true,
      attemptId: paymentRetryInfo?.attemptId || "",
      orderNumber: paymentRetryInfo?.orderNumber || "",
    };
    setPaymentRetryInfo(null);
    setPaymentError("");
    setScreen("payment");
    return handlePay("card", retryOptions);
  }, [handlePay, paymentRetryInfo]);

  const openSummaryScreen = useCallback(() => {
    setScreen("summary");
  }, []);

  const openPaymentScreen = useCallback(() => {
    const hasCompletionExtras = reservationCompleteExtraProductsForLanguage.length > 0;
    if (hasCompletionExtras && !hasSeenReservationCompleteExtras()) {
      setReservationCompleteExtraModalOpen(true);
      return;
    }
    setScreen("payment");
  }, [reservationCompleteExtraProductsForLanguage.length, hasSeenReservationCompleteExtras]);

  const continueFromReservationCompleteExtras = useCallback(() => {
    markReservationCompleteExtrasSeen();
    setReservationCompleteExtraModalOpen(false);
    setScreen("payment");
  }, [markReservationCompleteExtrasSeen]);

  const backToSummaryScreen = useCallback(() => {
    setScreen("summary");
  }, []);

  const openCancelAllConfirm = useCallback(() => {
    setConfirmCancelAllOpen(true);
  }, []);

  if (!bootstrapReady && screen !== "home") {
    return <LoadingSpinner message={getUiText(selectedLanguageCode).loading} />;
  }

  if (bootstrapError && !bootstrapReady) {
    return (
      <div style={screenStyle(true, false)}>
        <HomeScreen
          designData={bootstrapData.designData}
          onOrderNow={() => {}}
          bootstrapReady={false}
          languages={availableLanguageCodes}
          selectedLanguageCode={selectedLanguageCode}
          onLanguageSelect={handleLanguageSelect}
        />
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[10001] bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-xl max-w-2xl w-[90%] text-center text-lg font-semibold">
          {getUiText(selectedLanguageCode).initialError}: {bootstrapError}
        </div>
      </div>
    );
  }

  return (
    <>
      {bootstrapReady && (
        <AggressiveImagePreloader
          urls={menuImageUrls}
          onProgress={handleDomImagePreloadProgress}
          onReady={handleDomImagesReady}
        />
      )}

      <KeepAliveScreen active={screen === "home"} warm={false}>
        <HomeScreen
          designData={bootstrapData.designData}
          onOrderNow={handleHomeOrderNow}
          bootstrapReady={menuReadyToEnter}
          preparingText={menuPreparingText}
          languages={availableLanguageCodes}
          selectedLanguageCode={selectedLanguageCode}
          onLanguageSelect={handleLanguageAndStart}
          videoCacheStats={videoCacheStats}
          videoCacheError={videoCacheError}
          onRetryVideoCache={retryInitialVideoCache}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "customerName"} warm={false}>
        <CustomerNameScreen
          value={customerName}
          onChange={setCustomerName}
          onBack={handleCustomerNameBack}
          onContinue={handleCustomerNameContinue}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "menu"} warm={warmMenuScreen}>
        <MenuScreen
          bootstrapData={localizedBootstrapData}
          selectedCategoryId={selectedCategoryId}
          setSelectedCategoryId={setSelectedCategoryId}
          onProductClick={openProductFlow}
          onProceed={openSummaryScreen}
          onCancelAll={openCancelAllConfirm}
          cartCount={menuCartPresentation.cartCount}
          cartTotal={menuCartPresentation.cartTotal}
          productAddedCounts={menuCartPresentation.productAddedCounts}
          resetToken={menuResetToken}
          onWarmReady={markMenuWarmReady}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "design"} warm={false}>
        <DesignScreen
          product={currentProduct}
          selectedOptions={designSelections}
          setSelectedOptions={setDesignSelections}
          questionIndex={designQuestionIndex}
          setQuestionIndex={setDesignQuestionIndex}
          isEditing={!!editingOrderKey}
          onCancel={cancelDesign}
          onSave={saveDesignOrder}
          cartEntries={cartEntries}
          editingOrderKey={editingOrderKey || ""}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "personnel"} warm={false}>
        <PersonnelScreen
          personnel={personnelForSelection}
          loading={personnelOverviewLoading || !personnelOverviewReady}
          onBack={() => {
            setScreen(pendingOrderDraft?.isRecommendedExtra ? "summary" : editingOrderKey ? "summary" : currentProduct?.questions?.length ? "design" : "menu");
          }}
          onChoose={handlePersonnelChoose}
          contextName={reservationSelectionContextName}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "availability"} warm={false}>
        <AvailabilityScreen
          data={availabilityData}
          loading={!availabilityData && availabilityLoading}
          error={availabilityError || reservationError}
          onBack={() => setScreen("personnel")}
          onChoose={handleTimeChoose}
          totalDurationMinutes={pendingServiceDurationOverview.totalMinutes}
          totalDurationDays={pendingServiceDurationOverview.totalDays}
          durationParts={pendingServiceDurationOverview.parts}
          contextName={reservationSelectionContextName}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "dayReservation"} warm={false}>
        <DayReservationScreen
          durationDays={pendingOrderDraft?.reservationDuration?.durationDays || 1}
          serverNow={bootstrapData?.serverNow}
          onBack={() => {
            setScreen(pendingOrderDraft?.isRecommendedExtra ? "summary" : editingOrderKey ? "summary" : currentProduct?.questions?.length ? "design" : "menu");
          }}
          onChoose={handleDayChoose}
          contextName={reservationSelectionContextName}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "summary"} warm={false}>
        <SummaryScreen
          cartEntries={cartEntries}
          totalAmount={cartTotal}
          onBackToMenu={goToMenuPreserve}
          onAddNewProduct={goToMenuPreserve}
          onEdit={startEditOrder}
          onRemove={removeOrder}
          onProceedPayment={openPaymentScreen}
          recommendedProducts={[]}
          recommendedHeading=""
          onRecommendationAdd={continueRecommendedAdd}
          onRecommendationQuantityChange={changeRecommendedQuantity}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "paymentRetry"} warm={false}>
        <PaymentRetryScreen
          info={paymentRetryInfo}
          onRetry={retryCardPayment}
          onEditOrder={handleModifyOrder}
          onCancelOrder={cancelActiveOrderAndReturnHome}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "payment"} warm={false}>
        <PaymentScreen
          totalAmount={cartTotal}
          preferance={bootstrapData.preferance}
          loading={paymentLoading}
          error={paymentError}
          onBack={backToSummaryScreen}
          onPay={handlePay}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <KeepAliveScreen active={screen === "final"} warm={false}>
        <FinalScreen
          notice={paymentNotice}
          languageCode={selectedLanguageCode}
        />
      </KeepAliveScreen>

      <ProductPreviewModal
        open={!!productPreview}
        product={productPreview}
        onClose={() => setProductPreview(null)}
        onAdd={confirmProductPreview}
        languageCode={selectedLanguageCode}
      />

      {reservationError && screen !== "availability" && (
        <div className="fixed top-6 left-1/2 z-[13000] w-[90%] max-w-3xl -translate-x-1/2 rounded-2xl border-2 border-red-300 bg-red-50 px-6 py-5 text-center text-xl font-bold text-red-700 shadow-2xl">
          {reservationError}
        </div>
      )}

      <FullScreenProcessModal
        isOpen={paymentLoading || modifyOrderLoading || inactivityClosingLoading || reservationBusy}
        title={reservationBusy
          ? getUiText(selectedLanguageCode).checkingReservation
          : modifyOrderLoading
          ? getUiText(selectedLanguageCode).modifyRedirect
          : inactivityClosingLoading
            ? getUiText(selectedLanguageCode).returningHome
            : paymentFlowActive
              ? getUiText(selectedLanguageCode).paymentInProgress
              : getUiText(selectedLanguageCode).processing}
        text={reservationBusy
          ? getUiText(selectedLanguageCode).checkingReservationText
          : modifyOrderLoading
          ? getUiText(selectedLanguageCode).modifyWait
          : inactivityClosingLoading
            ? getUiText(selectedLanguageCode).returningHomeText
            : paymentFlowActive
              ? getUiText(selectedLanguageCode).cardPrompt
              : getUiText(selectedLanguageCode).preparingReservation}
      />

      <ConfirmModal
        isOpen={confirmCancelAllOpen}
        title={getUiText(selectedLanguageCode).cancelConfirmTitle}
        text={getUiText(selectedLanguageCode).cancelConfirmText}
        confirmText={getUiText(selectedLanguageCode).cancelAll}
        cancelText={getUiText(selectedLanguageCode).no}
        onConfirm={() => {
          setConfirmCancelAllOpen(false);
          resetToHomeKeepAuth();
        }}
        onCancel={() => setConfirmCancelAllOpen(false)}
      />

      <RecommendedProductsCatalogModal
        open={recommendedCatalogModalOpen && screen === "summary" && recommendationsEnabledForCart}
        cartEntries={cartEntries}
        recommendedProducts={allRecommendedProductsForLanguage}
        recommendedHeading={recommendedHeadingForSummary}
        onRecommendationAdd={continueRecommendedAdd}
        onRecommendationQuantityChange={changeRecommendedQuantity}
        onNoThanks={() => setRecommendedCatalogModalOpen(false)}
        languageCode={selectedLanguageCode}
      />

      <RecommendedProductsCatalogModal
        open={reservationCompleteExtraModalOpen && screen === "summary"}
        cartEntries={cartEntries}
        recommendedProducts={reservationCompleteExtraProductsForLanguage}
        recommendedHeading={reservationCompleteExtraHeading}
        onRecommendationAdd={continueRecommendedAdd}
        onRecommendationQuantityChange={changeRecommendedQuantity}
        onNoThanks={continueFromReservationCompleteExtras}
        languageCode={selectedLanguageCode}
      />


      <AddedProductModal
        isOpen={addedModalOpen}
        productInfo={addedProductInfo}
        languageCode={selectedLanguageCode}
        onClose={() => {
          setAddedModalOpen(false);
          setAddedProductInfo(null);
        }}
      />

      <InactivityModal
        isOpen={isInactivityModalOpen}
        secondsLeft={inactivitySecondsLeft}
        onContinue={continueSession}
        paymentMode={paymentFlowActive}
        languageCode={selectedLanguageCode}
      />
    </>
  );
}

export default App;
